// API endpoint constants
// All endpoints are relative to VITE_API_BASE_URL

/** `?only=a.sql&only=b.sql&force=false` for Migration/Run and Migration/Preview; empty when there is nothing to add. */
function migrationQuery(opts?: { only?: string[]; force?: boolean }): string {
  const parts = (opts?.only ?? []).map((name) => `only=${encodeURIComponent(name)}`);
  if (opts?.force !== undefined) parts.push(`force=${opts.force}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const API = {
  // Modules the signed-in facility is licensed for (Tech Log)
  licenses: '/api/licenses',

  // Patient / Resident
  patients: (facilityId: number) => `/api/patient/${facilityId}`,
  patientById: (facilityId: number, patientId: number) =>
    `/api/patient/${facilityId}/${patientId}`,
  patientMRU: (facilityId: number) =>
    `/api/patient/MRU/${facilityId}`,
  // Distinct non-empty TT_Note_B/L/D values across all patients in
  // the facility for a given mealtime (0=B, 1=L, 2=D). Reuses the
  // pre-existing `/TrayTicketNotes` endpoint rather than the
  // newer TT_Custom_Message variant — the user's dietitians draw
  // their saved phrasings from the TT_Note_* columns.
  patientTrayTicketMessages: (facilityId: number, mealtime: number) =>
    `/api/patient/TrayTicketNotes/${facilityId}/${mealtime}`,
  patientNutritionalNeeds: (facilityId: number, patientId: number, gender: string) =>
    `/api/patient/NutritionalNeeds/${facilityId}/${patientId}/${encodeURIComponent(gender || 'U')}`,
  patientAddMod: (facilityId: number) =>
    `/api/patient/AddMod/${facilityId}`,
  /** Next Res_ID for a new resident when the facility auto-increments. `{ resId: string | null }`. */
  patientNextResId: (facilityId: number) =>
    `/api/patient/NextResId/${facilityId}`,
  patientDietOrderAddMod: (facilityId: number, patientId: number) =>
    `/api/patient/DietOrder/AddMod/${facilityId}/${patientId}`,
  patientDietFluctuationAddMod: (facilityId: number, patientId: number) =>
    `/api/patient/DietFluctuation/AddMod/${facilityId}/${patientId}`,
  patientDietFluctuationModify: (facilityId: number, patientId: number, pKeyDietFlucs: number) =>
    `/api/patient/DietFluctuation/Modify/${facilityId}/${patientId}/${pKeyDietFlucs}`,
  patientDietFluctuationDelete: (facilityId: number, patientId: number, pKeyDietFlucs: number) =>
    `/api/patient/DietFluctuation/${facilityId}/${patientId}/${pKeyDietFlucs}`,
  patientDietFluctuationHistory: (facilityId: number, patientId: number) =>
    `/api/patient/DietFluctuation/History/${facilityId}/${patientId}`,
  // Diet order lock + held EHR diet orders (tblDiet_Flucs_Lock).
  patientDietFluctuationLock: (facilityId: number, patientId: number, pKeyDietFlucs: number) =>
    `/api/patient/DietFluctuation/Lock/${facilityId}/${patientId}/${pKeyDietFlucs}`,
  patientDietFluctuationHeld: (facilityId: number, patientId: number) =>
    `/api/patient/DietFluctuation/Held/${facilityId}/${patientId}`,
  /** Every current resident's held orders (Dashboard card). */
  patientDietFluctuationHeldFacility: (facilityId: number) =>
    `/api/patient/DietFluctuation/Held/${facilityId}`,
  patientDietFluctuationHeldResolve: (facilityId: number, patientId: number, pKeyDietFlucsLock: number) =>
    `/api/patient/DietFluctuation/Held/Resolve/${facilityId}/${patientId}/${pKeyDietFlucsLock}`,
  patientFoodAllergiesApply: (facilityId: number, patientId: number) =>
    `/api/patient/FoodAllergies/Apply/${facilityId}/${patientId}`,
  patientUnlinkEhr: (facilityId: number, patientId: number) =>
    `/api/patient/UnlinkEhr/${facilityId}/${patientId}`,
  patientCalProteinFluidNeeds: (facilityId: number, patientId: number) =>
    `/api/patient/CalProteinFluidNeeds/${facilityId}/${patientId}`,
  patientDelete: (facilityId: number, patientId: number) =>
    `/api/patient/${facilityId}/${patientId}`,
  patientDischarge: (facilityId: number, patientId: number) =>
    `/api/patient/Discharge/${facilityId}/${patientId}`,
  patientReadmit: (facilityId: number, patientId: number) =>
    `/api/patient/Readmit/${facilityId}/${patientId}`,
  /** "Move Resident To...": the facilities the user may move this resident to. */
  residentMoveTargets: (facilityId: number, patientId: number) =>
    `/api/ResidentMove/Targets/${facilityId}/${patientId}`,
  /** "Move Resident To...": POST `{ toFacilityId }`; moves the resident and their guests. */
  residentMove: (facilityId: number, patientId: number) =>
    `/api/ResidentMove/${facilityId}/${patientId}`,
  patientQuickAddGuests: (facilityId: number, parentPatientId: number) =>
    `/api/patient/QuickAddGuests/${facilityId}/${parentPatientId}`,
  patientSaveGuestProfile: (facilityId: number) =>
    `/api/patient/SaveGuestProfile/${facilityId}`,
  patientUpdateCheck: (facilityId: number) =>
    `/api/patient/UpdateCheck/${facilityId}`,
  patientFlags: (facilityId: number) =>
    `/api/patient/Flags/${facilityId}`,
  patientStatusHistory: (facilityId: number, patientId: number) =>
    `/api/patient/StatusHistory/${facilityId}/${patientId}`,
  // FKey_Patient lets the server refuse a row that isn't this resident's.
  patientStatusHistoryDelete: (facilityId: number, pKeyStatusHistory: number, patientId: number) =>
    `/api/patient/StatusHistory/${facilityId}/${pKeyStatusHistory}?FKey_Patient=${patientId}`,
  patientStatusHistoryUpdate: (facilityId: number, pKeyStatusHistory: number, patientId: number) =>
    `/api/patient/StatusHistory/${facilityId}/${pKeyStatusHistory}?FKey_Patient=${patientId}`,
  patientImage: (facilityId: number, patientId: number) =>
    `/api/patient/Image/${facilityId}/${patientId}`,
  patientDeleted: (facilityId: number) =>
    `/api/patient/Deleted/${facilityId}`,
  patientDeletedSummary: (facilityId: number) =>
    `/api/patient/DeletedSummary/${facilityId}`,
  patientRestore: (facilityId: number, patientId: number) =>
    `/api/patient/Restore/${facilityId}/${patientId}`,
  /** The deleted resident a new resident's ID matches (restore by Resident ID). 204 when none. */
  patientDeletedByResId: (facilityId: number, resId: string) =>
    `/api/patient/DeletedByResId/${facilityId}?resId=${encodeURIComponent(resId)}`,
  patientPermanentDelete: (facilityId: number, patientId: number) =>
    `/api/patient/Permanent/${facilityId}/${patientId}`,
  patientPermanentDeleteAll: (facilityId: number) =>
    `/api/patient/PermanentAll/${facilityId}`,

  patientTrayTicketNotes: (facilityId: number, mealtime: number) =>
    `/api/Patient/TrayTicketNotes/${facilityId}/${mealtime}`,
  patientTrayTicketNoteUpdate: (facilityId: number) =>
    `/api/Patient/TrayTicketNote/${facilityId}`,
  patientFoodAllergiesUpdate: (facilityId: number) =>
    `/api/Patient/FoodAllergies/${facilityId}`,
  patientSpecialNeedsUpdate: (facilityId: number) =>
    `/api/Patient/SpecialNeeds/${facilityId}`,
  patientSpecialDevicesUpdate: (facilityId: number) =>
    `/api/Patient/SpecialDevices/${facilityId}`,

  // Patient Attendance / Tray Line / Location Exceptions
  patientAttendance: (facilityId: number, patientId: number) =>
    `/api/patient/Attendance/${facilityId}/${patientId}`,
  patientSaveAttendance: (facilityId: number) =>
    `/api/patient/SaveAttendance/${facilityId}`,
  patientAttendanceExtended: (facilityId: number, patientId: number) =>
    `/api/patient/AttendanceExtended/${facilityId}/${patientId}`,
  patientSaveAttendanceExtended: (facilityId: number) =>
    `/api/patient/SaveAttendanceExtended/${facilityId}`,
  patientFutureSelectiveMenuDays: (facilityId: number, patientId: number) =>
    `/api/patient/FutureSelectiveMenuDays/${facilityId}/${patientId}`,
  patientDeleteFutureSelectiveMenuDays: (facilityId: number, patientId: number, mealtime: number) =>
    `/api/patient/DeleteFutureSelectiveMenuDays/${facilityId}/${patientId}/${mealtime}`,
  patientSelectiveMenuCount: (facilityId: number, patientId: number) =>
    `/api/patient/SelectiveMenuCount/${facilityId}/${patientId}`,
  patientClearSelectiveMenu: (facilityId: number, patientId: number) =>
    `/api/patient/ClearSelectiveMenu/${facilityId}/${patientId}`,
  patientClearSelectiveMenuHistory: (facilityId: number, patientId: number, before: string) =>
    `/api/patient/ClearSelectiveMenuHistory/${facilityId}/${patientId}?before=${encodeURIComponent(before)}`,
  patientTrayLine: (facilityId: number, patientId: number) =>
    `/api/patient/TrayLine/${facilityId}/${patientId}`,
  patientSaveTrayLine: (facilityId: number, patientId: number) =>
    `/api/patient/SaveTrayLine/${facilityId}/${patientId}`,
  // New tray-line endpoints (defaults + overrides). The legacy
  // two above hit tblPatientTrayLine_Transient (a different
  // concept) and stay in place for other consumers.
  patientTrayLineEnvelope: (facilityId: number, patientId: number) =>
    `/api/patient/TrayLineEnvelope/${facilityId}/${patientId}`,
  patientTrayLineDefaults: (facilityId: number, patientId: number) =>
    `/api/patient/TrayLineDefaults/${facilityId}/${patientId}`,
  patientTrayLineOverrides: (facilityId: number, patientId: number) =>
    `/api/patient/TrayLineOverrides/${facilityId}/${patientId}`,
  // Batch save for the Assign Tray Line dialog — one PUT with
  // every resident whose Set/Tray/Location moved.
  patientTrayLineAssignments: (facilityId: number) =>
    `/api/patient/TrayLineAssignments/${facilityId}`,
  patientLocationExceptions: (facilityId: number, patientId: number) =>
    `/api/patient/LocationExceptions/${facilityId}/${patientId}`,
  patientSaveLocationExceptions: (facilityId: number, patientId: number) =>
    `/api/patient/SaveLocationExceptions/${facilityId}/${patientId}`,

  // Likes & Dislikes
  likesDislikes: (facilityId: number, patientId: number) =>
    `/api/LikesDislikes/${facilityId}/${patientId}`,
  likesDislikesAdd: (facilityId: number) =>
    `/api/LikesDislikes/Add/${facilityId}`,
  likesDislikesUpdate: (facilityId: number) =>
    `/api/LikesDislikes/Update/${facilityId}`,
  likesDislikesDelete: (facilityId: number, id: number) =>
    `/api/LikesDislikes/${facilityId}/${id}`,
  likesDislikesSaveAll: (facilityId: number, patientId: number) =>
    `/api/LikesDislikes/SaveAll/${facilityId}/${patientId}`,

  // Patient Notes
  patientNotes: (facilityId: number, patientId: number) =>
    `/api/Notes/GetPatientNotes/${facilityId}/${patientId}`,
  patientNoteAdd: (facilityId: number) =>
    `/api/Notes/AddNote/${facilityId}`,
  patientNoteUpdate: (facilityId: number) =>
    `/api/Notes/UpdateNote/${facilityId}`,
  patientNoteDelete: (facilityId: number, noteId: number) =>
    `/api/Notes/DeleteNote/${facilityId}/${noteId}`,
  patientNoteHistory: (facilityId: number, noteId: number) =>
    `/api/Notes/History/${facilityId}/${noteId}`,
  patientNoteDeletedHistory: (facilityId: number, patientId: number) =>
    `/api/Notes/DeletedHistory/${facilityId}/${patientId}`,

  // Recommendations
  patientRecommendations: (facilityId: number, patientId: number) =>
    `/api/Recommendations/GetPatientRecommendations/${facilityId}/${patientId}`,

  // Credits
  patientCredits: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/patient/${patientId}`,
  creditDayMenus: (facilityId: number) =>
    `/api/Credits/${facilityId}/day-menus`,
  creditItemUsageOptions: (facilityId: number) =>
    `/api/Credits/${facilityId}/item-usage-options`,
  patientCreditBalance: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/patient/${patientId}/balance`,
  creditBillingCodes: (facilityId: number) =>
    `/api/Credits/${facilityId}/billing-codes`,
  creditAdd: (facilityId: number) =>
    `/api/Credits/${facilityId}/add-credit`,
  creditAddRefund: (facilityId: number) =>
    `/api/Credits/${facilityId}/add-refund`,
  creditVoid: (facilityId: number) =>
    `/api/Credits/${facilityId}/void`,
  patientCreditsByMonth: (facilityId: number, patientId: number, year: number, month: number) =>
    `/api/Credits/${facilityId}/patient/${patientId}/month/${year}/${month}`,
  creditToggleAutoRefill: (facilityId: number) =>
    `/api/Credits/${facilityId}/toggle-auto-refill`,
  creditUpdateNote: (facilityId: number) =>
    `/api/Credits/${facilityId}/update-note`,
  // WinForms-parity endpoints (header summary + month-aware
  // transactions + facility settings)
  creditsHeader: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/${patientId}/header`,
  creditsSettings: (facilityId: number) =>
    `/api/Credits/${facilityId}/settings`,
  creditsTransactions: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/${patientId}/transactions`,
  creditsEndOfMonth: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/${patientId}/end-of-month`,
  creditsEndOfMonthConfirm: (facilityId: number) =>
    `/api/Credits/${facilityId}/end-of-month/confirm`,
  creditsEndOfMonthResidents: (facilityId: number) =>
    `/api/Credits/${facilityId}/end-of-month/residents`,
  creditsEndOfMonthAutoProcess: (facilityId: number) =>
    `/api/Credits/${facilityId}/end-of-month/auto-process`,
  creditsVoidMeal: (facilityId: number) =>
    `/api/Credits/${facilityId}/void-meal`,

  // Intake Study
  patientIntakeHeader: (facilityId: number, patientId: number) =>
    `/api/Intake/GetPatientIntakeHeader/${facilityId}/${patientId}`,

  // Intake Study CRUD
  intakeAddHeader: (facilityId: number) =>
    `/api/Intake/AddHeader/${facilityId}`,
  intakeDeleteHeader: (facilityId: number, pKey: number) =>
    `/api/Intake/DeleteHeader/${facilityId}/${pKey}`,
  intakeBasicDetails: (facilityId: number, headerKey: number) =>
    `/api/Intake/GetBasicDetails/${facilityId}/${headerKey}`,
  intakeSaveBasicDetail: (facilityId: number) =>
    `/api/Intake/SaveBasicDetail/${facilityId}`,
  intakeDeleteBasicDetail: (facilityId: number, pKey: number) =>
    `/api/Intake/DeleteBasicDetail/${facilityId}/${pKey}`,
  intakeSaveBasicDetailsBatch: (facilityId: number) =>
    `/api/Intake/SaveBasicDetailsBatch/${facilityId}`,
  intakeProductionDetails: (facilityId: number, headerKey: number) =>
    `/api/Intake/GetProductionDetails/${facilityId}/${headerKey}`,
  /** Every intake study in the facility, with its resident's name. */
  facilityIntakeHeaders: (facilityId: number) =>
    `/api/Intake/GetFacilityIntakeHeaders/${facilityId}`,
  intakeSaveProductionDetail: (facilityId: number) =>
    `/api/Intake/SaveProductionDetail/${facilityId}`,
  /** The whole study in one write, as the WinForms Save does. */
  intakeSaveProductionDetails: (facilityId: number, headerKey: number) =>
    `/api/Intake/SaveProductionDetails/${facilityId}/${headerKey}`,
  /** Rebuilds one day + mealtime and returns the rows unsaved. */
  intakeRegenerateProductionSlice: (facilityId: number, headerKey: number) =>
    `/api/Intake/RegenerateProductionSlice/${facilityId}/${headerKey}`,
  intakeDeleteProductionDetail: (facilityId: number, pKey: number) =>
    `/api/Intake/DeleteProductionDetail/${facilityId}/${pKey}`,
  intakeUpdateProductionPercent: (facilityId: number) =>
    `/api/Intake/UpdateProductionPercent/${facilityId}`,
  intakeAutoFillFromMealPattern: (facilityId: number, headerKey: number) =>
    `/api/Intake/AutoFillFromMealPattern/${facilityId}/${headerKey}`,

  // Tube Feed (patient-level)
  patientTubeFeed: (facilityId: number, patientId: number) =>
    `/api/TubeFeed/GetPatientTubeFeed/${facilityId}/${patientId}`,
  patientTubeFeedLabels: (facilityId: number, patientId: number) =>
    `/api/TubeFeed/GetPatientTubeFeedLabel/${facilityId}/${patientId}`,
  tubeFeedAddMod: (facilityId: number) =>
    `/api/TubeFeed/AddModTubeFeed/${facilityId}`,
  tubeFeedDelete: (facilityId: number, id: number) =>
    `/api/TubeFeed/DeleteTubeFeed/${facilityId}/${id}`,
  tubeFeedLabelAddMod: (facilityId: number) =>
    `/api/TubeFeed/AddModTubeFeedLabel/${facilityId}`,
  tubeFeedLabelDelete: (facilityId: number, id: number) =>
    `/api/TubeFeed/DeleteTubeFeedLabel/${facilityId}/${id}`,

  // Auto Item Modifications (read-only; they are edited with their item on the item page)
  /** The AIMs of the items a menu cycle can serve; menuHeaderId 0 = every item with an AIM. */
  autoItemMods: (facilityId: number, menuHeaderId: number) =>
    `/api/AutoItemMod/${facilityId}/${menuHeaderId}`,
  autoItemModByItem: (facilityId: number, itemId: number) =>
    `/api/AutoItemMod/ByItem/${facilityId}/${itemId}`,

  // Facility Units
  facilityUnits: (facilityId: number) =>
    `/api/facility/${facilityId}/Units`,
  facilityUnitAddMod: (facilityId: number) =>
    `/api/facility/${facilityId}/Units`,
  facilityUnitDelete: (unitId: number) =>
    `/api/facility/Units/${unitId}`,
  facilityUnitPatientCount: (unitId: number) =>
    `/api/facility/Units/${unitId}/PatientCount`,

  // Facility Unit Rooms (tblUnit_Room_Mapping)
  facilityUnitRooms: (unitId: number) =>
    `/api/facility/Units/${unitId}/Rooms`,
  facilityUnitRoomSave: (unitId: number) =>
    `/api/facility/Units/${unitId}/Rooms`,
  facilityUnitRoomsBatch: (unitId: number) =>
    `/api/facility/Units/${unitId}/Rooms/Batch`,
  facilityUnitRoomDelete: (roomId: number) =>
    `/api/facility/Units/Rooms/${roomId}`,
  facilityUnitRoomsDeleteBatch: () =>
    `/api/facility/Units/Rooms/DeleteBatch`,

  // Facility Print Schedules
  facilityPrintSchedules: (facilityId: number) =>
    `/api/facilityunitprintschedule/${facilityId}`,
  facilityPrintScheduleAddMod: (facilityId: number) =>
    `/api/facilityunitprintschedule/AddMod/${facilityId}`,
  facilityPrintScheduleDelete: (facilityId: number, id: number) =>
    `/api/facilityunitprintschedule/${facilityId}/${id}`,
  /** GET/PUT the facility's Auto Print User (tblFacility.FKey_User_SchedulePrinting). */
  facilityPrintScheduleAutoPrintUser: (facilityId: number) =>
    `/api/facilityunitprintschedule/AutoPrintUser/${facilityId}`,

  // Guest Schedule
  // `date` (YYYY-MM-DD, the client's today) sets each guest's mealB/L/D;
  // `includeDischarged` adds discharged guests (never deleted ones).
  patientGuests: (facilityId: number, opts: { date?: string; includeDischarged?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (opts.date) qs.set('date', opts.date);
    if (opts.includeDischarged) qs.set('includeDischarged', 'true');
    const q = qs.toString();
    return `/api/patient/Guests/${facilityId}${q ? `?${q}` : ''}`;
  },
  // Patient Labels report grid — backs `usePatientLabelsGrid`.
  // Returns patients + guests with per-day attendance columns
  // and a joined Unit. Mirrors WinForms `Fill_dtPatientGrid`
  // (ucDietech_Report_Generator_Resident_Labels.vb:307-318).
  patientLabelsGrid: (facilityId: number, dateIso?: string) =>
    dateIso
      ? `/api/patient/LabelsGrid/${facilityId}?date=${encodeURIComponent(dateIso)}`
      : `/api/patient/LabelsGrid/${facilityId}`,
  guestSchedules: (facilityId: number) =>
    `/api/GuestSchedule/${facilityId}`,
  guestSchedulesByPatient: (facilityId: number, patientId: number) =>
    `/api/GuestSchedule/${facilityId}/ByPatient/${patientId}`,
  // Full TblPatient_GuestSchedule rows for a given guest's PKey.
  // Path matches the controller's [HttpGet("ByPatient/{f}/{p}")]
  // attribute (the legacy `guestSchedulesByPatient` URL above
  // is a known mismatch — different segment order).
  guestScheduleByGuestPatient: (facilityId: number, guestPatientId: number) =>
    `/api/GuestSchedule/ByPatient/${facilityId}/${guestPatientId}`,

  // Special Events (Production)
  specialEvents: (facilityId: number) =>
    `/api/SpecialEvent/${facilityId}`,
  specialEventById: (facilityId: number, id: number) =>
    `/api/SpecialEvent/${facilityId}/${id}`,
  specialEventAddMod: (facilityId: number) =>
    `/api/SpecialEvent/AddMod/${facilityId}`,
  specialEventDelete: (facilityId: number, id: number) =>
    `/api/SpecialEvent/${facilityId}/${id}`,
  /** The event's Special Requests (tblPrd_SpecialEventRequest_Mapping): GET lists them, POST replaces them. */
  specialEventRequests: (facilityId: number, id: number) =>
    `/api/SpecialEvent/Requests/${facilityId}/${id}`,

  // Reference: Doctors, Units, Special Devices
  doctors: (facilityId: number) => `/api/doctor/${facilityId}`,
  units: (facilityId: number) => `/api/unit/GetUnitMapping/${facilityId}`,
  unitRooms: (unitId: number) => `/api/unit/GetUnitRooms/${unitId}`,
  unitRoomsSave: (unitId: number) => `/api/unit/SaveUnitRooms/${unitId}`,
  // The facility is in the route: the API checks it against the user's facilities, not the body.
  unitSave: (facilityId: number) => `/api/unit/SaveUnit/${facilityId}`,
  unitsSave: (facilityId: number) => `/api/unit/SaveUnits/${facilityId}`,
  unitDelete: (unitId: number) => `/api/unit/DeleteUnit/${unitId}`,
  specialDevices: (facilityId: number) => `/api/specialdevices/${facilityId}`,

  // Diet Flucs
  dietFlucsActive: (facilityId: number) =>
    `/api/dietflucs/GetActive/${facilityId}`,
  dietFlucsFuture: (facilityId: number) =>
    `/api/dietflucs/GetFuture/${facilityId}`,

  // Facility
  facility: (facilityId: number) => `/api/facility/GetById/${facilityId}`,
  facilityAssociated: (facilityId: number, userId: number) =>
    `/api/facility/GetAssociated/${facilityId}/${userId}`,
  /** Every facility in the database, NOT scoped to the signed-in user. */
  facilityAll: (facilityId: number) => `/api/facility/All/${facilityId}`,
  facilityCorporateId: '/api/facility/CorporateId',
  facilityCorporate: '/api/facility/Corporate',
  facilityDietMenuCategoryPrimary: (facilityId: number) => `/api/FacilityDietMenuCategoryPrimary/${facilityId}`,
  facilityDietMenuCategoryPrimaryAddMod: (facilityId: number) => `/api/FacilityDietMenuCategoryPrimary/AddMod/${facilityId}`,
  facilityDietMenuCategoryPrimaryDelete: (facilityId: number, id: number) => `/api/FacilityDietMenuCategoryPrimary/${facilityId}/${id}`,
  facilityChildFacilities: (facilityId: number, userId: number) =>
    `/api/Facility/GetChildFacilities/${facilityId}/${userId}`,

  // Corporate Dashboard
  corporateDashboardSummary: (facilityId: number, userId: number, hours: number) =>
    `/api/CorporateDashboard/Summary/${facilityId}/${userId}/${hours}`,
  corporateMenuCompliance: (facilityId: number, userId: number) =>
    `/api/CorporateDashboard/MenuCompliance/${facilityId}/${userId}`,
  corporateFinancial: (facilityId: number, userId: number, months: number) =>
    `/api/CorporateDashboard/Financial/${facilityId}/${userId}/${months}`,
  corporateTrends: (facilityId: number, userId: number, days: number) =>
    `/api/CorporateDashboard/Trends/${facilityId}/${userId}/${days}`,

  // Menu Category
  menuCategories: (facilityId: number) => `/api/MenuCategory/${facilityId}`,
  menuCategoryPrintOrder: (facilityId: number) =>
    `/api/MenuCategory/GetPrintOrder/${facilityId}`,
  menuCategoryAlwaysServedFilter: (facilityId: number) =>
    `/api/MenuCategory/AlwaysServedFilter/${facilityId}`,
  menuCategoryAlwaysServedFilterDelete: (facilityId: number, id: number) =>
    `/api/MenuCategory/AlwaysServedFilter/${facilityId}/${id}`,
  // Menu Categories > Print > Item Never Serve Association, as a PDF (204 when there is none).
  menuCategoryNeverServeAssociation: (facilityId: number) =>
    `/api/MenuCategory/NeverServeAssociation/${facilityId}`,
  // Menu Category Priority (per-report print order; WinForms frmDietech_Facility_MenuCategoryPriority_AddMod)
  menuCategoryPriority: (facilityId: number) => `/api/MenuCategoryPriority/${facilityId}`,
  menuCategoryPrioritySave: (facilityId: number) => `/api/MenuCategoryPriority/Save/${facilityId}`,

  // Menu Cycle
  menuHeaders: (facilityId: number) => `/api/menuheader/${facilityId}`,

  // Dashboard - Layout
  dashboardLayout: (facilityId: number, userId: number) =>
    `/api/dashboard/GetLayout/${facilityId}/${userId}`,
  dashboardSaveLayout: (facilityId: number) =>
    `/api/dashboard/SaveLayout/${facilityId}`,
  dashboardResetLayout: (facilityId: number, userId: number) =>
    `/api/dashboard/ResetLayout/${facilityId}/${userId}`,

  // Dashboard - Data
  dashboardDietOrderChanges: (facilityId: number, hours: number) =>
    `/api/dashboard/GetDietOrderChanges/${facilityId}/${hours}`,
  dashboardAdmittedResidents: (facilityId: number, hours: number) =>
    `/api/dashboard/GetAdmittedResidents/${facilityId}/${hours}`,
  dashboardAdmittedGuests: (facilityId: number, hours: number) =>
    `/api/dashboard/GetAdmittedGuests/${facilityId}/${hours}`,
  dashboardTrayLineWarnings: (facilityId: number) =>
    `/api/dashboard/GetTrayLineWarnings/${facilityId}`,
  dashboardDischargedResidents: (facilityId: number, hours: number) =>
    `/api/dashboard/GetDischargedResidents/${facilityId}/${hours}`,
  dashboardUnitRoomBedChanges: (facilityId: number, hours: number) =>
    `/api/dashboard/GetUnitRoomBedChanges/${facilityId}/${hours}`,
  dashboardAllergyChanges: (facilityId: number, hours: number) =>
    `/api/dashboard/GetAllergyChanges/${facilityId}/${hours}`,
  dashboardMealPatternNotes: (facilityId: number, hours: number) =>
    `/api/dashboard/GetMealPatternNotes/${facilityId}/${hours}`,
  dashboardAllMealPatternNotes: (facilityId: number) =>
    `/api/dashboard/GetAllMealPatternNotes/${facilityId}`,
  dashboardSelectiveMenuChanges: (facilityId: number, hours: number) =>
    `/api/dashboard/GetSelectiveMenuChanges/${facilityId}/${hours}`,

  // Dashboard - New Cards
  // `date` is the client's today: totalGuests counts guests eating that day.
  dashboardCensusSummary: (facilityId: number, date?: string) =>
    `/api/dashboard/GetCensusSummary/${facilityId}${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  dashboardUpcomingBirthdays: (facilityId: number, daysAhead: number) =>
    `/api/dashboard/GetUpcomingBirthdays/${facilityId}/${daysAhead}`,
  dashboardWeightAlerts: (facilityId: number) =>
    `/api/dashboard/GetWeightAlerts/${facilityId}`,
  dashboardMealServiceStatus: (facilityId: number) =>
    `/api/dashboard/GetMealServiceStatus/${facilityId}`,

  // Nutrition Compliance (formerly PDPM; the API route stays api/Pdpm)
  pdpmDashboard: (facilityId: number) =>
    `/api/Pdpm/Dashboard/${facilityId}`,
  pdpmEvaluate: (facilityId: number) =>
    `/api/Pdpm/Evaluate/${facilityId}`,
  pdpmFindings: (facilityId: number) =>
    `/api/Pdpm/Findings/${facilityId}`,
  pdpmFinding: (facilityId: number, id: number) =>
    `/api/Pdpm/Finding/${facilityId}/${id}`,
  pdpmFindingAction: (facilityId: number) =>
    `/api/Pdpm/FindingAction/${facilityId}`,
  pdpmAssignees: (facilityId: number) =>
    `/api/Pdpm/Assignees/${facilityId}`,
  pdpmNotifications: (facilityId: number) =>
    `/api/Pdpm/Notifications/${facilityId}`,
  pdpmRules: (facilityId: number) =>
    `/api/Pdpm/Rules/${facilityId}`,
  pdpmResident: (facilityId: number, patientId: number) =>
    `/api/Pdpm/Resident/${facilityId}/${patientId}`,
  pdpmMds: (facilityId: number, patientId: number) =>
    `/api/Pdpm/Mds/${facilityId}/${patientId}`,
  pdpmMdsSave: (facilityId: number) =>
    `/api/Pdpm/Mds/Save/${facilityId}`,
  pdpmMdsDelete: (facilityId: number, id: number) =>
    `/api/Pdpm/Mds/${facilityId}/${id}`,
  pdpmMdsReview: (facilityId: number, patientId: number) =>
    `/api/Pdpm/Mds/Review/${facilityId}/${patientId}`,
  pdpmConsistencies: (facilityId: number) =>
    `/api/Pdpm/Consistencies/${facilityId}`,
  pdpmConsistencyReview: (facilityId: number) =>
    `/api/Pdpm/Consistencies/Review/${facilityId}`,
  /** A Nutrition Compliance report as PDF or Excel (open-findings, weight-changes, tube-feeding, mds-section-k, readiness). */
  pdpmReport: (facilityId: number, report: string) =>
    `/api/Pdpm/Reports/${facilityId}/${report}`,
  pdpmResidentPacket: (facilityId: number, patientId: number) =>
    `/api/Pdpm/Reports/Resident/${facilityId}/${patientId}`,
  pdpmCorporateReport: (corporateFacilityId: number) =>
    `/api/Pdpm/Reports/Corporate/${corporateFacilityId}`,
  pdpmSignoffs: (facilityId: number) =>
    `/api/Pdpm/Signoffs/${facilityId}`,
  pdpmWeightData: (facilityId: number, months: number) =>
    `/api/Pdpm/WeightData/${facilityId}?months=${months}`,
  pdpmWeightReview: (facilityId: number) =>
    `/api/Pdpm/WeightData/Review/${facilityId}`,
  pdpmAlertConfig: (facilityId: number) =>
    `/api/Pdpm/GetAlertConfig/${facilityId}`,
  pdpmSaveAlertConfig: (facilityId: number) =>
    `/api/Pdpm/SaveAlertConfig/${facilityId}`,
  pdpmCorporateSummary: (corporateFacilityId: number) =>
    `/api/Pdpm/CorporateSummary/${corporateFacilityId}`,

  // Reports
  reportList: (facilityId: number) => `/api/report/List/${facilityId}`,
  reportDocuments: (facilityId: number) =>
    `/api/report/GetReportDocuments/${facilityId}`,
  reportStatus: (facilityId: number) =>
    `/api/report/Status/${facilityId}`,
  reportReadyCount: (facilityId: number) =>
    `/api/report/ReadyCount/${facilityId}`,
  reportPdf: (facilityId: number, guid: string) =>
    `/api/report/Pdf/${facilityId}/${guid}`,
  /** Excel or Word copy of a stored report; the format goes in `?format=xlsx|docx`. */
  reportExport: (facilityId: number, guid: string) =>
    `/api/report/Export/${facilityId}/${guid}`,
  reportCombinePdf: (facilityId: number) =>
    `/api/report/CombinePdf/${facilityId}`,
  reportDocumentBytes: (facilityId: number, guid: string) =>
    `/api/report/Document/${facilityId}/${guid}`,
  reportDeleteDocument: (facilityId: number, reportDocumentId: number) =>
    `/api/report/DeleteDocument/${facilityId}/${reportDocumentId}`,
  reportReprocess: (facilityId: number, reportDocumentId: number) =>
    `/api/report/Reprocess/${facilityId}/${reportDocumentId}`,
  reportCancel: (facilityId: number, reportDocumentId: number) =>
    `/api/report/Cancel/${facilityId}/${reportDocumentId}`,
  reportMarkPrinted: (facilityId: number, reportDocumentId: number) =>
    `/api/report/MarkPrinted/${facilityId}/${reportDocumentId}`,
  reportMarkViewed: (facilityId: number, reportDocumentId: number) =>
    `/api/report/MarkViewed/${facilityId}/${reportDocumentId}`,
  reportFavorites: (facilityId: number, userId: number) =>
    `/api/report/Favorites/${facilityId}/${userId}`,
  reportFavoritesAdd: (facilityId: number) =>
    `/api/report/Favorites/Add/${facilityId}`,
  reportFavoritesDelete: (facilityId: number, favoriteId: number) =>
    `/api/report/Favorites/Delete/${facilityId}/${favoriteId}`,
  reportSchedules: (facilityId: number) =>
    `/api/report/Schedules/${facilityId}`,
  reportScheduleById: (facilityId: number, scheduleId: number) =>
    `/api/report/Schedules/${facilityId}/${scheduleId}`,
  reportScheduleToggle: (facilityId: number, scheduleId: number) =>
    `/api/report/Schedules/${facilityId}/${scheduleId}/toggle`,
  reportScheduleHistory: (facilityId: number, scheduleId: number) =>
    `/api/report/Schedules/${facilityId}/${scheduleId}/history`,
  reportScheduleRun: (facilityId: number, scheduleId: number) =>
    `/api/report/Schedules/${facilityId}/${scheduleId}/run`,
  /** Whether this API runs scheduled reports (ScheduledReports:Enabled). */
  reportScheduleRunner: (facilityId: number) =>
    `/api/report/ScheduleRunner/${facilityId}`,
  /** Whether Restaurant Style has a layout to print from: the paper layout or a Selective Touch layout. */
  reportSelectiveMenuRestaurantLayout: (facilityId: number) =>
    `/api/report/SelectiveMenuRestaurantLayout/${facilityId}`,
  /** Whether the facility has menu categories set up for printing selective
   *  menus, for Menu Categories A (0) or B (1). */
  reportSelectiveMenuCategoryOrder: (facilityId: number, menuCategoryTypeIndex: number) =>
    `/api/report/SelectiveMenuCategoryOrder/${facilityId}/${menuCategoryTypeIndex}`,
  reportQueue: (facilityId: number) =>
    `/api/report/Queue/${facilityId}`,
  reportQueueSpecific: (facilityId: number, reportType: string) =>
    `/api/report/${reportType}/${facilityId}`,

  // Cost Analysis Reports
  reportQueueCostMenu: (facilityId: number) =>
    `/api/report/CostAnalysis/Menu/${facilityId}`,
  reportQueueCostNourishment: (facilityId: number) =>
    `/api/report/CostAnalysis/Nourishment/${facilityId}`,

  // Tray Ticket image picker — mirrors WinForms `LoadImages()`
  // at `ucDietech_Report_Generator_TrayTicket.vb:1143-1192`.
  // `List` returns the picker rows (Group / Name / ResourceName +
  // per-row thumbnail URL). `Image` returns the PNG bytes.
  trayTicketImagesList: (facilityId: number) =>
    `/api/TrayTicketImages/List/${facilityId}`,
  trayTicketImage: (resourceName: string) =>
    `/api/TrayTicketImages/Image/${encodeURIComponent(resourceName)}`,
  /** Preset Tray Ticket note strings sourced from `tblFacility.TT_Notes`
   *  (WinForms MRUEdit blob). Returns `string[]`. */
  trayTicketNotes: (facilityId: number) =>
    `/api/TrayTicketImages/Notes/${facilityId}`,
  /** Pre-print Non-Returned Selective Menu picker rows. Mirrors
   *  WinForms `DisplaySelectiveMenuNotifyForm`. Empty rows → no
   *  dialog needed. */
  trayTicketNonReturnedSelectiveMenus: (facilityId: number) =>
    `/api/TrayTicket/NonReturnedSelectiveMenus/${facilityId}`,

  // Users
  users: (facilityId: number) => `/api/user/${facilityId}`,
  userById: (facilityId: number, userId: number) =>
    `/api/user/GetById/${facilityId}/${userId}`,
  userLoggedIn: (facilityId: number) =>
    `/api/user/GetLoggedIn/${facilityId}`,
  userDeleted: (facilityId: number) =>
    `/api/user/GetDeleted/${facilityId}`,
  userAddMod: (facilityId: number) =>
    `/api/user/AddMod/${facilityId}`,
  userDelete: (facilityId: number, userId: number) =>
    `/api/user/${facilityId}/${userId}`,
  userRestore: (facilityId: number, userId: number) =>
    `/api/user/Restore/${facilityId}/${userId}`,
  userMe: '/api/user/Me',
  /** `{ mode: 'Off' | 'Audit' | 'Enforce' }` for the caller's connection (staged role levels). */
  userMePermissionEnforcement: '/api/user/Me/PermissionEnforcement',
  /** The caller's own sign-in facility; 0 clears it (#2072). */
  userMeDefaultFacility: (facilityId: number) => `/api/user/Me/DefaultFacility/${facilityId}`,
  /** The caller's own start screen (DF_Start_Form). */
  userMeStartForm: (startForm: number) => `/api/user/Me/StartForm/${startForm}`,
  /** The caller's small website state (welcome tour, What's New seen): `{ available, values }` (hooks/useServerUserState). */
  userMeState: '/api/user/Me/State',
  /** Saves one of those values; the body is the JSON value. Answers `{ saved }`. */
  userMeStateKey: (key: string) => `/api/user/Me/State/${encodeURIComponent(key)}`,
  userCreateWithKeycloak: (facilityId: number) =>
    `/api/user/CreateWithKeycloak/${facilityId}`,
  userResetKeycloakPassword: (facilityId: number) =>
    `/api/user/ResetKeycloakPassword/${facilityId}`,
  userHeartbeat: (facilityId: number, userId: number) =>
    `/api/user/Heartbeat/${facilityId}/${userId}`,
  userHeartbeatClear: (facilityId: number, userId: number) =>
    `/api/user/Heartbeat/Clear/${facilityId}/${userId}`,
  userSignature: (facilityId: number, userId: number) =>
    `/api/user/Signature/${facilityId}/${userId}`,
  /** Users > History: `from` / `to` are ISO UTC instants (`to` exclusive). */
  logInteractionUserActivity: (facilityId: number, userId: number, from: string, to: string) =>
    `/api/LogInteraction/UserActivity/${facilityId}/${userId}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,

  // Roles
  roles: (facilityId: number) => `/api/role/${facilityId}`,
  roleById: (facilityId: number, roleId: number) =>
    `/api/role/GetById/${facilityId}/${roleId}`,
  roleAddMod: (facilityId: number) =>
    `/api/role/AddMod/${facilityId}`,
  roleDelete: (facilityId: number, roleId: number) =>
    `/api/role/${facilityId}/${roleId}`,
  roleUsage: (facilityId: number, roleId: number) =>
    `/api/role/GetUsage/${facilityId}/${roleId}`,
  roleCopy: (facilityId: number, roleId: number) =>
    `/api/role/Copy/${facilityId}/${roleId}`,
  roleReplace: (facilityId: number, oldRoleId: number, newRoleId: number) =>
    `/api/role/Replace/${facilityId}/${oldRoleId}/${newRoleId}`,

  // User Facility
  userFacilityDietech: (facilityId: number, userId: number) =>
    `/api/userfacility/Dietech/${facilityId}/${userId}`,
  userFacilitySaveDietech: (facilityId: number, userId: number) =>
    `/api/userfacility/SaveDietech/${facilityId}/${userId}`,
  userFacilityPurchasing: (facilityId: number, userId: number) =>
    `/api/userfacility/Purchasing/${facilityId}/${userId}`,
  userFacilitySavePurchasing: (facilityId: number, userId: number) =>
    `/api/userfacility/SavePurchasing/${facilityId}/${userId}`,
  userFacilityDepartments: (facilityId: number, userId: number) =>
    `/api/userfacility/Departments/${facilityId}/${userId}`,
  userFacilitySaveDepartments: (facilityId: number, userId: number) =>
    `/api/userfacility/SaveDepartments/${facilityId}/${userId}`,

  // Reference Tables
  dietTypes: (facilityId: number) => `/api/diettype/${facilityId}`,
  dietTypesByMenuCycle: (facilityId: number, menuHeaderId: number) =>
    `/api/diettype/ByMenuCycle/${facilityId}/${menuHeaderId}`,
  consistencies: (facilityId: number) => `/api/consistency/${facilityId}`,
  consistenciesByMenuCycle: (facilityId: number, menuHeaderId: number) =>
    `/api/consistency/ByMenuCycle/${facilityId}/${menuHeaderId}`,
  // Combined Diet Type + Consistency rows for a menu cycle —
  // mirrors WinForms `SQL_DietTypeConsist_Select_MenuCycle`.
  // Used by Recipes - Menu Cycle report.
  dtConsistByMenuCycle: (facilityId: number, menuHeaderId: number) =>
    `/api/consistency/DTConsistByMenuCycle/${facilityId}/${menuHeaderId}`,
  foodAllergies: (facilityId: number) => `/api/foodallergy/${facilityId}`,
  specialNeeds: (facilityId: number) => `/api/specialneed/${facilityId}`,
  items: (facilityId: number) => `/api/item/${facilityId}`,
  itemById: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}`,
  itemAddMod: (facilityId: number) => `/api/item/AddMod/${facilityId}`,
  itemDelete: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/delete`,
  itemEditState: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/editState`,
  itemFoodGroups: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/foodGroups`,
  itemEditor: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/editor`,
  /** Items list > Item Usage (ItemUsageController.GetUsage). */
  itemUsage: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/usage`,
  /** Items list > Replace (ItemReplaceController): check a pair, then replace. */
  itemReplaceCheck: (facilityId: number, itemId: number, keepItemId: number) =>
    `/api/item/${facilityId}/${itemId}/replace/${keepItemId}`,
  itemReplace: (facilityId: number, itemId: number) =>
    `/api/item/${facilityId}/${itemId}/replace`,
  itemLinkOptions: (facilityId: number) => `/api/item/${facilityId}/linkOptions`,
  /** This facility's desktop order guides (tblOP_OrderGuide) for the item's Order Guide picker. */
  itemOrderGuides: (facilityId: number) => `/api/item/${facilityId}/orderGuides`,
  itemEditorSettings: (facilityId: number) => `/api/item/${facilityId}/editorSettings`,
  itemIngredients: (facilityId: number) => `/api/item/${facilityId}/ingredients`,
  /** Items list cell edits (ItemListEditController). */
  itemListEditGroups: (facilityId: number) => `/api/item/ListEdit/${facilityId}/groups`,
  itemListEditCell: (facilityId: number, itemId: number) => `/api/item/ListEdit/${facilityId}/${itemId}`,
  itemListEditFoodGroup: (facilityId: number, itemId: number) => `/api/item/ListEdit/${facilityId}/${itemId}/foodGroup`,
  itemListEditAllergyGroup: (facilityId: number, itemId: number) => `/api/item/ListEdit/${facilityId}/${itemId}/allergyGroup`,
  /** Items list > Group Modify (ItemGroupModifyController). */
  itemGroupModify: (facilityId: number) => `/api/item/GroupModify/${facilityId}`,
  itemGroupModifyFoodGroups: (facilityId: number) => `/api/item/GroupModify/${facilityId}/itemFoodGroups`,
  /** Items list tools (ItemToolsController): Highlight Active Menu Cycle Items, Auto Item Cleanup, Order Guide/Shelf Assignment. */
  itemActiveMenuCycleItems: (facilityId: number) => `/api/item/${facilityId}/activeMenuCycleItems`,
  itemCleanupPreview: (facilityId: number) => `/api/item/${facilityId}/cleanup/preview`,
  itemCleanup: (facilityId: number) => `/api/item/${facilityId}/cleanup`,
  itemOrderGuideAssignment: (facilityId: number) => `/api/item/${facilityId}/orderGuideAssignment`,
  itemRecipeMatch: (facilityId: number, recipeId: number) => `/api/item/${facilityId}/recipeMatch/${recipeId}`,
  itemAimAudit: (facilityId: number, itemId: number) => `/api/item/${facilityId}/${itemId}/aimAudit`,
  itemSameName: (facilityId: number, name: string, excludeItemId: number) =>
    `/api/item/${facilityId}/sameName?name=${encodeURIComponent(name)}&excludeItemId=${excludeItemId}`,
  itemSearch: (facilityId: number, searchString: string, menuDate?: string, fKeyMenuHeader?: number) => {
    const base = `/api/item/Search/${facilityId}/${encodeURIComponent(searchString)}`;
    const params = new URLSearchParams();
    if (menuDate) params.append('menuDate', menuDate);
    if (fKeyMenuHeader != null) params.append('fKey_Menu_Header', String(fKeyMenuHeader));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },
  /** Menu Cycle Workbench item box: items by description, then by menu description, then by recipe name (each capped server-side). */
  itemPickerSearch: (facilityId: number, q: string, take?: number) =>
    `/api/item/PickerSearch/${facilityId}?q=${encodeURIComponent(q)}${take != null ? `&take=${take}` : ''}`,
  /** Top bar search: items by description or this facility's menu description (capped server-side). */
  itemGlobalSearch: (facilityId: number, q: string, take: number) =>
    `/api/item/GlobalSearch/${facilityId}?q=${encodeURIComponent(q)}&take=${take}`,
  /** Top bar search: recipes by name, menu name or recipe number (capped server-side). */
  recipeGlobalSearch: (facilityId: number, q: string, take: number) =>
    `/api/recipe/GlobalSearch/${facilityId}?q=${encodeURIComponent(q)}&take=${take}`,
  itemProductionAreas: (facilityId: number) =>
    `/api/item/GetProductionAreas/${facilityId}`,
  itemGarnish: (facilityId: number) => `/api/item/GetGarnish/${facilityId}`,
  portions: (facilityId: number) => `/api/portion/${facilityId}`,
  itemTypes: (facilityId: number) => `/api/itemtype/${facilityId}`,
  itemSizes: (facilityId: number) => `/api/itemsize/${facilityId}`,
  itemSizesByItem: (facilityId: number, itemId: number) =>
    `/api/itemsize/ByItem/${facilityId}/${itemId}`,
  itemSizeAddMod: (facilityId: number) => `/api/itemsize/AddMod/${facilityId}`,
  itemSizeDelete: (facilityId: number, id: number) =>
    `/api/itemsize/${facilityId}/${id}`,
  itemSizeUsage: (facilityId: number, id: number) =>
    `/api/itemsize/${facilityId}/${id}/usage`,
  serviceItems: (facilityId: number) => `/api/serviceitem/${facilityId}`,
  recipes: (facilityId: number) => `/api/recipe/${facilityId}`,
  recipeById: (facilityId: number, recipeId: number) =>
    `/api/recipe/GetById/${facilityId}/${recipeId}`,
  recipeSave: (facilityId: number) => `/api/recipe/Save/${facilityId}`,
  recipeDelete: (facilityId: number, recipeId: number) =>
    `/api/recipe/${facilityId}/${recipeId}`,

  // Recipe - Ingredients
  recipeIngredients: (facilityId: number, recipeId: number) =>
    `/api/recipe/Ingredients/${facilityId}/${recipeId}`,
  recipeIngredientSave: (facilityId: number) =>
    `/api/recipe/Ingredients/Save/${facilityId}`,
  /** An ingredient and its amounts in one request (RecipeController.SaveIngredientWithSizes). */
  recipeIngredientSaveWithSizes: (facilityId: number) =>
    `/api/recipe/Ingredients/SaveWithSizes/${facilityId}`,
  recipeIngredientDelete: (facilityId: number, ingredientId: number) =>
    `/api/recipe/Ingredients/${facilityId}/${ingredientId}`,
  recipeIngredientPriorities: (facilityId: number, recipeId: number) =>
    `/api/recipe/Ingredients/UpdatePriorities/${facilityId}/${recipeId}`,

  // Recipe - Instructions
  recipeInstructions: (facilityId: number, recipeId: number) =>
    `/api/recipe/Instructions/${facilityId}/${recipeId}`,
  recipeInstructionSave: (facilityId: number) =>
    `/api/recipe/Instructions/Save/${facilityId}`,
  recipeInstructionDelete: (facilityId: number, instructionId: number) =>
    `/api/recipe/Instructions/${facilityId}/${instructionId}`,
  recipeInstructionPriorities: (facilityId: number, recipeId: number) =>
    `/api/recipe/Instructions/UpdatePriorities/${facilityId}/${recipeId}`,

  // Recipe - Categories
  recipeAllCategories: (facilityId: number) =>
    `/api/recipe/Categories/${facilityId}`,
  recipeCategoryMappings: (facilityId: number, recipeId: number) =>
    `/api/recipe/RecipeCategories/${facilityId}/${recipeId}`,
  recipeCategorySave: (facilityId: number, recipeId: number) =>
    `/api/recipe/RecipeCategories/Save/${facilityId}/${recipeId}`,
  recipeAllCategoryJunctions: (facilityId: number) =>
    `/api/recipe/AllRecipeCategories/${facilityId}`,

  // Recipe - Nutrients
  recipeNutrients: (facilityId: number, recipeId: number) =>
    `/api/recipe/Nutrients/${facilityId}/${recipeId}`,
  recipeNutrientsSave: (facilityId: number, recipeId: number) =>
    `/api/recipe/Nutrients/Save/${facilityId}/${recipeId}`,

  // Recipe - Sizes (tblRC_Recipe_Item_Unit)
  recipeSizes: (facilityId: number, recipeId: number) =>
    `/api/recipe/RecipeSizes/${facilityId}/${recipeId}`,
  recipeSizesSave: (facilityId: number, recipeId: number) =>
    `/api/recipe/RecipeSizes/Save/${facilityId}/${recipeId}`,

  // Recipe - Ingredient Sizes (tblRC_Recipe_Ingredient_Size)
  recipeIngredientSizesByRecipe: (facilityId: number, recipeId: number) =>
    `/api/recipe/IngredientSizes/${facilityId}/${recipeId}`,
  recipeIngredientSizesSave: (facilityId: number, ingredientId: number) =>
    `/api/recipe/IngredientSizes/Save/${facilityId}/${ingredientId}`,

  // Recipe - Auto Number / Clone
  recipeNewNumber: (facilityId: number) =>
    `/api/recipe/NewRecipeNumber/${facilityId}`,
  recipeClone: (facilityId: number, recipeId: number) =>
    `/api/recipe/Clone/${facilityId}/${recipeId}`,

  // Recipe - Preparation Types
  recipePreparationTypes: (facilityId: number) =>
    `/api/recipe/PreparationTypes/${facilityId}`,

  // Recipe - Usage Check, History, Allergies, Replace, OnMenu
  recipeCheckUsage: (facilityId: number, recipeId: number) =>
    `/api/recipe/CheckUsage/${facilityId}/${recipeId}`,
  recipeHistory: (facilityId: number, recipeId: number) =>
    `/api/recipe/History/${facilityId}/${recipeId}`,
  recipeAllergies: (facilityId: number, recipeId: number) =>
    `/api/recipe/Allergies/${facilityId}/${recipeId}`,
  recipeReplace: (facilityId: number, oldRecipeId: number, newRecipeId: number) =>
    `/api/recipe/Replace/${facilityId}/${oldRecipeId}/${newRecipeId}`,
  recipeOnMenu: (facilityId: number) =>
    `/api/recipe/OnMenu/${facilityId}`,
  recipeNutrition: (facilityId: number, recipeId: number, baseYield: number, driGroupId: number) =>
    `/api/recipe/Nutrition/${facilityId}/${recipeId}?baseYield=${baseYield}${driGroupId > 0 ? `&driGroupId=${driGroupId}` : ''}`,
  recipeNutrientReportSettings: (facilityId: number, recipeId: number, baseYield: number, driGroupId: number | null, driReport: boolean, showGraph: boolean) =>
    `/api/recipe/NutrientReportSettings/${facilityId}/${recipeId}?baseYield=${baseYield}${driGroupId ? `&driGroupId=${driGroupId}` : ''}&driReport=${driReport}&showGraph=${showGraph}`,

  // Menu Cycle Info (POST - body is DateTime)
  activeMenuCycleInfo: (facilityId: number) =>
    `/api/MenuCycleInfo/GetActiveMenuCycleInfo/${facilityId}`,

  // Selective Touch - Setup
  selectiveTouchPages: (facilityId: number) =>
    `/api/SelectiveTouch/GetPages/${facilityId}`,
  selectiveTouchLayout: (facilityId: number) =>
    `/api/SelectiveTouch/GetLayout/${facilityId}`,
  selectiveTouchFlags: (facilityId: number) =>
    `/api/SelectiveTouch/GetFlags/${facilityId}`,
  selectiveTouchUnitColors: (facilityId: number) =>
    `/api/SelectiveTouch/GetUnitMappingColors/${facilityId}`,

  // Selective Touch - Page Layout CRUD
  selectiveTouchSavePage: (facilityId: number) =>
    `/api/SelectiveTouch/SavePage/${facilityId}`,
  selectiveTouchDeletePage: (facilityId: number, pKey: number) =>
    `/api/SelectiveTouch/DeletePage/${facilityId}/${pKey}`,
  selectiveTouchSaveLayout: (facilityId: number, fKeyPage: number) =>
    `/api/SelectiveTouch/SaveLayout/${facilityId}/${fKeyPage}`,
  selectiveTouchReorderPages: (facilityId: number) =>
    `/api/SelectiveTouch/ReorderPages/${facilityId}`,
  selectiveTouchMenuCategoryUsage: (facilityId: number) =>
    `/api/SelectiveTouch/GetMenuCategoryUsage/${facilityId}`,

  // Selective Touch - Patient (POST)
  selectiveMenuPatients: (facilityId: number) =>
    `/api/Patient/GetSelectiveMenuPatients/${facilityId}`,

  // Selective Touch - Production (POST)
  selectiveMenuChoices: (facilityId: number) =>
    `/api/Production/GetSelectiveMenuChoices/${facilityId}`,
  /** GET: the credit a resident's meal was billed or credited with, or 0 (ProductionController.GetMealCredit). */
  selectiveMenuMealCredit: (facilityId: number) =>
    `/api/Production/MealCredit/${facilityId}`,
  saveSelectiveMenuChoices: (facilityId: number) =>
    `/api/Production/SaveSelectiveMenuChoices/${facilityId}`,
  printTickets: (facilityId: number) =>
    `/api/Production/PrintTickets/${facilityId}`,
  employeeSelectiveMenuChoices: (facilityId: number) =>
    `/api/Production/GetEmployeeSelectiveMenuChoices/${facilityId}`,
  saveEmployeeSelectiveMenuChoices: (facilityId: number) =>
    `/api/Production/SaveEmployeeSelectiveMenuChoices/${facilityId}`,
  selectiveMenuModifiers: (facilityId: number, modId: number) =>
    `/api/Production/GetModifiers/${facilityId}/${modId}`,
  selectiveMenuNutrients: (facilityId: number) =>
    `/api/Production/GetItemNutrients/${facilityId}`,
  selectiveMenuAllergenConflicts: (facilityId: number) =>
    `/api/Production/AllergenConflicts/${facilityId}`,
  selectiveMenuOffMenuAdjustments: (facilityId: number) =>
    `/api/Production/OffMenuAdjustments/${facilityId}`,

  // Selective Touch - Guest Schedule
  guestScheduleAdd: (facilityId: number, quantity: number) =>
    `/api/GuestSchedule/AddGuest/${facilityId}/${quantity}`,

  // Selective Touch - Print Queue (POST)
  printQueueAll: (facilityId: number) =>
    `/api/PrintQueue/GetAll/${facilityId}`,
  printQueueScheduled: (facilityId: number) =>
    `/api/PrintQueue/GetAllScheduled/${facilityId}`,
  printQueuePrinted: (facilityId: number) =>
    `/api/PrintQueue/GetAllPrinted/${facilityId}`,
  printQueuePatientScheduledTime: (facilityId: number, fKeyPatient: number, fKeyMenuHeader: number, mealtime: number) =>
    `/api/PrintQueue/PatientScheduledTime/${facilityId}/${fKeyPatient}/${fKeyMenuHeader}/${mealtime}`,
  printQueueClearQueue: (facilityId: number) =>
    `/api/PrintQueue/ClearQueue/${facilityId}`,
  printQueueClearQueueTable: (facilityId: number, tableId: number) =>
    `/api/PrintQueue/ClearQueueTable/${facilityId}/${tableId}`,
  printQueueClearScheduled: (facilityId: number) =>
    `/api/PrintQueue/ClearScheduled/${facilityId}`,
  printQueueRemoveResident: (facilityId: number, fKeyPatient: number) =>
    `/api/PrintQueue/RemoveResident/${facilityId}/${fKeyPatient}`,

  // Dining Room / Seating
  diningRoomFacilityMenuRooms: (facilityId: number, menuHeaderId: number) =>
    `/api/DiningRoom/GetFacilityMenuDiningRooms/${facilityId}/${menuHeaderId}`,
  diningRoomTimesByMealtime: (facilityId: number, mealtime: number, diningRoomId: number) =>
    `/api/DiningRoom/GetDiningTimesByMealtime/${facilityId}/${mealtime}/${diningRoomId}`,
  diningRoomTables: (facilityId: number, diningRoomId: number) =>
    `/api/DiningRoom/GetTables/${facilityId}/${diningRoomId}`,
  diningRoomSeatAssignments: (facilityId: number) =>
    `/api/DiningRoom/GetTableSeatAssignments/${facilityId}`,
  diningRoomDeletePendingSeat: (facilityId: number, seatNum: number, tableId: number, timeId: number, mealtime: number) =>
    `/api/DiningRoom/DeletePendingSeat/${facilityId}/${seatNum}/${tableId}/${timeId}/${mealtime}`,
  diningRoomUpdatePendingSeat: (facilityId: number) =>
    `/api/DiningRoom/UpdatePendingSeat/${facilityId}`,
  diningRoomClearTable: (facilityId: number, tableId: number, timeId: number, mealtime: number) =>
    `/api/DiningRoom/ClearTable/${facilityId}/${tableId}/${timeId}/${mealtime}`,
  diningRoomClearTablePending: (facilityId: number, tableId: number, timeId: number, mealtime: number) =>
    `/api/DiningRoom/ClearTablePending/${facilityId}/${tableId}/${timeId}/${mealtime}`,
  diningRoomDeleteSeat: (facilityId: number, seatNum: number, tableId: number, timeId: number, mealtime: number) =>
    `/api/DiningRoom/DeleteSeat/${facilityId}/${seatNum}/${tableId}/${timeId}/${mealtime}`,
  diningRoomUpdateTempSeats: (facilityId: number, tableId: number, numberOfTempSeats: number) =>
    `/api/DiningRoom/UpdateTempSeats/${facilityId}/${tableId}/${numberOfTempSeats}`,
  diningRoomAddPendingSeat: (facilityId: number) =>
    `/api/DiningRoom/AddPendingSeat/${facilityId}`,
  diningRoomAssignSeat: (facilityId: number) =>
    `/api/DiningRoom/AssignSeat/${facilityId}`,
  diningRoomGetAssignedPatients: (facilityId: number, menuHeaderId: number, mealtime: number) =>
    `/api/DiningRoom/GetAssignedPatients/${facilityId}/${menuHeaderId}/${mealtime}`,

  // Credits
  creditBalance: (facilityId: number, patientId: number) =>
    `/api/Credits/${facilityId}/patient/${patientId}/balance`,

  // Printers
  printerTypes: (facilityId: number) =>
    `/api/Printer/GetPrinterTypes/${facilityId}`,
  facilityPrinters: (facilityId: number, menuHeaderId: number, userId: number) =>
    `/api/Printer/GetFacilityPrinters/${facilityId}/${menuHeaderId}/${userId}`,
  saveUserPrinter: (facilityId: number, userId: number) =>
    `/api/Printer/SaveUserPrinter/${facilityId}/${userId}`,
  // Facility printer setup (replaces the WinForms Facility Printers forms)
  printerSetup: (facilityId: number) => `/api/Printer/Setup/${facilityId}`,
  facilityPrinter: (facilityId: number, printerId: number) =>
    `/api/Printer/FacilityPrinter/${facilityId}/${printerId}`,
  facilityPrinterSave: (facilityId: number) => `/api/Printer/FacilityPrinter/Save/${facilityId}`,
  facilityPrinterUsage: (facilityId: number, printerId: number) =>
    `/api/Printer/FacilityPrinter/Usage/${facilityId}/${printerId}`,

  // Menu Header
  menuHeaderById: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/${facilityId}/${menuHeaderId}`,
  menuHeadersActive: (facilityId: number) =>
    `/api/menuheader/Active/${facilityId}`,
  menuHeaderAddMod: (facilityId: number) =>
    `/api/menuheader/AddMod/${facilityId}`,
  /** Copy toolbar — Phase 1 (Mode B: Copy to EXISTING slice).
   *  Body: `MenuCycleCopyToExistingRequest`. Server-side wraps
   *  the shared VB `ProductionMenuCycleCopyManager` under a
   *  MenuCycleLockManager lock; response tells us whether the
   *  lock was acquired and (on failure) who's holding it. */
  menuHeaderCopyToExisting: (facilityId: number) =>
    `/api/menuheader/CopyToExisting/${facilityId}`,
  /** Copy toolbar — Copy Override branch (From is a holiday menu →
   *  creates a NEW holiday under the destination master). WinForms
   *  `panelCopyOverride` at frmDietech_Menu_Cycle_CopyNew.vb:2554-2586. */
  menuHeaderCopyHolidayToOverride: (facilityId: number) =>
    `/api/menuheader/CopyHolidayToOverride/${facilityId}`,
  /** Copy holiday menus to another menu cycle (Menu Cycles list / Holiday/Override
   *  Menus window). Each becomes a new holiday on the target, its diet settings on
   *  the target's diet columns. */
  menuCycleModifyCopyHolidaysToCycle: (facilityId: number) =>
    `/api/MenuCycleModify/CopyHolidaysToCycle/${facilityId}`,
  /** Roll out to facilities (Menu Cycles page): one holiday menu or a new season's menu
   *  cycle for many facilities, previewed, then made in a background job that is polled. */
  menuRolloutTargets: (facilityId: number) => `/api/MenuRollout/Targets/${facilityId}`,
  menuRolloutSourceHolidays: (facilityId: number) => `/api/MenuRollout/SourceHolidays/${facilityId}`,
  menuRolloutPreviewHoliday: (facilityId: number) => `/api/MenuRollout/PreviewHoliday/${facilityId}`,
  menuRolloutPreviewSeason: (facilityId: number) => `/api/MenuRollout/PreviewSeason/${facilityId}`,
  menuRolloutStartHoliday: (facilityId: number) => `/api/MenuRollout/StartHoliday/${facilityId}`,
  menuRolloutStartSeason: (facilityId: number) => `/api/MenuRollout/StartSeason/${facilityId}`,
  menuRolloutSchedules: (facilityId: number) => `/api/MenuRollout/Schedules/${facilityId}`,
  menuRolloutCancelSchedule: (facilityId: number, scheduleId: number) => `/api/MenuRollout/CancelSchedule/${facilityId}/${scheduleId}`,
  menuRolloutJob: (facilityId: number, jobId: string) =>
    `/api/MenuRollout/Job/${facilityId}/${encodeURIComponent(jobId)}`,
  /** Copy toolbar — Phase 2 (Copy to NEW master). Creates a new
   *  master menu cycle from a source master + optional
   *  Holiday/Override bring-forward + optional Selective Menu
   *  bring-forward. WinForms `CreateNewMenuCycle` at
   *  frmDietech_Menu_Cycle_CopyNew.vb:3097-3446. */
  menuHeaderCopyToNew: (facilityId: number) =>
    `/api/menuheader/CopyToNew/${facilityId}`,
  /** Progress polling for a Copy To New in flight. The client
   *  mints an operationId, ships it in the CopyToNew request body,
   *  and polls this endpoint every ~400 ms until Done=true. 404
   *  after 2 min of no updates (store TTL) — the client treats
   *  that as "already done, nothing to show". */
  menuHeaderCopyProgress: (operationId: string) =>
    `/api/menuheader/CopyProgress/${operationId}`,
  menuHeaderDelete: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/${facilityId}/${menuHeaderId}`,
  // WinForms-cascade Delete for the Menu Cycles page. Preflight
  // returns counts that vary the confirmation dialog text; the
  // POST does the full cascade (parent + child holidays + facility
  // deactivations + employee-menu clear + audit logs).
  menuHeaderDeletePreflight: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/DeletePreflight/${facilityId}/${menuHeaderId}`,
  menuHeaderDeleteCascade: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/DeleteCascade/${facilityId}/${menuHeaderId}`,
  // WinForms DeleteHolidayMenuHeader cascade for a single detail
  // (holiday / override) row. See MenuHeaderDAL.SoftDeleteHolidayCascade.
  menuHeaderDeleteHolidayCascade: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/DeleteHolidayCascade/${facilityId}/${menuHeaderId}`,
  // WinForms `frmDietech_Menu_Cycle_AddMod` — General Information
  // tab. Get = load existing (Modify path); SaveMetadata handles
  // both Add (PKey null) and Modify (PKey set).
  menuHeaderMetadata: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/Metadata/${facilityId}/${menuHeaderId}`,
  menuHeaderSaveMetadata: (facilityId: number) =>
    `/api/menuheader/SaveMetadata/${facilityId}`,
  menuHeaderRestore: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/Restore/${facilityId}/${menuHeaderId}`,
  menuHeaderRelatedFacilities: (facilityId: number) =>
    `/api/menuheader/RelatedFacilities/${facilityId}`,
  menuHeaderShare: (facilityId: number) =>
    `/api/menuheader/Share/${facilityId}`,
  menuHeaderShareStatus: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/ShareStatus/${facilityId}/${menuHeaderId}`,
  menuHeaderDeviationSummary: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/DeviationSummary/${facilityId}/${menuHeaderId}`,
  menuHeaderHolidayMenus: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/HolidayMenus/${facilityId}/${menuHeaderId}`,
  // Per-facility holiday feed for the Menu Cycles master-detail
  // (includes IsActive via join to tblMenu_Facility). Needed for
  // the 4-view bucket logic (Holiday / Active / Always Served /
  // History) matching WinForms .vb:1313 SetMasterDetailRelationships.
  menuHeaderHolidayMenusMasterDetail: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/HolidayMenusMasterDetail/${facilityId}/${menuHeaderId}`,
  // Parent → live-child-holiday count map. Client uses membership
  // to hide the master-detail chevron on empty rows.
  menuHeaderHolidayCountsByParent: (facilityId: number) =>
    `/api/menuheader/HolidayCountsByParent/${facilityId}`,
  menuHeaderDeleted: (facilityId: number) =>
    `/api/menuheader/Deleted/${facilityId}`,

  // Menu Cycle Recycle Bin — new dedicated controller with the
  // WinForms-parity master-detail + conflict-aware restore flow.
  menuCycleRecycleBinContext: (facilityId: number, showOld: boolean) =>
    `/api/MenuCycleRecycleBin/${facilityId}?showOld=${showOld ? 'true' : 'false'}`,
  menuCycleRecycleBinRestoreMaster: (facilityId: number, pKey: number) =>
    `/api/MenuCycleRecycleBin/Restore/Master/${facilityId}/${pKey}`,
  menuCycleRecycleBinRestoreHoliday: (facilityId: number, pKey: number, strategy?: string) =>
    strategy
      ? `/api/MenuCycleRecycleBin/Restore/Holiday/${facilityId}/${pKey}?strategy=${encodeURIComponent(strategy)}`
      : `/api/MenuCycleRecycleBin/Restore/Holiday/${facilityId}/${pKey}`,
  menuCycleRecycleBinDeleteMaster: (facilityId: number, pKey: number) =>
    `/api/MenuCycleRecycleBin/Master/${facilityId}/${pKey}`,
  menuCycleRecycleBinDeleteHoliday: (facilityId: number, pKey: number) =>
    `/api/MenuCycleRecycleBin/Holiday/${facilityId}/${pKey}`,
  menuCycleRecycleBinEmpty: (facilityId: number, showOld: boolean) =>
    `/api/MenuCycleRecycleBin/Empty/${facilityId}?showOld=${showOld ? 'true' : 'false'}`,
  menuHeaderAlwaysServed: (facilityId: number) =>
    `/api/menuheader/AlwaysServed/${facilityId}`,
  menuHeaderCost: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/Cost/${facilityId}/${menuHeaderId}`,
  menuHeaderItemCost: (facilityId: number, menuHeaderId: number) =>
    `/api/menuheader/ItemCost/${facilityId}/${menuHeaderId}`,
  // Menu Cost dialog (WinForms frmDietech_Menu_Cycle_Cost): GET loads, POST saves.
  menuCycleCost: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuCycleCost/${facilityId}/${menuHeaderId}`,
  // Push MC Items into Global Adjustments (WinForms frmDietech_Prd_Menu_Cycle_ImportGlobalAdjustment):
  // GET the food group / item grid, POST the import.
  menuCycleGlobalAdjustmentImport: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuCycleGlobalAdjustmentImport/${facilityId}/${menuHeaderId}`,

  // Menu Cycle Modify (items editor) — Stage 1 read-only load.
  // Server reuses shared VB `PrdMenuCycle.FillData()`; both variants
  // return the same envelope shape (`MenuCycleModifyLoadResult`).
  menuCycleModifyLoad: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuCycleModify/Load/${facilityId}/${menuHeaderId}`,
  menuCycleModifyLoadHoliday: (facilityId: number, holidayId: number, parentId: number) =>
    `/api/MenuCycleModify/LoadHoliday/${facilityId}/${holidayId}/${parentId}`,
  // Phase 1 pilot — optimistic single-boolean-field write.
  // Field name is server-whitelisted (see `MenuCycleModifyDAL
  // ._updateRowBoolAllowlist`); a rogue field name 400s.
  menuCycleModifyUpdateRowBool: (facilityId: number) =>
    `/api/MenuCycleModify/UpdateRowBool/${facilityId}`,
  // Phase 2 pilot — optimistic nullable-int (FKey) write for
  // dropdown editors (Consist / Item Type). Server allowlist at
  // `MenuCycleModifyDAL._updateRowFKeyAllowlist`.
  menuCycleModifyUpdateRowFKey: (facilityId: number) =>
    `/api/MenuCycleModify/UpdateRowFKey/${facilityId}`,
  // Sizes available for a specific Item — used by the MC Modify
  // Size dropdown editor. Filters out Size_Num=1 default.
  menuCycleModifyItemSizes: (facilityId: number, fKeyItem: number) =>
    `/api/MenuCycleModify/ItemSizes/${facilityId}/${fKeyItem}`,
  menuCycleModifyMenuCategories: (facilityId: number) =>
    `/api/MenuCycleModify/MenuCategories/${facilityId}`,
  menuCycleModifyAddDetail: (facilityId: number) =>
    `/api/MenuCycleModify/AddDetail/${facilityId}`,
  menuCycleModifyDeleteDetail: (facilityId: number) =>
    `/api/MenuCycleModify/DeleteDetail/${facilityId}`,
  menuCycleModifyDeleteMealtime: (facilityId: number) =>
    `/api/MenuCycleModify/DeleteMealtime/${facilityId}`,
  menuCycleModifyDeleteDay: (facilityId: number) =>
    `/api/MenuCycleModify/DeleteDay/${facilityId}`,
  menuCycleModifyDeleteUtility: (facilityId: number) =>
    `/api/MenuCycleModify/DeleteUtility/${facilityId}`,
  menuCycleModifyDeleteByDeviation: (facilityId: number) =>
    `/api/MenuCycleModify/DeleteByDeviation/${facilityId}`,
  // AIM - Push into Item (Workbench row menu): preview, then save into the item's AIM rules.
  menuCycleAimPushPreview: (facilityId: number, menuHeaderId: number, detailId: number, parentId?: number | null) =>
    `/api/MenuCycleAimPush/${facilityId}/${menuHeaderId}/${detailId}?parent=${parentId ?? 0}`,
  menuCycleAimPushSave: (facilityId: number) =>
    `/api/MenuCycleAimPush/${facilityId}`,
  // Modifier Group toolbar
  menuCycleModifyModifierGroupSave: (facilityId: number) =>
    `/api/MenuCycleModify/ModifierGroup/Save/${facilityId}`,
  menuCycleModifyModifierGroupUsageCount: (facilityId: number, pKey: number) =>
    `/api/MenuCycleModify/ModifierGroup/UsageCount/${facilityId}/${pKey}`,
  menuCycleModifyModifierGroupDelete: (facilityId: number) =>
    `/api/MenuCycleModify/ModifierGroup/Delete/${facilityId}`,
  // Vendor menu import: match vendor item names to the facility's items
  menuImportMatch: (facilityId: number) =>
    `/api/MenuImport/Match/${facilityId}`,
  menuImportNotes: (facilityId: number) =>
    `/api/MenuImport/Notes/${facilityId}`,
  menuImportReview: (facilityId: number) =>
    `/api/MenuImport/Review/${facilityId}`,
  menuImportRemember: (facilityId: number) =>
    `/api/MenuImport/Remember/${facilityId}`,
  menuImportExtras: (facilityId: number, vendor: string) =>
    `/api/MenuImport/Extras/${facilityId}?vendor=${encodeURIComponent(vendor)}`,
  menuImportForget: (facilityId: number) =>
    `/api/MenuImport/Forget/${facilityId}`,
  menuImportVendors: (facilityId: number) =>
    `/api/MenuImport/Vendors/${facilityId}`,
  menuImportTemplates: (facilityId: number, include?: number | null) =>
    `/api/MenuImport/Templates/${facilityId}${include ? `?include=${include}` : ''}`,
  menuImportCompare: (facilityId: number) =>
    `/api/MenuImport/Compare/${facilityId}`,
  menuImportFacts: (facilityId: number) =>
    `/api/MenuImport/Facts/${facilityId}`,
  menuImportBuildStart: (facilityId: number, draftId?: number | null) =>
    `/api/MenuImport/BuildStart/${facilityId}${draftId ? `?draftId=${draftId}` : ''}`,
  menuImportDrafts: (facilityId: number) => `/api/MenuImport/Drafts/${facilityId}`,
  menuImportDraft: (facilityId: number, id?: number) => `/api/MenuImport/Draft/${facilityId}${id ? `/${id}` : ''}`,
  menuImportBuildStatus: (facilityId: number, jobId: string) =>
    `/api/MenuImport/BuildStatus/${facilityId}/${jobId}`,
  menuImportBuild: (facilityId: number) =>
    `/api/MenuImport/Build/${facilityId}`,
  menuImportPreview: (facilityId: number) => `/api/MenuImport/Preview/${facilityId}`,
  menuImportFacilityColumns: (facilityId: number) => `/api/MenuImport/FacilityColumns/${facilityId}`,
  menuBuilderOptions: (facilityId: number) => `/api/MenuBuilder/Options/${facilityId}`,
  menuBuilderPattern: (facilityId: number, templateId: number, frame?: number[]) =>
    `/api/MenuBuilder/Pattern/${facilityId}/${templateId}${frame && frame.length ? `?${frame.map((f) => `frame=${f}`).join('&')}` : ''}`,
  menuBuilderStart: (facilityId: number) => `/api/MenuBuilder/Start/${facilityId}`,
  menuBuilderStatus: (facilityId: number, jobId: string) => `/api/MenuBuilder/Status/${facilityId}/${jobId}`,
  menuBuilderCancel: (facilityId: number, jobId: string) => `/api/MenuBuilder/Cancel/${facilityId}/${jobId}`,
  menuBuilderRemember: (facilityId: number, jobId: string) => `/api/MenuBuilder/Remember/${facilityId}/${jobId}`,
  /** What the AI menu builder remembers about the customer (MenuMemoryController). */
  menuMemoryAlternatives: (facilityId: number) => `/api/MenuMemory/Alternatives/${facilityId}`,
  menuMemoryRefresh: (facilityId: number) => `/api/MenuMemory/Refresh/${facilityId}`,
  menuMemoryRefreshStatus: (facilityId: number) => `/api/MenuMemory/RefreshStatus/${facilityId}`,
  menuMemoryAlternativeEnabled: (facilityId: number, id: number) => `/api/MenuMemory/AlternativeEnabled/${facilityId}/${id}`,
  menuMemoryRules: (facilityId: number) => `/api/MenuMemory/Rules/${facilityId}`,
  menuMemoryRule: (facilityId: number) => `/api/MenuMemory/Rule/${facilityId}`,
  menuMemoryRuleActive: (facilityId: number, id: number) => `/api/MenuMemory/RuleActive/${facilityId}/${id}`,
  menuMemorySwaps: (facilityId: number) => `/api/MenuMemory/Swaps/${facilityId}`,
  menuMemoryReadWebEdits: (facilityId: number) => `/api/MenuMemory/ReadWebEdits/${facilityId}`,
  menuMemorySwapEnabled: (facilityId: number, id: number) => `/api/MenuMemory/SwapEnabled/${facilityId}/${id}`,
  menuMemoryDecisions: (facilityId: number) => `/api/MenuMemory/Decisions/${facilityId}`,
  menuMemoryDecisionEnabled: (facilityId: number, id: number) => `/api/MenuMemory/DecisionEnabled/${facilityId}/${id}`,
  /** Change a menu: AI proposals on an existing menu, applied to a copy (MenuChangeController). */
  menuChangeOptions: (facilityId: number) => `/api/MenuChange/Options/${facilityId}`,
  menuChangeStart: (facilityId: number) => `/api/MenuChange/Start/${facilityId}`,
  menuChangeStatus: (facilityId: number, jobId: string) => `/api/MenuChange/Status/${facilityId}/${jobId}`,
  menuChangeRetry: (facilityId: number, jobId: string) => `/api/MenuChange/Retry/${facilityId}/${jobId}`,
  menuChangeApply: (facilityId: number, jobId: string) => `/api/MenuChange/Apply/${facilityId}/${jobId}`,
  menuChangeCancel: (facilityId: number, jobId: string) => `/api/MenuChange/Cancel/${facilityId}/${jobId}`,
  menuChangePreview: (facilityId: number, jobId: string) => `/api/MenuChange/Preview/${facilityId}/${jobId}`,
  // Menu nutrition analysis per diet order
  menuAnalysisOptions: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuAnalysis/Options/${facilityId}/${menuHeaderId}`,
  menuAnalysisStart: (facilityId: number) =>
    `/api/MenuAnalysis/Start/${facilityId}`,
  menuAnalysisStatus: (jobId: string) =>
    `/api/MenuAnalysis/Status/${jobId}`,
  menuAnalysisCancel: (jobId: string) =>
    `/api/MenuAnalysis/Cancel/${jobId}`,
  menuAnalysisExplain: (facilityId: number) =>
    `/api/MenuAnalysis/Explain/${facilityId}`,
  menuAnalysisMeal: (facilityId: number) =>
    `/api/MenuAnalysis/Meal/${facilityId}`,
  menuAnalysisMealStatus: (facilityId: number, jobId: string) =>
    `/api/MenuAnalysis/MealStatus/${facilityId}/${jobId}`,
  menuAnalysisResident: (facilityId: number) =>
    `/api/MenuAnalysis/Resident/${facilityId}`,
  menuAnalysisResidentWeek: (facilityId: number) =>
    `/api/MenuAnalysis/ResidentWeek/${facilityId}`,
  menuAnalysisIntakeStudies: (facilityId: number) =>
    `/api/MenuAnalysis/IntakeStudies/${facilityId}`,
  menuAnalysisIntake: (facilityId: number) =>
    `/api/MenuAnalysis/Intake/${facilityId}`,
  // Swap meals / categories / items between days (one-time overrides or the cycle itself)
  menuSwapLoad: (facilityId: number) =>
    `/api/MenuSwap/Load/${facilityId}`,
  menuSwapApply: (facilityId: number) =>
    `/api/MenuSwap/Apply/${facilityId}`,
  menuSwapWeekMenu: (facilityId: number) =>
    `/api/MenuSwap/WeekMenu/${facilityId}`,
  // Read returned extension sheets / typed week menus against the menu
  menuRequestRead: (facilityId: number) =>
    `/api/MenuRequest/Read/${facilityId}`,
  // Request a Menu Change: the facility asks PSI for a change (sent on to Tech Log)
  menuChangeRequestLoad: (facilityId: number) =>
    `/api/MenuChangeRequests/Load/${facilityId}`,
  menuChangeRequestColumns: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuChangeRequests/Columns/${facilityId}/${menuHeaderId}`,
  menuChangeRequests: (facilityId: number) =>
    `/api/MenuChangeRequests/${facilityId}`,
  // One of the facility's requests (DELETE: withdraw it while PSI hasn't started it)
  menuChangeRequest: (facilityId: number, requestId: number) =>
    `/api/MenuChangeRequests/${facilityId}/${requestId}`,
  // Menu Preview panel
  menuCycleModifyPreviewFocusedRow: (facilityId: number) =>
    `/api/MenuCycleModify/Preview/FocusedRow/${facilityId}`,
  menuCycleModifyPreviewMeal: (facilityId: number) =>
    `/api/MenuCycleModify/Preview/Meal/${facilityId}`,
  menuCycleModifyStickyLookups: (facilityId: number, menuHeaderId: number) =>
    `/api/MenuCycleModify/StickyLookups/${facilityId}/${menuHeaderId}`,
  menuCycleModifyDRIGroups: (facilityId: number) =>
    `/api/MenuCycleModify/DRIGroups/${facilityId}`,
  menuCycleModifyCopyMealtime: (facilityId: number) =>
    `/api/MenuCycleModify/CopyMealtime/${facilityId}`,
  menuCycleModifyCopyCurrentMC: (facilityId: number) =>
    `/api/MenuCycleModify/CopyCurrentMenuCategory/${facilityId}`,
  menuCycleModifyCopyMenuCategories: (facilityId: number) =>
    `/api/MenuCycleModify/CopyMenuCategories/${facilityId}`,
  menuCycleModifyFillMenuCategories: (facilityId: number) =>
    `/api/MenuCycleModify/FillMenuCategories/${facilityId}`,
  menuCycleModifyOrganizeMenuCategories: (facilityId: number) =>
    `/api/MenuCycleModify/OrganizeMenuCategories/${facilityId}`,
  menuCycleModifyRebuildLevels: (facilityId: number) =>
    `/api/MenuCycleModify/RebuildLevels/${facilityId}`,
  menuCycleModifyRebuildCustomPaths: (facilityId: number) =>
    `/api/MenuCycleModify/RebuildCustomPaths/${facilityId}`,
  menuCycleModifyBreakSync: (facilityId: number) =>
    `/api/MenuCycleModify/BreakSync/${facilityId}`,
  menuCycleModifyCreateSync: (facilityId: number) =>
    `/api/MenuCycleModify/CreateSync/${facilityId}`,
  menuCycleModifyCopyDetail: (facilityId: number) =>
    `/api/MenuCycleModify/CopyDetail/${facilityId}`,
  menuCycleModifyUpdateSyncedGroup: (facilityId: number) =>
    `/api/MenuCycleModify/UpdateSyncedGroup/${facilityId}`,
  menuCycleModifySaveMenuDetailInfo: (facilityId: number) =>
    `/api/MenuCycleModify/SaveMenuDetailInfo/${facilityId}`,
  menuCycleModifySaveCustomPathRequiredMc: (facilityId: number) =>
    `/api/MenuCycleModify/SaveCustomPathRequiredMc/${facilityId}`,
  menuCycleModifySaveCustomPathSkipMc: (facilityId: number) =>
    `/api/MenuCycleModify/SaveCustomPathSkipMc/${facilityId}`,
  menuCycleModifySaveCustomPathAssociations: (facilityId: number) =>
    `/api/MenuCycleModify/SaveCustomPathAssociations/${facilityId}`,
  menuCycleModifySaveCustomPathStopPath: (facilityId: number) =>
    `/api/MenuCycleModify/SaveCustomPathStopPath/${facilityId}`,
  menuCycleModifyApplyAimAutoFill: (facilityId: number) =>
    `/api/MenuCycleModify/ApplyAimAutoFill/${facilityId}`,
  menuCycleModifyApplyAimFillItem: (facilityId: number) =>
    `/api/MenuCycleModify/ApplyAimFillItem/${facilityId}`,
  // Recipe Composition analyzer (Tools ribbon → Analysis group).
  // POST body: { FKey_Menu_Header, Day, Mealtime }. Response is
  // three parallel PKey_Prd_Menu_Detail arrays (matched / not-
  // matched / added) — one glyph bucket each. See WinForms
  // `mbtnRecipeComposition_ItemClick` (frmDietech_Prd_Menu_Cycle
  // .vb:17629-17727) — this endpoint is the server-side port.
  menuCycleModifyRecipeComposition: (facilityId: number) =>
    `/api/MenuCycleModify/RecipeComposition/${facilityId}`,
  // Push To (Tools ribbon → Copy Utilities group). POST body:
  // `{ FKey_Menu_Header, SourcePKey_Prd_Menu_Detail,
  //    TargetPKeys_Prd_Menu_Detail, IncludeDeviations }`. Response
  // is `{ results: SyncOperationResponse[] }` — one entry per
  // input target in the same order. Ports WinForms
  // `mbtnCopy_PushTo_ItemClick`
  // (frmDietech_Prd_Menu_Cycle.vb:17566-17609) — one-to-many copy
  // over the shared CopyDetail primitive.
  menuCycleModifyPushTo: (facilityId: number) =>
    `/api/MenuCycleModify/PushTo/${facilityId}`,
  // Push To Phase 2a — cross-menu discovery. Returns eligible target
  // menus for a source item + position, with facility permissions and
  // DF-subset filter already applied. Client uses the response to
  // render the Menu Filter picker and gate the auto-load threshold.
  menuCycleModifyPushCandidateMenus: (
    facilityId: number,
    fKeyItem: number,
    sourceMenuHeader: number,
    sourcePosition: number,
    sourceRowPKey: number,
  ) => `/api/MenuCycleModify/PushCandidateMenus/${facilityId}`
    + `?FKey_Item=${fKeyItem}`
    + `&SourceMenuHeader=${sourceMenuHeader}`
    + `&SourcePosition=${sourcePosition}`
    + `&SourceRowPKey=${sourceRowPKey}`,
  // Push To Phase 2b — cross-menu candidate row hydration. POST body
  // carries the parent PKey list from the picker; returns candidate
  // rows enriched with `menuName` + `parentMenuHeaderPKey` + remapped
  // DetailDf pivot cells.
  menuCycleModifyPushCandidateRows: (facilityId: number) =>
    `/api/MenuCycleModify/PushCandidateRows/${facilityId}`,
  // Push To Phase 2c — cross-menu Push write. Body carries source
  // metadata + list of (menuHeaderPKey, targetRowPKey) pairs.
  menuCycleModifyPushToCrossMenu: (facilityId: number) =>
    `/api/MenuCycleModify/PushToCrossMenu/${facilityId}`,
  menuCycleModifyBumpSwap: (facilityId: number) =>
    `/api/MenuCycleModify/BumpSwap/${facilityId}`,
  menuCycleModifyBumpOut: (facilityId: number) =>
    `/api/MenuCycleModify/BumpOut/${facilityId}`,
  menuCycleModifyBumpIn: (facilityId: number) =>
    `/api/MenuCycleModify/BumpIn/${facilityId}`,
  menuCycleModifyDfOverrideSave: (facilityId: number) =>
    `/api/MenuCycleModify/DfOverride/${facilityId}`,
  // Ribbon Clipboard: Paste. Body: PasteRowsRequest — a target
  // PKey_Prd_Menu_Detail plus a serialized clip payload cloned from
  // localStorage. Server performs the WinForms `ReplaceMenuDetailRow`
  // + link-fixup + depth-cap semantics in one transaction and returns
  // {Committed, SkippedReason?} — client refetches when Committed.
  menuCycleModifyPasteRows: (facilityId: number) =>
    `/api/MenuCycleModify/PasteRows/${facilityId}`,
  // Immediate icon-toggle write for the DT / Consist band on the
  // MC Modify grid. Body: {FKey_Prd_Menu_Detail, FKey_Prd_Menu_DF,
  // Enabled}. Server UPSERTs the Detail_DF row and, when a DT
  // toggle goes to 0 (or a Consist toggle goes to 2), cascades a
  // ClearOverride: NULLs the row's override fields, deletes every
  // Detail_DF_Override child, deletes every CombinationDietOverride
  // (with its _DF triggers and _Child rows). Returns the new /
  // existing PKey_Prd_Menu_Detail_DF.
  menuCycleModifyDfEnabled: (facilityId: number) =>
    `/api/MenuCycleModify/DfEnabled/${facilityId}`,
  // Item Details Popup — SaveItemDetails, SyncMenuCategory,
  // RebuildMenuCategory, sync target lookup, synced-master flags
  // read. Backing WinForms `pccItemDetails` popup close/sync/
  // rebuild branches (see modSQL region comments).
  menuCycleModifyItemDetailsSave: (facilityId: number) =>
    `/api/MenuCycleModify/ItemDetails/${facilityId}`,
  menuCycleModifyItemDetailsSyncTargets: (facilityId: number, pKey: number) =>
    `/api/MenuCycleModify/ItemDetails/SyncTargets/${facilityId}/${pKey}`,
  menuCycleModifyItemDetailsSyncedMasterFlags: (facilityId: number, pKeyMaster: number) =>
    `/api/MenuCycleModify/ItemDetails/SyncedMasterFlags/${facilityId}/${pKeyMaster}`,
  menuCycleModifyItemDetailsSyncMenuCategory: (facilityId: number) =>
    `/api/MenuCycleModify/ItemDetails/SyncMenuCategory/${facilityId}`,
  menuCycleModifyItemDetailsRebuildMenuCategory: (facilityId: number) =>
    `/api/MenuCycleModify/ItemDetails/RebuildMenuCategory/${facilityId}`,
  // Position=1 always-served view: 7-day active-day panel.
  // Server MERGE-upserts by FKey_Prd_Menu_Detail so first-set +
  // update both go through this endpoint.
  menuCycleModifyItemDetailsAlwaysServedActive: (facilityId: number) =>
    `/api/MenuCycleModify/ItemDetails/AlwaysServedActive/${facilityId}`,
  // Facility tab child-list save. Server MERGE-diffs by
  // (FKey_Prd_Menu_Detail, FKey_Facility): inserts missing rows,
  // deletes surplus. Empty list clears every child row for the
  // detail.
  menuCycleModifyItemDetailsDetailFacilities: (facilityId: number) =>
    `/api/MenuCycleModify/ItemDetails/DetailFacilities/${facilityId}`,

  // Holiday Menus
  holidayMenus: (facilityId: number) =>
    `/api/holidaymenu/${facilityId}`,
  holidayMenuById: (facilityId: number, id: number) =>
    `/api/holidaymenu/${facilityId}/${id}`,
  holidayMenusByParent: (facilityId: number, parentId: number) =>
    `/api/holidaymenu/ByParent/${facilityId}/${parentId}`,
  holidayMenusActive: (facilityId: number, parentId: number) =>
    `/api/holidaymenu/Active/${facilityId}/${parentId}`,
  holidayMenuAddMod: (facilityId: number) =>
    `/api/holidaymenu/AddMod/${facilityId}`,
  holidayMenuDelete: (facilityId: number, id: number) =>
    `/api/holidaymenu/${facilityId}/${id}`,
  holidayMenuRestore: (facilityId: number, id: number) =>
    `/api/holidaymenu/Restore/${facilityId}/${id}`,
  holidayMenuSetActive: (facilityId: number, id: number) =>
    `/api/holidaymenu/SetActive/${facilityId}/${id}`,
  holidayMenuDeleted: (facilityId: number, parentId: number) =>
    `/api/holidaymenu/Deleted/${facilityId}/${parentId}`,

  // Menu Facility
  menuFacilitySetActive: (facilityId: number, menuHeaderId: number, isActive: boolean) =>
    `/api/menufacility/SetActive/${facilityId}/${menuHeaderId}/${isActive}`,
  // WinForms-parity activate-only endpoint. Server refuses to
  // deactivate, no-ops when already active, writes audit-log
  // entries for the new + previously-active menu. Preferred over
  // menuFacilitySetActive above for the Menu Cycles page.
  menuFacilityActivate: (facilityId: number, menuHeaderId: number) =>
    `/api/menufacility/Activate/${facilityId}/${menuHeaderId}`,
  menuFacilityCurrent: (facilityId: number, menuHeaderId: number) =>
    `/api/menufacility/Current/${facilityId}/${menuHeaderId}`,

  // Log Interaction
  logInteractions: (facilityId: number, logType: number, patientId: number) =>
    `/api/LogInteraction/${facilityId}/${logType}/${patientId}`,
  /** One item's interaction history. Shared: the generic route above
   *  queries by FKey_Patient, so an item id returns nothing there. */
  logInteractionsItem: (facilityId: number, itemId: number) =>
    `/api/LogInteraction/Item/${facilityId}/${itemId}`,
  /** Items list > History > Deleted Items (FormType 3 Item_DataGrid_Deleted). */
  logInteractionsItemDeleted: (facilityId: number) =>
    `/api/LogInteraction/ItemDeleted/${facilityId}`,
  logInteractionAdd: (facilityId: number) =>
    `/api/LogInteraction/Add/${facilityId}`,
  /** Composite per-row detail payload for the Patient Profile
   *  Interaction Log expand — SM + MP + Adj history over the
   *  last N days (default 30, matching WinForms). Server does
   *  the view/modify pairing to stamp FKey_Log_Interaction on
   *  MP/Adj rows. */
  logInteractionDetails: (facilityId: number, patientId: number, days: number = 30) =>
    `/api/LogInteraction/Details/${facilityId}/${patientId}?days=${days}`,
  logInteractionSystem: (facilityId: number, startDate: string, endDate: string) =>
    `/api/LogInteraction/System/${facilityId}?startDate=${startDate}&endDate=${endDate}`,
  logInteractionDeletedResidents: (facilityId: number) =>
    `/api/LogInteraction/DeletedResidents/${facilityId}`,
  // Tools → History / Deleted Items on the Global Meal
  // Patterns + Global Adjustments page. Server pulls from
  // `tblLog_Interaction` filtered by the global-FK column
  // (or by Type=Delete for the deleted-items variant) via
  // the existing wrapper SQL helpers.
  logInteractionGlobalMP: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/GlobalMP/${facilityId}/${pKey}`,
  logInteractionGlobalMPDeleted: (facilityId: number) =>
    `/api/LogInteraction/GlobalMPDeleted/${facilityId}`,
  logInteractionGlobalAdj: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/GlobalAdj/${facilityId}/${pKey}`,
  logInteractionGlobalAdjDeleted: (facilityId: number) =>
    `/api/LogInteraction/GlobalAdjDeleted/${facilityId}`,

  // Allergy Group interaction log — History on the Allergy Groups
  // table. `…AllergyGroup` is every row for one group
  // (WinForms InteractionLogFormType.AllergyGroup_DataGrid = 27);
  // `…AllergyGroupDeleted` is facility-wide deletes
  // (AllergyGroup_DataGrid_Delete = 26).
  logInteractionAllergyGroup: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/AllergyGroup/${facilityId}/${pKey}`,
  logInteractionAllergyGroupDeleted: (facilityId: number) =>
    `/api/LogInteraction/AllergyGroupDeleted/${facilityId}`,

  // Service Item interaction log — the History ribbon group on
  // `ucDietech_ServiceItems.vb`. FormType 35 ServiceItem_DataGrid /
  // 36 ServiceItem_Deleted.
  logInteractionServiceItem: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/ServiceItem/${facilityId}/${pKey}`,
  logInteractionServiceItemDeleted: (facilityId: number) =>
    `/api/LogInteraction/ServiceItemDeleted/${facilityId}`,

  // Tube Feed Product interaction log — the History ribbon group on
  // `ucDietech_TubeFeedProducts.vb`. InteractionLogFormType 65
  // TubeFeedProduct_DataGrid / 66 TubeFeedProduct_Deleted.
  logInteractionTubeFeedProduct: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/TubeFeedProduct/${facilityId}/${pKey}`,
  logInteractionTubeFeedProductDeleted: (facilityId: number) =>
    `/api/LogInteraction/TubeFeedProductDeleted/${facilityId}`,

  // Global Meal Pattern Group interaction log — the History ribbon group
  // on `ucDietech_MealPatternGlobalGroups.vb`. InteractionLogFormType 4
  // GlobalMealPatternGroup_DataGrid / 6 GlobalMealPatternGroup_Deleted.
  logInteractionGlobalMealPatternGroup: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/GlobalMealPatternGroup/${facilityId}/${pKey}`,
  logInteractionGlobalMealPatternGroupDeleted: (facilityId: number) =>
    `/api/LogInteraction/GlobalMealPatternGroupDeleted/${facilityId}`,

  // Global Adjustment Group interaction log — the History ribbon group
  // on `ucDietech_AdjustmentGlobalGroups.vb`. InteractionLogFormType 5
  // GlobalAdjustmentGroup_DataGrid / 7 GlobalAdjustmentGroup_Deleted.
  logInteractionGlobalAdjustmentGroup: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/GlobalAdjustmentGroup/${facilityId}/${pKey}`,
  logInteractionGlobalAdjustmentGroupDeleted: (facilityId: number) =>
    `/api/LogInteraction/GlobalAdjustmentGroupDeleted/${facilityId}`,

  // Dining Room interaction log — the History ribbon group on
  // `ucDietech_DiningRooms.vb`. InteractionLogFormType 63
  // DiningRoom_DataGrid / 64 DiningRoom_DataGrid_Deleted.
  logInteractionDiningRoom: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/DiningRoom/${facilityId}/${pKey}`,
  logInteractionDiningRoomDeleted: (facilityId: number) =>
    `/api/LogInteraction/DiningRoomDeleted/${facilityId}`,

  // Special Need interaction log — the History ribbon group on
  // `ucDietech_Special_Needs.vb`. `…SpecialNeed` is every row for one
  // need (InteractionLogFormType.SpecialNeeds_DataGrid = 38);
  // `…SpecialNeedDeleted` is facility-wide deletes
  // (SpecialNeeds_DataGrid_Delete = 39).
  logInteractionSpecialNeed: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/SpecialNeed/${facilityId}/${pKey}`,
  logInteractionSpecialNeedDeleted: (facilityId: number) =>
    `/api/LogInteraction/SpecialNeedDeleted/${facilityId}`,

  // Portion interaction log — the History ribbon group on
  // `ucDietech_Portions.vb`. One endpoint only: the tab offers
  // "Selected Item" (InteractionLogFormType.Portion_DataGrid = 60) and
  // no View Deleted, because Portions is a fixed list with no Delete.
  logInteractionPortion: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/Portion/${facilityId}/${pKey}`,

  // Item Type interaction log — the History ribbon group on
  // `ucDietech_ItemType.vb`. `…ItemType` is every row for one type
  // (WinForms InteractionLogFormType.ItemType_DataGrid = 33);
  // `…ItemTypeDeleted` is facility-wide deletes (ItemType_Deleted = 34).
  logInteractionItemType: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/ItemType/${facilityId}/${pKey}`,
  logInteractionItemTypeDeleted: (facilityId: number) =>
    `/api/LogInteraction/ItemTypeDeleted/${facilityId}`,

  // Food Allergy interaction log — the History ribbon group on
  // `ucDietech_Food_Allergies.vb`. `…FoodAllergy` is one allergy's whole
  // history (FormType 12 FoodAllergy_DataGrid); `…FoodAllergyDeleted` is
  // every Delete-type row across all allergies (FormType 13
  // FoodAllergy_DataGrid_Deleted) — a deletion history, not a recycle bin.
  logInteractionFoodAllergy: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/FoodAllergy/${facilityId}/${pKey}`,
  logInteractionFoodAllergyDeleted: (facilityId: number) =>
    `/api/LogInteraction/FoodAllergyDeleted/${facilityId}`,

  // DRI Group interaction log — History on the DRI Groups table.
  // FormType 61 DRIGroup_DataGrid / 62 DRIGroup_DataGrid_Deleted.
  logInteractionDRIGroup: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/DRIGroup/${facilityId}/${pKey}`,
  logInteractionDRIGroupDeleted: (facilityId: number) =>
    `/api/LogInteraction/DRIGroupDeleted/${facilityId}`,

  // Menu Cycle interaction log — one row per action taken on
  // this menu (delete, restore, add, modify, etc.). Drives the
  // Recycle Bin's "Selected Menu Cycle History" dialog.
  logInteractionMenuHeader: (facilityId: number, pKey: number) =>
    `/api/LogInteraction/MenuHeader/${facilityId}/${pKey}`,
  // Facility-wide deleted Menu Cycle history — drives Menu
  // Cycles page's History → View Deleted dialog.
  logInteractionMenuHeaderDeleted: (facilityId: number) =>
    `/api/LogInteraction/MenuHeaderDeleted/${facilityId}`,

  // AIM (Automatic Item Modification) — runs on patient diet
  // change. Backed by the WinForms `AIM_Diet_Update` flow.
  aimDietRun: (facilityId: number, patientId: number) =>
    `/api/AimDiet/Run/${facilityId}/${patientId}`,
  aimDietResolve: (facilityId: number, patientId: number) =>
    `/api/AimDiet/Resolve/${facilityId}/${patientId}`,

  // Selective Menu AIM — chains AFTER the MP/Adj AIM leg on a
  // diet-order tuple change. Backed by `GenerateSMChanges` /
  // `SaveSMChanges`.
  aimSelectiveMenuRun: (facilityId: number, patientId: number) =>
    `/api/AimSelectiveMenu/Run/${facilityId}/${patientId}`,
  aimSelectiveMenuResolve: (facilityId: number, patientId: number) =>
    `/api/AimSelectiveMenu/Resolve/${facilityId}/${patientId}`,
  aimSelectiveMenuRevertOld: (facilityId: number, patientId: number) =>
    `/api/AimSelectiveMenu/RevertOldValues/${facilityId}/${patientId}`,
  /** Dashboard "Auto Generated Selective Menus" viewer — replays
   *  an already-applied SaveSMChanges call (identified by GUID)
   *  and returns the same 4-table shape as Run so the same
   *  dialog renders it. */
  aimSelectiveMenuHistorical: (facilityId: number, patientId: number, guid: string) =>
    `/api/AimSelectiveMenu/Historical/${facilityId}/${patientId}/${encodeURIComponent(guid)}`,
  /** Dashboard save-back — user clicked Save from the historical
   *  viewer. Server rewrites only UNLOCKED mealtimes and writes
   *  a new tblPrd_Selective_Menu_AutoProcessed row + SM log +
   *  interaction log per rewritten mealtime. */
  aimSelectiveMenuResolveDashboard: (facilityId: number, patientId: number) =>
    `/api/AimSelectiveMenu/ResolveDashboard/${facilityId}/${patientId}`,
  /** Resident Selective Menu tab "Populate Current Selections" -
   *  fills every empty day/mealtime of the current menu cycle from
   *  the resident's most recent past selections (WinForms
   *  `btnStaticRepopulate_Click`). */
  residentSelectiveMenuPopulate: (facilityId: number, patientId: number) =>
    `/api/ResidentSelectiveMenu/PopulateFromHistory/${facilityId}/${patientId}`,
  /** Selective Menu History for a date (yyyy-MM-dd) and mealtime (WinForms
   *  `frmDietech_SelectiveMenu_History_DataGrid`). */
  residentSelectiveMenuHistory: (facilityId: number, patientId: number, menuDate: string, mealtime: number) =>
    `/api/ResidentSelectiveMenu/History/${facilityId}/${patientId}?menuDate=${encodeURIComponent(menuDate)}&mealtime=${mealtime}`,
  /** Regenerate Menu (Using History): clears the meal's saved choices for the
   *  date so the menu regenerates (WinForms `btnRegenerate_UsingHistory_ItemClick`). */
  residentSelectiveMenuRegenerate: (facilityId: number, patientId: number) =>
    `/api/ResidentSelectiveMenu/Regenerate/${facilityId}/${patientId}`,

  // Diet Flucs - Patient History
  dietFlucsPatientHistory: (facilityId: number, patientId: number) =>
    `/api/dietflucs/GetPatientHistory/${facilityId}/${patientId}`,

  // DRI Group / Nutrients
  defaultNutrients: (facilityId: number, driGroupId: number) =>
    `/api/DRIGroup/DefaultNutrients/${facilityId}/${driGroupId}`,
  /** GET loads / POST saves the facility's nutrient targets by DRI group */
  nutrientTargets: (facilityId: number) =>
    `/api/DRIGroup/NutrientTargets/${facilityId}`,

  // Nutritional Validation
  /**
   * Items + sizes + the menu cycle in use, in one call. WinForms never asks
   * which cycle (GetMenuCycleInUseInfo resolves it), so `menuHeaderId` stands
   * in for `g_FKey_Menu_Hdr_Override` only; pass 0 for the normal case.
   */
  nutritionalValidation: (facilityId: number, menuHeaderId = 0) =>
    `/api/NutritionalValidation/${facilityId}${menuHeaderId > 0 ? `?FKey_Menu_Header=${menuHeaderId}` : ''}`,
  nutritionalValidationResetOverride: (facilityId: number, itemId: number) =>
    `/api/NutritionalValidation/ResetOverride/${facilityId}/${itemId}`,
  nutritionalValidationMissingData: (facilityId: number) =>
    `/api/NutritionalValidation/MissingData/${facilityId}`,
  nutritionalValidationMakeRecipe: (facilityId: number) =>
    `/api/NutritionalValidation/MakeRecipe/${facilityId}`,

  // Facility Unit Print Schedule
  facilityUnitPrintSchedules: (facilityId: number) =>
    `/api/FacilityUnitPrintSchedule/${facilityId}`,

  // Employee
  employeeGetByPKey: (facilityId: number, pKeyEmployee: number) =>
    `/api/Employee/${facilityId}/${pKeyEmployee}`,
  employeeGetById: (facilityId: number, employeeId: string) =>
    `/api/Employee/ByEmployeeId/${facilityId}/${employeeId}`,
  employeeIdExists: (facilityId: number, id: string) =>
    `/api/Employee/IdExists/${facilityId}/${id}`,
  employeeAdd: (facilityId: number) =>
    `/api/Employee/Add/${facilityId}`,

  // EMR / EHR
  emrPending: (facilityId: number) =>
    `/api/EMR/GetPending/${facilityId}`,
  emrHistory: (facilityId: number, days?: number) =>
    `/api/EMR/GetHistory/${facilityId}${days ? `?days=${days}` : ''}`,
  emrHistoryCounts: (facilityId: number) =>
    `/api/EMR/HistoryCounts/${facilityId}`,
  emrHistoryByResident: (facilityId: number, linkedId: string) =>
    `/api/EMR/GetHistory/${facilityId}/${encodeURIComponent(linkedId)}`,
  emrClock: (facilityId: number) =>
    `/api/EMR/Clock/${facilityId}`,
  ehrUnitMappings: (facilityId: number) =>
    `/api/EhrSettings/GetUnitMappings/${facilityId}`,

  // EMR message actions
  emrDelete: (facilityId: number, pendingId: number) =>
    `/api/EMR/Delete/${facilityId}/${pendingId}`,
  emrLock: (facilityId: number, pendingId: number, userId: number) =>
    `/api/EMR/Lock/${facilityId}/${pendingId}/${userId}`,
  emrUnlock: (facilityId: number, pendingId: number) =>
    `/api/EMR/Unlock/${facilityId}/${pendingId}`,
  emrReprocess: (facilityId: number, pendingId: number) =>
    `/api/EMR/Reprocess/${facilityId}/${pendingId}`,
  emrReprocessLast: (facilityId: number, linkedId: string) =>
    `/api/EMR/ReprocessLast/${facilityId}?linkedId=${encodeURIComponent(linkedId)}`,
  emrReprocessAllLastMessages: (facilityId: number) =>
    `/api/EMR/ReprocessAllLastMessages/${facilityId}`,
  emrMessageContents: (facilityId: number, pendingId: number) =>
    `/api/EMR/MessageContents/${facilityId}/${pendingId}`,
  emrAddUnitMapping: (facilityId: number) =>
    `/api/EMR/UnitMapping/${facilityId}`,
  emrDietOrderMappingContext: (facilityId: number, pendingId: number) =>
    `/api/EMR/DietOrderMapping/${facilityId}/${pendingId}`,
  emrDietOrderTargetLines: (facilityId: number, pendingId: number) =>
    `/api/EMR/DietOrderMapping/TargetLines/${facilityId}/${pendingId}`,
  emrSaveDietOrderMapping: (facilityId: number) =>
    `/api/EMR/DietOrderMapping/${facilityId}`,
  emrAllergyMappingContext: (facilityId: number, pendingId: number) =>
    `/api/EMR/AllergyMapping/${facilityId}/${pendingId}`,
  emrSaveAllergyMapping: (facilityId: number) =>
    `/api/EMR/AllergyMapping/${facilityId}`,
  emrClearMappingError: (facilityId: number, pendingId: number) =>
    `/api/EMR/ClearMappingError/${facilityId}/${pendingId}`,
  emrSetLinkedId: (facilityId: number, pendingId: number) =>
    `/api/EMR/SetLinkedId/${facilityId}/${pendingId}`,
  emrSetMessageType: (facilityId: number, pendingId: number, messageTypeId: number) =>
    `/api/EMR/SetMessageType/${facilityId}/${pendingId}/${messageTypeId}`,

  // ===== RESIDENT EHR HISTORY =====
  // One resident's EHR history (active visit number, other feeds) and Switch Linked ID.
  emrResidentHistory: (facilityId: number, patientId: number) =>
    `/api/EMR/ResidentHistory/${facilityId}/${patientId}`,
  emrSwitchLinkedId: (facilityId: number, patientId: number) =>
    `/api/EMR/SwitchLinkedId/${facilityId}/${patientId}`,
  // ===== END RESIDENT EHR HISTORY =====

  // ===== EHR MAPPING MAINTENANCE =====
  // EhrMappingsController - the Mappings tab on /emr.
  ehrMapLookups: (facilityId: number) => `/api/EhrMappings/Lookups/${facilityId}`,
  ehrMapList: (facilityId: number) => `/api/EhrMappings/${facilityId}`,
  ehrMapAddMod: (facilityId: number) => `/api/EhrMappings/AddMod/${facilityId}`,
  ehrMapDelete: (facilityId: number, id: number) => `/api/EhrMappings/${facilityId}/${id}`,
  ehrMapHistory: (facilityId: number, id: number) => `/api/EhrMappings/History/${facilityId}/${id}`,
  ehrMapDeleted: (facilityId: number) => `/api/EhrMappings/Deleted/${facilityId}`,
  ehrMapExclusions: (facilityId: number) => `/api/EhrMappings/Exclusions/${facilityId}`,
  ehrMapExclusionAddMod: (facilityId: number) => `/api/EhrMappings/Exclusions/AddMod/${facilityId}`,
  ehrMapExclusionDelete: (facilityId: number, id: number) => `/api/EhrMappings/Exclusions/${facilityId}/${id}`,
  ehrMapExclusionHistory: (facilityId: number, id: number) => `/api/EhrMappings/Exclusions/History/${facilityId}/${id}`,
  ehrMapExclusionDeleted: (facilityId: number) => `/api/EhrMappings/Exclusions/Deleted/${facilityId}`,
  ehrMapUnits: (facilityId: number) => `/api/EhrMappings/Units/${facilityId}`,
  ehrMapUnitAddMod: (facilityId: number) => `/api/EhrMappings/Units/AddMod/${facilityId}`,
  ehrMapUnitDelete: (facilityId: number, id: number) => `/api/EhrMappings/Units/${facilityId}/${id}`,
  ehrMapUnitHistory: (facilityId: number, id: number) => `/api/EhrMappings/Units/History/${facilityId}/${id}`,
  ehrMapUnitDeleted: (facilityId: number) => `/api/EhrMappings/Units/Deleted/${facilityId}`,
  ehrMapRoomBeds: (facilityId: number) => `/api/EhrMappings/RoomBeds/${facilityId}`,
  ehrMapRoomBedAddMod: (facilityId: number) => `/api/EhrMappings/RoomBeds/AddMod/${facilityId}`,
  ehrMapRoomBedDelete: (facilityId: number, id: number) => `/api/EhrMappings/RoomBeds/${facilityId}/${id}`,
  ehrMapRoomBedHistory: (facilityId: number, id: number) => `/api/EhrMappings/RoomBeds/History/${facilityId}/${id}`,
  ehrMapRoomBedDeleted: (facilityId: number) => `/api/EhrMappings/RoomBeds/Deleted/${facilityId}`,
  ehrMapAllergyResidentHistory: (facilityId: number, value: string) =>
    `/api/EMR/GetAllergyMappingHistory/${facilityId}?name=${encodeURIComponent(value)}`,
  // EHR targets (phase 4): what mappings give, the frequency table, the switches (EhrMappingsController)
  ehrMapTargetLookups: (facilityId: number) => `/api/EhrMappings/TargetLookups/${facilityId}`,
  ehrMapSwitches: (facilityId: number) => `/api/EhrMappings/Switches/${facilityId}`,
  ehrMapFrequencies: (facilityId: number) => `/api/EhrMappings/Frequencies/${facilityId}`,
  ehrMapMatchPreview: (facilityId: number) => `/api/EhrMappings/MatchPreview/${facilityId}`,
  // EHR > Added Items (EhrAttachmentReviewController)
  ehrAddedItemsList: (facilityId: number, reviewed: boolean) => `/api/EhrAttachmentReview/${facilityId}?reviewed=${reviewed}`,
  ehrAddedItemsCount: (facilityId: number) => `/api/EhrAttachmentReview/Count/${facilityId}`,
  ehrAddedItemsMark: (facilityId: number) => `/api/EhrAttachmentReview/Review/${facilityId}`,
  // "Map this" pop-up and mapping health (EhrMapThisController)
  ehrMapThisQueue: (facilityId: number) => `/api/EhrMapThis/Queue/${facilityId}`,
  ehrMapThisResidents: (facilityId: number, text: string, part: number) =>
    `/api/EhrMapThis/Residents/${facilityId}?text=${encodeURIComponent(text)}&part=${part}`,
  ehrMapThisPreview: (facilityId: number) => `/api/EhrMapThis/Preview/${facilityId}`,
  ehrMapThisSave: (facilityId: number) => `/api/EhrMapThis/Save/${facilityId}`,
  ehrMapThisHealth: (facilityId: number) => `/api/EhrMapThis/Health/${facilityId}`,
  /** The "waiting for mapping" badge count. */
  ehrMapThisCount: (facilityId: number) => `/api/EhrMapThis/Count/${facilityId}`,
  /** Apply saved mappings to current residents: preview, then apply on confirm. */
  ehrMapThisApplyPreview: (facilityId: number) => `/api/EhrMapThis/ApplyPreview/${facilityId}`,
  ehrMapThisApply: (facilityId: number) => `/api/EhrMapThis/Apply/${facilityId}`,
  // EHR > Allergy Changes (EhrAllergyReviewController)
  ehrAllergyReviewList: (facilityId: number, reviewed: boolean) =>
    `/api/EhrAllergyReview/${facilityId}?reviewed=${reviewed}`,
  ehrAllergyReviewCount: (facilityId: number) => `/api/EhrAllergyReview/Count/${facilityId}`,
  ehrAllergyReviewMark: (facilityId: number) => `/api/EhrAllergyReview/Review/${facilityId}`,
  // ===== END EHR MAPPING MAINTENANCE =====

  // Getting Started (GettingStartedController): the setup wizard, behind GettingStarted:Enabled.
  gettingStarted: (facilityId: number) => `/api/GettingStarted/${facilityId}`,
  gettingStartedAnswers: (facilityId: number, stepKey: string) =>
    `/api/GettingStarted/${facilityId}/Answers/${encodeURIComponent(stepKey)}`,
  gettingStartedStatus: (facilityId: number, stepKey: string) =>
    `/api/GettingStarted/${facilityId}/Status/${encodeURIComponent(stepKey)}`,
  /** Facility basics (B1): the basics columns of tblFacility; PSI staff may save it below Facility level 2. */
  gettingStartedBasics: (facilityId: number) => `/api/GettingStarted/${facilityId}/Basics`,
  /** Printing and reports (B9): the changed print option columns only (GettingStartedController.PrintingFields). */
  gettingStartedPrinting: (facilityId: number) => `/api/GettingStarted/${facilityId}/Printing`,
  /** Printing and reports (B9): the printer for one report type (a Print Agent routing rule); PSI staff may set it too. */
  gettingStartedPrinter: (facilityId: number) => `/api/GettingStarted/${facilityId}/Printer`,
  /** Recompute every step and send all statuses to Tech Log. */
  gettingStartedSync: (facilityId: number) => `/api/GettingStarted/${facilityId}/Sync`,
  /** Selective Touch (B10): screen checks, servers, tablets, printing and the test order. */
  gettingStartedTouch: (facilityId: number) => `/api/GettingStarted/${facilityId}/Touch`,
  gettingStartedTouchSettings: (facilityId: number) => `/api/GettingStarted/${facilityId}/Touch/Settings`,
  /** The wizard's own test resident: POST creates, DELETE removes it and its orders. */
  gettingStartedTouchTestResident: (facilityId: number) => `/api/GettingStarted/${facilityId}/Touch/TestResident`,
  gettingStartedTouchTestConfirm: (facilityId: number) => `/api/GettingStarted/${facilityId}/Touch/TestResident/Confirm`,

  // Allergy and food safety check (AllergyProtectionController): Getting Started B4 and Tables > Food Allergies.
  allergyProtection: (facilityId: number) => `/api/AllergyProtection/${facilityId}`,
  allergyProtectionProof: (facilityId: number, allergyId: number) => `/api/AllergyProtection/${facilityId}/Proof/${allergyId}`,
  /** One-click repair: link an allergy to an existing never-serve adjustment the check suggests. */
  allergyProtectionLink: (facilityId: number) => `/api/AllergyProtection/${facilityId}/Link`,
  /** Bulk repair: add an allergy's adjustment for every current resident missing it (count confirmed first). */
  allergyProtectionAddToResidents: (facilityId: number) => `/api/AllergyProtection/${facilityId}/AddToResidents`,
  /** Self-order and bedside: block allergen items (tblFacility.SelfOrder_BlockAllergens only). */
  allergyProtectionBlock: (facilityId: number) => `/api/AllergyProtection/${facilityId}/BlockAllergens`,

  // Tray line auto setup (TrayLineController.AutoSetup): Getting Started B2 and Auto setup on Tray Line Setup.
  trayLineAutoSetup: (facilityId: number) => `/api/TrayLine/AutoSetup/${facilityId}`,
  trayLineAutoSetupPreview: (facilityId: number) => `/api/TrayLine/AutoSetup/Preview/${facilityId}`,
  trayLineAutoSetupApply: (facilityId: number) => `/api/TrayLine/AutoSetup/Apply/${facilityId}`,
  trayLineAutoSetupUndo: (facilityId: number) => `/api/TrayLine/AutoSetup/Undo/${facilityId}`,

  // Facility Save
  facilitySave: (facilityId: number) =>
    `/api/facility/Save/${facilityId}`,
  /** tblFacility.Logo as base64 PNG (GET) / set or clear it (PUT { logo }). */
  facilityLogo: (facilityId: number) => `/api/facility/Logo/${facilityId}`,
  facilityTTNotes: (facilityId: number) =>
    `/api/facility/TTNotes/${facilityId}`,

  // Production Menu (PrdMenu)
  prdMenuDetail: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/Detail/${facilityId}/${menuHeaderId}`,
  prdMenuDetailByDay: (facilityId: number, menuHeaderId: number, day: number, mealtime: number) =>
    `/api/PrdMenu/DetailByDay/${facilityId}/${menuHeaderId}/${day}/${mealtime}`,
  prdMenuDetailWithDisplay: (facilityId: number, menuHeaderId: number, day: number, mealtime: number) =>
    `/api/PrdMenu/DetailWithDisplay/${facilityId}/${menuHeaderId}/${day}/${mealtime}`,
  prdMenuAllDetailWithDisplay: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/AllDetailWithDisplay/${facilityId}/${menuHeaderId}`,
  prdMenuAddAtPosition: (facilityId: number) =>
    `/api/PrdMenu/AddAtPosition/${facilityId}`,
  prdMenuBump: (facilityId: number) =>
    `/api/PrdMenu/Bump/${facilityId}`,
  prdMenuCopy: (facilityId: number) =>
    `/api/PrdMenu/Copy/${facilityId}`,
  prdMenuSaveDetail: (facilityId: number) =>
    `/api/PrdMenu/SaveDetail/${facilityId}`,
  prdMenuSaveDetails: (facilityId: number) =>
    `/api/PrdMenu/SaveDetails/${facilityId}`,
  prdMenuDeleteDetail: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/Detail/${facilityId}/${pKey}`,
  prdMenuDietFilter: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/DietFilter/${facilityId}/${menuHeaderId}`,
  prdMenuSaveDietFilter: (facilityId: number) =>
    `/api/PrdMenu/SaveDietFilter/${facilityId}`,
  prdMenuDietFilterWithNames: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/DietFilterWithNames/${facilityId}/${menuHeaderId}`,
  // Menu Cycle AddMod — Diet Types / Consistencies tab (React
  // slice AM-7). Master lookups per facility + full-diff save
  // cascade including tblPrd_Menu_Detail_DF seeding.
  prdMenuDietTypeMasterList: (facilityId: number) =>
    `/api/PrdMenu/DietTypeMasterList/${facilityId}`,
  prdMenuConsistMasterList: (facilityId: number) =>
    `/api/PrdMenu/ConsistMasterList/${facilityId}`,
  prdMenuSaveDietTypeConsist: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/SaveDietTypeConsist/${facilityId}/${menuHeaderId}`,
  prdMenuExtensionExcel: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/ExtensionExcel/${facilityId}/${menuHeaderId}`,
  prdMenuSaveExtensionExcel: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/SaveExtensionExcel/${facilityId}/${menuHeaderId}`,
  prdMenuFacilityPermissions: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/FacilityPermissions/${facilityId}/${menuHeaderId}`,
  prdMenuSaveFacilityPermissions: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/SaveFacilityPermissions/${facilityId}/${menuHeaderId}`,
  prdMenuFacilityDiningRooms: (facilityId: number) =>
    `/api/PrdMenu/FacilityDiningRooms/${facilityId}`,
  prdMenuDiningRoomAssignments: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/DiningRoomAssignments/${facilityId}/${menuHeaderId}`,
  prdMenuSaveDiningRoomAssignments: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/SaveDiningRoomAssignments/${facilityId}/${menuHeaderId}`,
  prdMenuFacilityPrinters: (facilityId: number) =>
    `/api/PrdMenu/FacilityPrinters/${facilityId}`,
  // Holiday/Override Menu grid load (HD-1). Distinct route from the
  // older `prdMenuHolidayMenus` helper below — that one returns a
  // 7-column stub view model; this one returns the full grid DTO
  // with server-computed NextDate. Route: HolidayMenusForParent.
  prdMenuHolidayMenusForParent: (facilityId: number, parentPKey: number) =>
    `/api/PrdMenu/HolidayMenusForParent/${facilityId}/${parentPKey}`,
  // HD-3 AddMod dialog endpoints — see api/menuCycleApi.ts for the
  // matching hooks.
  prdMenuHolidayMenuMetadata: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/HolidayMenuMetadata/${facilityId}/${pKey}`,
  prdMenuHolidayMenuSMInfo: (facilityId: number, parentPKey: number, mealtime: number) =>
    `/api/PrdMenu/HolidayMenuSMInfo/${facilityId}/${parentPKey}/${mealtime}`,
  prdMenuSaveHolidayMenu: (facilityId: number) =>
    `/api/PrdMenu/SaveHolidayMenu/${facilityId}`,
  /** Toolbar Set Active → Select/Unselect toggle for a holiday-menu
   *  detail row (WinForms `SelectHolidayMenu` at
   *  frmDietech_Menu_Cycle_DataGrid.vb:2644-2839). Server flips the
   *  row's Active flag and cascades to sibling holidays per MenuType. */
  prdMenuSelectHolidayMenu: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/SelectHolidayMenu/${facilityId}/${pKey}`,
  prdMenuPrinterAssignments: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/PrinterAssignments/${facilityId}/${menuHeaderId}`,
  prdMenuSavePrinterAssignments: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/SavePrinterAssignments/${facilityId}/${menuHeaderId}`,
  prdMenuDetailDietFilter: (facilityId: number, menuHeaderId: number, day: number, mealtime: number) =>
    `/api/PrdMenu/DetailDietFilter/${facilityId}/${menuHeaderId}/${day}/${mealtime}`,
  prdMenuSaveDetailDietFilter: (facilityId: number) =>
    `/api/PrdMenu/SaveDetailDietFilter/${facilityId}`,
  prdMenuDfOverrideChildren: (facilityId: number, dfId: number) =>
    `/api/PrdMenu/DfOverrideChildren/${facilityId}/${dfId}`,
  prdMenuSaveDfOverrideChild: (facilityId: number) =>
    `/api/PrdMenu/DfOverrideChild/${facilityId}`,
  prdMenuDeleteDfOverrideChild: (facilityId: number, id: number) =>
    `/api/PrdMenu/DfOverrideChild/${facilityId}/${id}`,
  prdMenuSwap: (facilityId: number) =>
    `/api/PrdMenu/Swap/${facilityId}`,
  prdMenuFind: (facilityId: number) =>
    `/api/PrdMenu/Find/${facilityId}`,
  // Menu Cycle Lock — real port lives at /api/MenuCycleLock/*
  // (see menuCycleLockAcquire etc.). The old /PrdMenu/Lock/*
  // endpoints were built against a stale table shape and are
  // removed 2026-09-14.
  menuCycleLockAcquire: () => `/api/MenuCycleLock/Acquire`,
  menuCycleLockHeartbeat: (pKey: number) =>
    `/api/MenuCycleLock/Heartbeat/${pKey}`,
  menuCycleLockRelease: (pKey: number) =>
    `/api/MenuCycleLock/Release/${pKey}`,
  menuCycleLockForce: (menuHeaderId: number) =>
    `/api/MenuCycleLock/Force/${menuHeaderId}`,
  menuCycleLockIsOpen: (menuHeaderId: number, includeHolidayMenus: boolean = false) =>
    `/api/MenuCycleLock/IsOpen/${menuHeaderId}?includeHolidayMenus=${includeHolidayMenus}`,
  prdMenuBumpInOut: (facilityId: number) =>
    `/api/PrdMenu/BumpInOut/${facilityId}`,
  prdMenuSuperCopy: (facilityId: number) =>
    `/api/PrdMenu/SuperCopy/${facilityId}`,
  prdMenuBulkDelete: (facilityId: number) =>
    `/api/PrdMenu/BulkDelete/${facilityId}`,
  prdMenuCopyMealtime: (facilityId: number) =>
    `/api/PrdMenu/CopyMealtime/${facilityId}`,
  prdMenuGetNote: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/Note/${facilityId}/${pKey}`,
  prdMenuSaveNote: (facilityId: number) =>
    `/api/PrdMenu/Note/${facilityId}`,
  prdMenuCreateSync: (facilityId: number) =>
    `/api/PrdMenu/Sync/${facilityId}`,
  prdMenuBreakSync: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/BreakSync/${facilityId}/${pKey}`,
  prdMenuSyncedItems: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/SyncedItems/${facilityId}/${pKey}`,

  // Always Served Active Days
  prdMenuAlwaysServedActive: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/AlwaysServedActive/${facilityId}/${menuHeaderId}`,
  prdMenuAlwaysServedActiveByDetail: (facilityId: number, detailId: number, mealtime: number) =>
    `/api/PrdMenu/AlwaysServedActive/${facilityId}/${detailId}/${mealtime}`,
  prdMenuAlwaysServedActiveSave: (facilityId: number) =>
    `/api/PrdMenu/AlwaysServedActive/Save/${facilityId}`,

  // Holiday Menus
  prdMenuHolidayMenus: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/HolidayMenus/${facilityId}/${menuHeaderId}`,

  // Custom Paths
  prdMenuCustomPaths: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/CustomPaths/${facilityId}/${menuHeaderId}`,
  prdMenuCustomPathsByMaster: (facilityId: number, masterPKey: number) =>
    `/api/PrdMenu/CustomPathsByMaster/${facilityId}/${masterPKey}`,
  prdMenuCustomPathAdd: (facilityId: number) =>
    `/api/PrdMenu/CustomPath/Add/${facilityId}`,
  prdMenuCustomPathDelete: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/CustomPath/${facilityId}/${pKey}`,

  // Link / Break Link
  prdMenuLink: (facilityId: number) =>
    `/api/PrdMenu/Link/${facilityId}`,
  prdMenuBreakLink: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/BreakLink/${facilityId}/${pKey}`,
  prdMenuLinkedItems: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/LinkedItems/${facilityId}/${pKey}`,

  // Garnishes + Modifier Headers
  prdMenuGarnishes: (facilityId: number) =>
    `/api/PrdMenu/Garnishes/${facilityId}`,
  prdMenuModifierHeaders: (facilityId: number) =>
    `/api/PrdMenu/ModifierHeaders/${facilityId}`,

  // Maintenance Utilities
  prdMenuRebuildLevels: (facilityId: number) =>
    `/api/PrdMenu/RebuildLevels/${facilityId}`,
  prdMenuFixBrokenLinks: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/FixBrokenLinks/${facilityId}/${menuHeaderId}`,
  prdMenuRebuildCustomPaths: (facilityId: number, menuHeaderId: number) =>
    `/api/PrdMenu/RebuildCustomPaths/${facilityId}/${menuHeaderId}`,

  // Recipe Composition + MC Fill + MC Organize + Copy Categories
  prdMenuRecipeComposition: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/RecipeComposition/${facilityId}/${pKey}`,
  prdMenuMCFill: (facilityId: number) =>
    `/api/PrdMenu/MCFill/${facilityId}`,
  prdMenuMCOrganize: (facilityId: number) =>
    `/api/PrdMenu/MCOrganize/${facilityId}`,
  prdMenuCopyMenuCategories: (facilityId: number) =>
    `/api/PrdMenu/CopyMenuCategories/${facilityId}`,

  // AIM + Plate Color
  prdMenuAimCount: (facilityId: number, pKey: number) =>
    `/api/PrdMenu/AimCount/${facilityId}/${pKey}`,
  prdMenuPlateColorAnalysis: (facilityId: number, menuHeaderId: number, day: number, mealtime: number) =>
    `/api/PrdMenu/PlateColorAnalysis/${facilityId}/${menuHeaderId}/${day}/${mealtime}`,

  // Workflow
  workflowTemplates: (facilityId: number) =>
    `/api/workflow/Templates/${facilityId}`,
  workflowTemplateById: (facilityId: number, templateId: number) =>
    `/api/workflow/Templates/${facilityId}/${templateId}`,
  workflowTemplateAddMod: (facilityId: number) =>
    `/api/workflow/Templates/AddMod/${facilityId}`,
  workflowTemplateDelete: (facilityId: number, templateId: number) =>
    `/api/workflow/Templates/${facilityId}/${templateId}`,
  workflowSteps: (facilityId: number, templateId: number) =>
    `/api/workflow/Steps/${facilityId}/${templateId}`,
  workflowStepsSaveAll: (facilityId: number, templateId: number) =>
    `/api/workflow/Steps/SaveAll/${facilityId}/${templateId}`,
  workflowToday: (facilityId: number) =>
    `/api/workflow/Today/${facilityId}`,
  workflowStartInstance: (facilityId: number) =>
    `/api/workflow/StartInstance/${facilityId}`,
  workflowInstanceDetail: (facilityId: number, instanceId: number) =>
    `/api/workflow/Instance/${facilityId}/${instanceId}`,
  workflowCompleteStep: (facilityId: number) =>
    `/api/workflow/Complete/${facilityId}`,
  workflowUncompleteStep: (facilityId: number, instanceId: number, stepId: number) =>
    `/api/workflow/Uncomplete/${facilityId}/${instanceId}/${stepId}`,
  workflowHistory: (facilityId: number) =>
    `/api/workflow/History/${facilityId}`,

  // Weight
  weightSettings: (facilityId: number) =>
    `/api/weight/GetWeightSettings/${facilityId}`,
  weightEntryGrid: (facilityId: number) =>
    `/api/weight/GetWeightEntryGrid/${facilityId}`,
  patientWeightHistory: (facilityId: number, patientId: number) =>
    `/api/weight/GetPatientWeightHistory/${facilityId}/${patientId}`,
  patientWeightTriggers: (facilityId: number, patientId: number) =>
    `/api/weight/GetPatientWeightTriggers/${facilityId}/${patientId}`,
  weightAdd: (facilityId: number) =>
    `/api/weight/AddWeight/${facilityId}`,
  weightUpdate: (facilityId: number) =>
    `/api/weight/UpdateWeight/${facilityId}`,
  weightDelete: (facilityId: number, weightId: number) =>
    `/api/weight/DeleteWeight/${facilityId}/${weightId}`,
  weightBatchSave: (facilityId: number) =>
    `/api/weight/BatchSaveChanges/${facilityId}`,
  weightUpdatePatient: (facilityId: number, patientId: number) =>
    `/api/weight/UpdatePatientWeight/${facilityId}/${patientId}`,

  // TrayLine / Facility Sets
  facilitySets: (facilityId: number) =>
    `/api/TrayLine/GetFacilitySets/${facilityId}`,
  facilitySetsByMealtime: (facilityId: number, mealtime: number) =>
    `/api/TrayLine/GetFacilitySets/${facilityId}/${mealtime}`,
  setResidentCounts: (facilityId: number, date: string) =>
    `/api/TrayLine/GetSetResidentCounts/${facilityId}?date=${encodeURIComponent(date)}`,
  trayLineRoomBed: (facilityId: number) =>
    `/api/TrayLine/GetRoomBedTrayLine/${facilityId}`,
  /** The Room/Bed tab's whole working table, after WinForms' load rules.
   *  `log: false` is the Refresh button, which goes nowhere near `Init`
   *  and so writes no View log. */
  trayLineRoomBedWorkingSet: (facilityId: number, log: boolean = true) =>
    `/api/TrayLine/RoomBed/${facilityId}?log=${log}`,
  /** The Save button — the changed rows, in one transaction (see trayLineRoomBedSave). */
  // ── Bulk Orders (ucDietech_Prd_BulkOrders.vb) ──
  /** The master/detail grid. */
  bulkOrders: (facilityId: number) => `/api/BulkOrder/${facilityId}`,
  /** New — GENERATES a bulk guest per menu choice per bulk resident. */
  bulkOrderCreate: (facilityId: number) => `/api/BulkOrder/Create/${facilityId}`,
  /** Delete a whole order and every guest it generated. */
  bulkOrderDelete: (facilityId: number, id: number) =>
    `/api/BulkOrder/${facilityId}/${id}`,
  /** Delete one choice and its guest. */
  bulkOrderDeleteDetail: (facilityId: number, patientId: number) =>
    `/api/BulkOrder/Detail/${facilityId}/${patientId}`,
  /** Qty — the grid's only editable cell. */
  bulkOrderQty: (facilityId: number, patientId: number, qty: number) =>
    `/api/BulkOrder/Qty/${facilityId}/${patientId}/${qty}`,
  /** Modify: the choice's menu for the order's date and mealtime (Selective Menu Entry's load). */
  bulkOrderMenu: (facilityId: number, orderId: number, patientId: number) =>
    `/api/BulkOrder/Menu/${facilityId}/${orderId}/${patientId}`,
  /** Modify, an item picked on a row: sizes, adjustment warning and Auto Item Modification. */
  bulkOrderMenuItem: (facilityId: number, orderId: number, patientId: number, itemId: number) =>
    `/api/BulkOrder/Menu/Item/${facilityId}/${orderId}/${patientId}/${itemId}`,
  /** Modify's Save: Selective Menu Entry's save, plus the bulk order rows, in one transaction. */
  bulkOrderMenuSave: (facilityId: number, orderId: number, patientId: number) =>
    `/api/BulkOrder/Menu/Save/${facilityId}/${orderId}/${patientId}`,

  // ── Forecasting editor (ucDietech_Prd_Forecasting.vb) ──
  /** Both grids and the editors' lookups. */
  forecastingEditor: (facilityId: number) => `/api/ForecastingEditor/${facilityId}`,
  /** The Size cell's list for one item. */
  forecastingEditorSizes: (facilityId: number, itemId: number) =>
    `/api/ForecastingEditor/Sizes/${facilityId}/${itemId}`,
  /** The New dialog's menu cycle panel for a start date (yyyy-MM-dd). */
  forecastingEditorMenuInfo: (facilityId: number, date: string) =>
    `/api/ForecastingEditor/MenuInfo/${facilityId}?date=${encodeURIComponent(date)}`,
  /** New: builds the forecast from the menu for each day and mealtime. */
  forecastingEditorCreate: (facilityId: number) => `/api/ForecastingEditor/Create/${facilityId}`,
  /** Save: every change, one transaction. */
  forecastingEditorSave: (facilityId: number) => `/api/ForecastingEditor/Save/${facilityId}`,

  trayLineRoomBedSave: (facilityId: number) =>
    `/api/TrayLine/RoomBed/Save/${facilityId}`,
  /** The Resident Assignment tab's working set. `log: false` on a refresh. */
  trayLineResidentWorkingSet: (facilityId: number, log: boolean = true) =>
    `/api/TrayLine/Residents/${facilityId}?log=${log}`,
  /** Its Save — edited rows plus the change log. */
  trayLineResidentSave: (facilityId: number) =>
    `/api/TrayLine/Residents/Save/${facilityId}`,
  trayLinePatients: (facilityId: number) =>
    `/api/TrayLine/GetPatientsForTrayLine/${facilityId}`,
  trayLineOverrides: (facilityId: number) =>
    `/api/TrayLine/GetTrayLineOverrides/${facilityId}`,
  trayLineLocations: (facilityId: number) =>
    `/api/TrayLine/GetTrayLineLocations/${facilityId}`,
  trayLineSavePatientAssignments: (facilityId: number) =>
    `/api/TrayLine/SavePatientTrayLineAssignments/${facilityId}`,
  trayLinePatientAssignment: (facilityId: number, patientId: number) =>
    `/api/TrayLine/GetPatientsForTrayLine/${facilityId}?patientId=${patientId}`,
  trayLineSaveSinglePatient: (facilityId: number) =>
    `/api/TrayLine/SavePatientTrayLineAssignments/${facilityId}`,

  // Tray Ticket Visualizer
  trayTicketData: (facilityId: number) =>
    `/api/TrayTicket/${facilityId}`,
  trayTicketMenuCycle: (facilityId: number) =>
    `/api/TrayTicket/MenuCycle/${facilityId}`,
  trayTicketPatientList: (facilityId: number) =>
    `/api/TrayTicket/PatientList/${facilityId}`,
  trayTicketPatientImage: (facilityId: number, patientId: number) =>
    `/api/TrayTicket/PatientImage/${facilityId}/${patientId}`,
  trayTicketDisplayOptions: (facilityId: number) =>
    `/api/TrayTicket/DisplayOptions/${facilityId}`,
  trayTicketGuestSchedule: (facilityId: number) =>
    `/api/TrayTicket/GuestSchedule/${facilityId}`,
  trayTicketExplainer: (facilityId: number) =>
    `/api/TrayTicket/Explainer/${facilityId}`,
  trayTicketExplainerRegenerate: (facilityId: number) =>
    `/api/TrayTicket/Explainer/Regenerate/${facilityId}`,

  // Reference Data - CRUD Endpoints
  // Diet Types
  dietTypeAddMod: (facilityId: number) => `/api/diettype/AddMod/${facilityId}`,
  dietTypeDelete: (facilityId: number, id: number) => `/api/diettype/${facilityId}/${id}`,
  // No Deleted / Restore pair: tblDiet_Type has no IsDeleted column and the DAL hard-
  // deletes, so there is nothing to restore. The ribbon's "View Deleted Diet Types" is an
  // audit-log view — `dietTypeInteractionLogDeleted` below. (A `dietTypeDeleted` /
  // `dietTypeRestore` pair used to be declared here and wired into the table config, which
  // put a "View Deleted" item in the menu that 404'd: the API never had those routes.)
  dietTypeIsInUse: (facilityId: number, id: number) => `/api/diettype/IsInUse/${facilityId}/${id}`,
  dietTypeReplace: (facilityId: number) => `/api/diettype/Replace/${facilityId}`,
  /** Drives the WinForms rename confirm "…is used in the default menu cycle…" (DFInUse). */
  dietTypeUsedInDefaultMenuCycle: (facilityId: number, id: number) =>
    `/api/diettype/UsedInDefaultMenuCycle/${facilityId}/${id}`,
  // Diet Type interaction log — History on the Diet Types table.
  // `…Selected` is every row for one diet type (WinForms
  // InteractionLogFormType.DietType_DataGrid = 17); `…Deleted` is every
  // Delete-type row across all diet types (DietType_DataGrid_Deleted = 18).
  dietTypeInteractionLogSelected: (facilityId: number, id: number) =>
    `/api/diettype/InteractionLog/${facilityId}/${id}`,
  dietTypeInteractionLogDeleted: (facilityId: number) =>
    `/api/diettype/InteractionLog/Deleted/${facilityId}`,
  dietTypeOverridesByDietType: (facilityId: number, dietTypeId: number) =>
    `/api/DietTypeConsistencyOverride/ByDietType/${facilityId}/${dietTypeId}`,
  dietTypeOverrideAddMod: (facilityId: number) => `/api/DietTypeConsistencyOverride/AddMod/${facilityId}`,
  dietTypeOverrideDelete: (facilityId: number, id: number) => `/api/DietTypeConsistencyOverride/${facilityId}/${id}`,

  // Consistencies
  //
  // There is deliberately NO Deleted / Restore pair here. `tblConsist`
  // has no IsDeleted column — the delete is a hard delete, and WinForms's
  // "View Deleted Consistencies" ribbon button opens the INTERACTION LOG
  // filtered to Delete-type rows (ucDietech_Consist.vb:519-530), not a
  // restore grid. `consistencyInteractionLogDeleted` below is that view.
  // (`consistencyDeleted` / `consistencyRestore` used to be declared here
  // and wired into tableConfigs, which put a "View Deleted" item on the
  // menu that 404'd: ConsistencyController never had those routes.)
  consistencyAddMod: (facilityId: number) => `/api/consistency/AddMod/${facilityId}`,
  consistencyDelete: (facilityId: number, id: number) => `/api/consistency/${facilityId}/${id}`,
  /** The ~60-occurrence in-use scan WinForms runs before it will delete. */
  consistencyIsInUse: (facilityId: number, id: number) => `/api/consistency/IsInUse/${facilityId}/${id}`,
  /** `AllowConsistencyFoodOnlyUsage` — can this consistency be Food Only? */
  consistencyFoodOnlyAllowed: (facilityId: number, id: number) =>
    `/api/consistency/FoodOnlyAllowed/${facilityId}/${id}`,
  /** `DFInUse` — is it in the facility's in-use menu cycle? (rename prompt) */
  consistencyInDefaultMenuCycle: (facilityId: number, id: number) =>
    `/api/consistency/InDefaultMenuCycle/${facilityId}/${id}`,
  /** Does ANY facility use production? Gates the production-only form options. */
  consistencyIddsiSupported: (facilityId: number) =>
    `/api/consistency/IddsiSupported/${facilityId}`,
  consistencyProductionEnabled: (facilityId: number) =>
    `/api/consistency/ProductionEnabled/${facilityId}`,
  consistencyInteractionLogSelected: (id: number) => `/api/consistency/InteractionLog/${id}`,
  consistencyInteractionLogDeleted: () => `/api/consistency/InteractionLog/Deleted`,
  consistencyReplace: (facilityId: number) => `/api/consistency/Replace/${facilityId}`,
  consistencyOverridesByConsist: (facilityId: number, consistId: number) =>
    `/api/DietTypeConsistencyOverride/ByConsist/${facilityId}/${consistId}`,

  // Food Allergies
  foodAllergyAddMod: (facilityId: number) => `/api/foodallergy/AddMod/${facilityId}`,
  /** Global adjustment groups and adjustments a food allergy can link to (frmDietech_Food_Allergy_AddMod luPopAdj). */
  foodAllergyAdjustmentLinks: (facilityId: number) => `/api/foodallergy/AdjustmentLinks/${facilityId}`,
  foodAllergyDelete: (facilityId: number, id: number) => `/api/foodallergy/${facilityId}/${id}`,
  // No `Deleted` / `Restore` pair: tblFood_Allergy has no IsDeleted column
  // and the delete is a hard DELETE, so there is no recycle bin to list.
  // Entries for both used to exist here and were wired into the table's
  // config, which put a "View Deleted" item in the toolbar that 404'd on
  // every click. WinForms's "View Deleted Food Allergies" is a DELETION
  // HISTORY — `logInteractionFoodAllergyDeleted` below.
  /**
   * How many residents / EHR mappings still reference this food allergy —
   * the `FoodAllergyInUse` delete guard
   * (ucDietech_Food_Allergies.vb:526-549).
   */
  foodAllergyReferences: (facilityId: number, id: number) =>
    `/api/foodallergy/References/${facilityId}/${id}`,
  /** Merge one food allergy into another (ReplaceFoodAllergy, .vb:624-694). */
  foodAllergyReplace: (facilityId: number) => `/api/foodallergy/Replace/${facilityId}`,

  // Item Types
  itemTypeAddMod: (facilityId: number) => `/api/itemtype/AddMod/${facilityId}`,
  itemTypeDelete: (facilityId: number, id: number) => `/api/itemtype/${facilityId}/${id}`,
  /** Repoints every reference to another item type, then deletes the original. */
  itemTypeReplace: (facilityId: number) => `/api/itemtype/Replace/${facilityId}`,
  /** Reference count across the nineteen tables the shared item type delete check counts. */
  itemTypeReferences: (facilityId: number, id: number) =>
    `/api/itemtype/References/${facilityId}/${id}`,

  // Item Units
  itemUnits: (facilityId: number) => `/api/itemunit/${facilityId}`,
  itemUnitAddMod: (facilityId: number) => `/api/itemunit/AddMod/${facilityId}`,
  itemUnitDelete: (facilityId: number, id: number) => `/api/itemunit/${facilityId}/${id}`,
  /** Repoints every reference to another unit, then deletes the original. */
  itemUnitReplace: (facilityId: number) => `/api/itemunit/Replace/${facilityId}`,
  /** Reference count across the six tables `CountOccurrences` checks. */
  itemUnitReferences: (facilityId: number, id: number) =>
    `/api/itemunit/References/${facilityId}/${id}`,

  // Portions
  portionAddMod: (facilityId: number) => `/api/portion/AddMod/${facilityId}`,

  // Food Groups
  foodGroups: (facilityId: number) => `/api/foodgroup/${facilityId}`,
  // There is deliberately no single-row AddMod / Delete for food groups.
  // Both existed once and both were wrong: AddMod wrote no interaction log and
  // skipped the tally-filter inheritance a new child needs, and Delete issued a
  // bare DELETE that left `tblMenuCategory_FoodGroup` and
  // `tblPrd_Tally_Filter_Detail` rows pointing at a vanished key. The ribbon
  // has only ever had one write — Save — so the port has only `foodGroupSaveTree`.
  // The ribbon's Save (ucDietech_FoodGroups.SaveData): one editing session's
  // adds, modifies and deletes commit together, because a new child can only
  // be inserted once its new parent has an identity. Returns the placeholder →
  // identity map so the page can swap its negative keys without a refetch.
  foodGroupSaveTree: (facilityId: number) => `/api/foodgroup/SaveTree/${facilityId}`,
  // CheckForInUse_AllTables — asked when Delete is pressed, so "Food group is
  // still in use." lands then rather than at Save.
  foodGroupInUse: (facilityId: number, id: number) => `/api/foodgroup/InUse/${facilityId}/${id}`,
  // Tools > Link (frmDietech_Item_LinkTo, FormType.FoodGroup).
  foodGroupLinkedItems: (facilityId: number, id: number) =>
    `/api/foodgroup/LinkedItems/${facilityId}/${id}`,
  // Tools > Item Sizes (frmDietech_Prd_FoodGroup_UpdateSize).
  foodGroupItemSize: (facilityId: number, id: number) =>
    `/api/foodgroup/ItemSize/${facilityId}/${id}`,
  // History: "Selected Food Group" (InteractionLogFormType.FoodGroup_DataGrid = 42)
  // and "View Deleted Food Groups" (FoodGroup_Deleted = 43).
  foodGroupInteractionLogSelected: (facilityId: number, id: number) =>
    `/api/foodgroup/InteractionLog/${facilityId}/${id}`,
  foodGroupInteractionLogDeleted: (facilityId: number) =>
    `/api/foodgroup/InteractionLog/Deleted/${facilityId}`,

  // Special Needs
  specialNeedAddMod: (facilityId: number) => `/api/specialneed/AddMod/${facilityId}`,
  specialNeedDelete: (facilityId: number, id: number) => `/api/specialneed/${facilityId}/${id}`,
  /** Moves every resident to another special need, then deletes the original. */
  specialNeedReplace: (facilityId: number) => `/api/specialneed/Replace/${facilityId}`,
  /** How many residents carry this special need (tblSpecial_Needs_Patient). */
  specialNeedReferences: (facilityId: number, id: number) =>
    `/api/specialneed/References/${facilityId}/${id}`,

  // Special Devices
  specialDeviceAddMod: (facilityId: number) => `/api/specialdevices/AddMod/${facilityId}`,
  specialDeviceDelete: (facilityId: number, id: number) => `/api/specialdevices/${facilityId}/${id}`,
  // Menu-action endpoints ported from `ucDietech_Special_Devices.vb`.
  // Usage + both History paths are system-wide (tblSpec_Devices has
  // no facility column); Replace is facility-stamped only for the
  // audit entry's `FKey_Facility` on `tblLog_Interaction`.
  specialDeviceUsage: (deviceId: number) => `/api/specialdevices/Usage/${deviceId}`,
  specialDeviceInteractionLogSelected: (deviceId: number) => `/api/specialdevices/InteractionLog/${deviceId}`,
  specialDeviceInteractionLogDeleted: () => `/api/specialdevices/InteractionLog/Deleted`,
  specialDeviceReplace: (facilityId: number) => `/api/specialdevices/Replace/${facilityId}`,
  /** Reference count across the thirteen checks `Delete_SpecialDevices` makes. */
  specialDeviceReferences: (deviceId: number) => `/api/specialdevices/References/${deviceId}`,

  // Feeding Status
  feedingStatus: (facilityId: number) => `/api/feedingstatus/${facilityId}`,
  feedingStatusAddMod: (facilityId: number) => `/api/feedingstatus/AddMod/${facilityId}`,
  feedingStatusDelete: (facilityId: number, id: number) => `/api/feedingstatus/${facilityId}/${id}`,
  // Feeding Status menu actions (Usage / History / Replace) — ports
  // ribbon menu on ucDietech_FeedingStatus.vb.
  feedingStatusUsage: (statusId: number) => `/api/feedingstatus/Usage/${statusId}`,
  feedingStatusInteractionLogSelected: (statusId: number) => `/api/feedingstatus/InteractionLog/${statusId}`,
  feedingStatusInteractionLogDeleted: () => `/api/feedingstatus/InteractionLog/Deleted`,
  feedingStatusReplace: (facilityId: number) => `/api/feedingstatus/Replace/${facilityId}`,

  // Note Types
  noteTypes: (facilityId: number) => `/api/Notes/GetNoteTypes/${facilityId}`,
  noteTypeAddMod: (facilityId: number) => `/api/notes/Types/AddMod/${facilityId}`,
  noteTypeDelete: (facilityId: number, id: number) => `/api/notes/Types/${facilityId}/${id}`,
  /** How many notes carry this note type (tblNotes.FKey_Note_Type). */
  noteTypeReferences: (facilityId: number, id: number) =>
    `/api/notes/Types/References/${facilityId}/${id}`,
  /** The Add/Modify `Type:` lookup — tblNote_Type_Default plus its claimant. */
  noteTypeDefaults: (facilityId: number) => `/api/Notes/NoteTypeDefaults/${facilityId}`,

  // Medication Effects
  medicationEffects: (facilityId: number) => `/api/medicationeffect/${facilityId}`,
  medicationEffectAddMod: (facilityId: number) => `/api/medicationeffect/AddMod/${facilityId}`,
  medicationEffectDelete: (facilityId: number, id: number) => `/api/medicationeffect/${facilityId}/${id}`,

  // DRI Groups
  driGroups: (facilityId: number) => `/api/drigroup/${facilityId}`,
  driGroupAddMod: (facilityId: number) => `/api/drigroup/AddMod/${facilityId}`,
  driGroupDelete: (facilityId: number, id: number) => `/api/drigroup/${facilityId}/${id}`,
  /** Residents still assigned to this DRI group — the delete guard. */
  driGroupReferences: (facilityId: number, id: number) =>
    `/api/drigroup/References/${facilityId}/${id}`,
  /** Records the View log WinForms writes when the Modify dialog opens. */
  driGroupLogView: (facilityId: number, id: number) =>
    `/api/drigroup/LogView/${facilityId}/${id}`,
  /** Diet Order Mapping rows (tblRC_DRI_Group_Diet_Flucs) for one group. */
  driGroupDietFlucs: (facilityId: number, id: number) =>
    `/api/drigroup/DietFlucs/${facilityId}/${id}`,
  driGroupDietFlucSave: (facilityId: number) => `/api/drigroup/DietFlucs/${facilityId}`,
  driGroupDietFlucDelete: (facilityId: number, id: number) =>
    `/api/drigroup/DietFlucs/${facilityId}/${id}`,

  // Service Items
  serviceItemAddMod: (facilityId: number) => `/api/serviceitem/AddMod/${facilityId}`,
  serviceItemDelete: (facilityId: number, id: number) => `/api/serviceitem/${facilityId}/${id}`,
  /** Repoints every reference from one OR MORE service items, then deletes them. */
  serviceItemReplace: (facilityId: number) => `/api/serviceitem/Replace/${facilityId}`,
  /** Reference count across the sixteen tables WinForms checks. */
  serviceItemReferences: (facilityId: number, id: number) =>
    `/api/serviceitem/References/${facilityId}/${id}`,

  // Tube Feed Products
  tubeFeedProducts: (facilityId: number) => `/api/tubefeed/${facilityId}`,
  tubeFeedProductAddMod: (facilityId: number) => `/api/tubefeed/AddMod/${facilityId}`,
  tubeFeedProductDelete: (facilityId: number, id: number) => `/api/tubefeed/${facilityId}/${id}`,
  tubeFeedProductReferences: (facilityId: number, id: number) =>
    `/api/tubefeed/ProductReferences/${facilityId}/${id}`,

  // Allergy Groups
  allergyGroups: (facilityId: number) => `/api/allergygroup/${facilityId}`,
  allergyGroupAddMod: (facilityId: number) => `/api/allergygroup/AddMod/${facilityId}`,
  allergyGroupDelete: (facilityId: number, id: number) => `/api/allergygroup/${facilityId}/${id}`,
  /** Repoints every reference to another group, then deletes the original. */
  allergyGroupReplace: (facilityId: number) => `/api/allergygroup/Replace/${facilityId}`,
  /** Reference count across tblItem_Prd_Allergy_Group + tblPrd_Adjustment(_Global). */
  allergyGroupReferences: (facilityId: number, id: number) =>
    `/api/allergygroup/References/${facilityId}/${id}`,
  /** GET lists every item with an IsLinked flag; POST saves the desired set. */
  allergyGroupLinkedItems: (facilityId: number, id: number) =>
    `/api/allergygroup/LinkedItems/${facilityId}/${id}`,
  /** GET: the items that carry the group, on the item, from its linked ingredient or its recipe (read only). */
  allergyGroupItemUsage: (facilityId: number, id: number) =>
    `/api/allergygroup/ItemUsage/${facilityId}/${id}`,

  // Menu Categories (AddMod/Delete)
  menuCategoryAddMod: (facilityId: number) => `/api/menucategory/AddMod/${facilityId}`,
  menuCategoryDelete: (facilityId: number, id: number) => `/api/menucategory/${facilityId}/${id}`,

  // Doctors
  doctorAddMod: (facilityId: number) => `/api/doctor/AddMod/${facilityId}`,
  doctorDelete: (facilityId: number, id: number) => `/api/doctor/${facilityId}/${id}`,
  doctorReferences: (facilityId: number, id: number) =>
    `/api/doctor/References/${facilityId}/${id}`,

  // Employees (full CRUD)
  employees: (facilityId: number) => `/api/employee/${facilityId}`,
  employeeAddMod: (facilityId: number) => `/api/employee/AddMod/${facilityId}`,
  employeeDelete: (facilityId: number, id: number) => `/api/employee/${facilityId}/${id}`,
  /** Menus offered by the Employees tab's "Menu:" picker. */
  employeeMenu: (facilityId: number) => `/api/employee/Menu/${facilityId}`,
  /** Sets tblFacility.FKey_Menu_Header_Employee and logs the change. */
  employeeMenuSave: (facilityId: number) => `/api/employee/Menu/${facilityId}`,

  // Dining Rooms
  diningRooms: (facilityId: number) => `/api/diningroom/${facilityId}`,
  diningRoomAddMod: (facilityId: number) => `/api/diningroom/AddMod/${facilityId}`,
  diningRoomDelete: (facilityId: number, id: number) => `/api/diningroom/${facilityId}/${id}`,
  diningRoomReferences: (facilityId: number, id: number) =>
    `/api/diningroom/References/${facilityId}/${id}`,
  /** How many residents sit at one table — warns before removing it. */
  diningRoomTableSeated: (facilityId: number, pKeyTable: number) =>
    `/api/diningroom/TableSeated/${facilityId}/${pKeyTable}`,
  /** Type lookup for the Dining Times grid (name + description). */
  diningRoomSeatingChoices: (facilityId: number) =>
    `/api/diningroom/SeatingChoices/${facilityId}`,
  diningRoomTimes: (facilityId: number, diningRoomId: number) =>
    `/api/diningroom/Times/${facilityId}/${diningRoomId}`,
  diningRoomTimesSave: (facilityId: number, diningRoomId: number) =>
    `/api/diningroom/Times/${facilityId}/${diningRoomId}`,
  diningRoomTablesSave: (facilityId: number, diningRoomId: number) =>
    `/api/diningroom/Tables/${facilityId}/${diningRoomId}`,
  /** True when a resident sits beyond the proposed seat count. */
  diningRoomTableSeatOverflow: (facilityId: number, pKeyTable: number, numberOfSeats: number) =>
    `/api/diningroom/TableSeatOverflow/${facilityId}/${pKeyTable}/${numberOfSeats}`,

  // Patient Seating Assignments (persistent, tblLayoutDiningRoom_PatientSeating)
  patientSeatingAssignments: (facilityId: number) =>
    `/api/DiningRoom/GetPatientSeatingAssignments/${facilityId}`,
  patientSeatingAssignmentAdd: (facilityId: number) =>
    `/api/DiningRoom/AddPatientSeatingAssignment/${facilityId}`,
  patientSeatingAssignmentUpdate: (facilityId: number) =>
    `/api/DiningRoom/UpdatePatientSeatingAssignment/${facilityId}`,
  patientSeatingAssignmentDelete: (facilityId: number, pKey: number) =>
    `/api/DiningRoom/DeletePatientSeatingAssignment/${facilityId}/${pKey}`,

  // Production Areas
  productionAreas: (facilityId: number) => `/api/production/Areas/${facilityId}`,
  productionAreaAddMod: (facilityId: number) => `/api/production/Areas/AddMod/${facilityId}`,
  productionAreaDelete: (facilityId: number, id: number) => `/api/production/Areas/${facilityId}/${id}`,
  productionAreaReferences: (facilityId: number, id: number) =>
    `/api/production/Areas/References/${facilityId}/${id}`,

  // Printer Categories (tblPrinterCategory, shared by every facility; PrinterCategoryController)
  printerCategories: (facilityId: number) => `/api/Printer/Categories/${facilityId}`,
  printerCategoryAddMod: (facilityId: number) => `/api/Printer/Categories/AddMod/${facilityId}`,
  printerCategoryDelete: (facilityId: number, id: number) => `/api/Printer/Categories/${facilityId}/${id}`,

  // Tables > Recipes lookups (database-wide; RecipeTablesController)
  recipeCategoryList: (facilityId: number) => `/api/RecipeTables/Categories/${facilityId}`,
  recipeCategoryAddMod: (facilityId: number) => `/api/RecipeTables/Categories/AddMod/${facilityId}`,
  recipeCategoryDelete: (facilityId: number, id: number) => `/api/RecipeTables/Categories/${facilityId}/${id}`,
  preparationTypeList: (facilityId: number) => `/api/RecipeTables/PreparationTypes/${facilityId}`,
  preparationTypeAddMod: (facilityId: number) => `/api/RecipeTables/PreparationTypes/AddMod/${facilityId}`,
  preparationTypeDelete: (facilityId: number, id: number) => `/api/RecipeTables/PreparationTypes/${facilityId}/${id}`,
  recipeIngredientsGrid: (facilityId: number) => `/api/RecipeTables/Ingredients/${facilityId}`,
  recipeNutrientsGrid: (facilityId: number) => `/api/RecipeTables/RecipeNutrients/${facilityId}`,
  recipeNutrientValues: (facilityId: number) => `/api/RecipeTables/RecipeNutrients/Values/${facilityId}`,
  unitEquivalenceList: (facilityId: number) => `/api/RecipeTables/UnitEquivalences/${facilityId}`,
  unitEquivalenceAddMod: (facilityId: number) => `/api/RecipeTables/UnitEquivalences/AddMod/${facilityId}`,
  unitEquivalenceDelete: (facilityId: number, id: number) => `/api/RecipeTables/UnitEquivalences/${facilityId}/${id}`,

  // Tables > Shelf Locations (tblOP_InventoryLocation, per facility; ShelfLocationController)
  shelfLocationList: (facilityId: number) => `/api/ShelfLocation/${facilityId}`,
  shelfLocationAddMod: (facilityId: number) => `/api/ShelfLocation/AddMod/${facilityId}`,
  // `confirmed` answers WinForms' second question (items are still on the shelf).
  shelfLocationDelete: (facilityId: number, id: number, confirmed = false) =>
    `/api/ShelfLocation/${facilityId}/${id}?confirmed=${confirmed}`,
  shelfLocationHistory: (facilityId: number, id: number) => `/api/ShelfLocation/History/${facilityId}/${id}`,
  shelfLocationHistoryDeleted: (facilityId: number) => `/api/ShelfLocation/HistoryDeleted/${facilityId}`,

  // Tables > Production lookups (database-wide; ProductionTablesController)
  utensilList: (facilityId: number) => `/api/ProductionTables/Utensils/${facilityId}`,
  utensilAddMod: (facilityId: number) => `/api/ProductionTables/Utensils/AddMod/${facilityId}`,
  utensilDelete: (facilityId: number, id: number) => `/api/ProductionTables/Utensils/${facilityId}/${id}`,
  specialEventRequestList: (facilityId: number) => `/api/ProductionTables/SpecialEventRequests/${facilityId}`,
  specialEventRequestAddMod: (facilityId: number) => `/api/ProductionTables/SpecialEventRequests/AddMod/${facilityId}`,
  specialEventRequestDelete: (facilityId: number, id: number) =>
    `/api/ProductionTables/SpecialEventRequests/${facilityId}/${id}`,
  // Special Events > Print: the blank requisition form for the facility, as a PDF.
  specialEventRequisition: (facilityId: number) => `/api/ProductionTables/SpecialEventRequisition/${facilityId}`,
  /** The licence and facility switches the Tables menu reads (Diet Lite, Assessment Only, an assessment facility). */
  tablesMenuFlags: (facilityId: number) => `/api/TablesMenu/${facilityId}`,

  // Global Adjustments
  globalAdjustments: (facilityId: number) => `/api/globaladjustment/${facilityId}`,
  globalAdjustmentByGroup: (facilityId: number, groupId: number) => `/api/globaladjustment/ByGroup/${facilityId}/${groupId}`,

  // Adjustment Global Groups
  adjustmentGlobalGroups: (facilityId: number) => `/api/globaladjustment/GlobalGroups/${facilityId}`,
  adjustmentGlobalGroupAddMod: (facilityId: number) => `/api/globaladjustment/GlobalGroups/AddMod/${facilityId}`,
  adjustmentGlobalGroupDelete: (facilityId: number, id: number) => `/api/globaladjustment/GlobalGroups/${facilityId}/${id}`,
  adjustmentGlobalGroupReferences: (facilityId: number, id: number) =>
    `/api/globaladjustment/GlobalGroups/References/${facilityId}/${id}`,

  // Global Adjustment CRUD
  globalAdjustmentAddMod: (facilityId: number) => `/api/globaladjustment/AddMod/${facilityId}`,
  // Bulk Activate/Deactivate every template in a group. Body:
  // { groupPKey, newInactive }. Cascades to patient mirror rows.
  // Powers the toolbar Activate/Deactivate → "Entire Group" menu
  // item on the Adjustments tab.
  globalAdjustmentBulkSetInactive: (facilityId: number) =>
    `/api/globaladjustment/BulkSetInactive/${facilityId}`,
  globalAdjustmentDelete: (facilityId: number, id: number) => `/api/globaladjustment/${facilityId}/${id}`,
  // Multi-select Delete: body { pKeys }; per-key results (GlobalAdjustmentController.DeleteBatch).
  globalAdjustmentDeleteBatch: (facilityId: number) => `/api/globaladjustment/DeleteBatch/${facilityId}`,
  // Food allergies linked to each of { pKeys } (the delete clears those links).
  globalAdjustmentFoodAllergyLinks: (facilityId: number) => `/api/globaladjustment/FoodAllergyLinks/${facilityId}`,
  // Active residents (and their facilities) holding a copy of each of { pKeys }, for the impact line.
  globalAdjustmentUsageCounts: (facilityId: number) => `/api/globaladjustment/UsageCounts/${facilityId}`,
  // The residents holding a copy of one adjustment ("See who"), same rows as GlobalItems/Usage.
  globalAdjustmentUsage: (facilityId: number, pKey: number) => `/api/globaladjustment/Usage/${facilityId}/${pKey}`,
  // Read the currently-linked group PKeys for a given global
  // adjustment — used by the edit dialog to pre-check group checkboxes.
  globalAdjustmentGroupIds: (facilityId: number, pKey: number) =>
    `/api/globaladjustment/GroupIds/${facilityId}/${pKey}`,
  // Master-detail loaders — return ALL detail rows attached to
  // templates (FKey_Patient IS NULL on parent), then the React detail
  // components filter in-memory per expanded row.
  globalAdjustmentChildren: (facilityId: number) =>
    `/api/globaladjustment/Children/${facilityId}`,
  globalAdjustmentExceptions: (facilityId: number) =>
    `/api/globaladjustment/Exceptions/${facilityId}`,
  globalAdjustmentOnMenu: (facilityId: number) =>
    `/api/globaladjustment/OnMenu/${facilityId}`,
  globalAdjustmentDietOrder: (facilityId: number) =>
    `/api/globaladjustment/DietOrder/${facilityId}`,

  // Meal Pattern Global Groups
  mealPatternGlobalGroups: (facilityId: number) => `/api/mealpattern/Groups/${facilityId}`,
  mealPatternGlobalGroupAddMod: (facilityId: number) => `/api/mealpattern/Groups/AddMod/${facilityId}`,
  mealPatternGlobalGroupDelete: (facilityId: number, id: number) => `/api/mealpattern/Groups/${facilityId}/${id}`,
  mealPatternGlobalGroupReferences: (facilityId: number, id: number) =>
    `/api/mealpattern/Groups/References/${facilityId}/${id}`,
  // Every group with its template count and the active residents holding any of them.
  mealPatternGlobalGroupUsageCounts: (facilityId: number) =>
    `/api/mealpattern/Groups/UsageCounts/${facilityId}`,
  mealPatternGetGlobalGroups: (facilityId: number) => `/api/mealpattern/GetGlobalGroups/${facilityId}`,
  mealPatternGlobalUsage: (facilityId: number, globalGroupId: number) =>
    `/api/mealpattern/GlobalUsage/${facilityId}/${globalGroupId}`,

  // Material Colors
  materialColors: (facilityId: number) => `/api/skin/Colors/${facilityId}`,
  materialColorAddMod: (facilityId: number) => `/api/skin/Colors/AddMod/${facilityId}`,
  materialColorDelete: (facilityId: number, id: number) => `/api/skin/Colors/${facilityId}/${id}`,
  materialColorReferences: (facilityId: number, id: number) =>
    `/api/skin/Colors/References/${facilityId}/${id}`,

  // Garnish
  garnish: (facilityId: number) => `/api/item/GetGarnish/${facilityId}`,
  garnishAddMod: (facilityId: number) => `/api/item/Garnish/AddMod/${facilityId}`,
  garnishDelete: (facilityId: number, id: number) => `/api/item/Garnish/${facilityId}/${id}`,
  garnishReferences: (facilityId: number, id: number) =>
    `/api/item/Garnish/References/${facilityId}/${id}`,

  // IBW
  ibw: (facilityId: number) => `/api/ibw/${facilityId}`,
  ibwAddMod: (facilityId: number) => `/api/ibw/AddMod/${facilityId}`,
  ibwDelete: (facilityId: number, id: number) => `/api/ibw/${facilityId}/${id}`,

  // Consistency Combinations (system-wide, no facility scoping)
  consistCombinations: () => `/api/ConsistCombination`,
  consistCombinationAddMod: () => `/api/ConsistCombination/AddMod`,
  consistCombinationDelete: (id: number) => `/api/ConsistCombination/${id}`,

  // Sequence Order (Diet Type / Consistency ordering)
  // Diet List Sequence — the whole screen, both lists.
  dietListSequence: (facilityId: number) => `/api/DietListSequence/${facilityId}`,
  /** Writes both sequences AND resequences the facility's residents. */
  dietListSequenceSave: (facilityId: number) => `/api/DietListSequence/Save/${facilityId}`,

  sequenceOrderDietTypes: (facilityId: number) => `/api/SequenceOrder/DietTypes/${facilityId}`,
  sequenceOrderDietTypesSave: (facilityId: number) => `/api/SequenceOrder/DietTypes/${facilityId}`,
  sequenceOrderConsistencies: (facilityId: number) => `/api/SequenceOrder/Consistencies/${facilityId}`,
  sequenceOrderConsistenciesSave: (facilityId: number) => `/api/SequenceOrder/Consistencies/${facilityId}`,

  // Special Need Children (Types, DF matching, Rooms)
  specialNeedTypes: (facilityId: number) => `/api/SpecialNeed/Types/${facilityId}`,
  assessmentSetupTypesForFacility: (facilityId: number) =>
    `/api/Assessment/FacilityTypes/${facilityId}`,
  createBlankAssessmentHeaderForReport: (facilityId: number) =>
    `/api/Assessment/CreateBlankHeaderForReport/${facilityId}`,
  specialNeedDoNotPrintTTPatients: (facilityId: number) =>
    `/api/SpecialNeed/DoNotPrintTTPatients/${facilityId}`,
  specialNeedDF: (facilityId: number, specialNeedId: number) =>
    `/api/SpecialNeed/DF/${facilityId}/${specialNeedId}`,
  specialNeedDFSave: (facilityId: number, specialNeedId: number) =>
    `/api/SpecialNeed/DF/${facilityId}/${specialNeedId}`,
  specialNeedRooms: (facilityId: number, specialNeedId: number) =>
    `/api/SpecialNeed/Rooms/${facilityId}/${specialNeedId}`,
  specialNeedRoomsSave: (facilityId: number, specialNeedId: number) =>
    `/api/SpecialNeed/Rooms/${facilityId}/${specialNeedId}`,
  /** The Add/Modify "Options" tab — the per-facility Do Not Print Tray
   *  Ticket override. GET returns null when there is none. */
  specialNeedFacilityOption: (facilityId: number, specialNeedId: number) =>
    `/api/SpecialNeed/FacilityOption/${facilityId}/${specialNeedId}`,

  // Menu Category - Food Groups (child)
  menuCategoryFoodGroups: (menuCategoryId: number) =>
    `/api/MenuCategory/FoodGroups/${menuCategoryId}`,
  menuCategoryFoodGroupsSave: (menuCategoryId: number) =>
    `/api/MenuCategory/FoodGroups/${menuCategoryId}`,
  menuCategoryReferences: (facilityId: number, id: number) =>
    `/api/MenuCategory/References/${facilityId}/${id}`,
  /** Zero means the add/mod form may still change "Sorting Only". */
  menuCategorySortingOnlyBlockers: (facilityId: number, id: number) =>
    `/api/MenuCategory/SortingOnlyBlockers/${facilityId}/${id}`,

  // Item Unit - Scoops (child)
  itemUnitScoops: (itemUnitId: number) => `/api/ItemUnit/Scoops/${itemUnitId}`,
  itemUnitScoopsSave: (itemUnitId: number) => `/api/ItemUnit/Scoops/${itemUnitId}`,

  // Assessments
  /** One page of the facility's assessments: ?skip=&take=&search= (current residents only). */
  assessmentList: (facilityId: number) => `/api/assessment/List/${facilityId}`,
  patientAssessments: (facilityId: number, patientId: number) =>
    `/api/assessment/${facilityId}/${patientId}`,
  // No assessment AddMod: full create / edit is the rebuild's P4 (the API refuses it, 403).
  // New Blank, Sign and Delete (P3) work only where the API's
  // Assessments:WebSaveConnections lists the connection; Capabilities says so.
  assessmentCapabilities: (facilityId: number) =>
    `/api/Assessment/Capabilities/${facilityId}`,
  assessmentBlank: (facilityId: number, patientId: number) =>
    `/api/Assessment/Blank/${facilityId}/${patientId}`,
  assessmentSign: (facilityId: number, id: number) =>
    `/api/Assessment/Sign/${facilityId}/${id}`,
  assessmentDelete: (facilityId: number, id: number, token: string) =>
    `/api/Assessment/${facilityId}/${id}?token=${encodeURIComponent(token)}`,
  assessmentToken: (facilityId: number, id: number) =>
    `/api/Assessment/Token/${facilityId}/${id}`,
  // Full create / modify through the web wizard (P4 E1), behind Assessments:WebEditConnections.
  /** The wizard's control values as it opens a saved assessment (the starting point of an edit). */
  assessmentFormState: (facilityId: number, id: number) =>
    `/api/Assessment/FormState/${facilityId}/${id}`,
  /** The wizard's control values for a new assessment of one facility type (writes nothing). */
  assessmentNewFormState: (facilityId: number, patientId: number, typeFacilityPKey: number) =>
    `/api/Assessment/FormState/${facilityId}/${patientId}?typeId=${typeFacilityPKey}`,
  /** Finish a new assessment: the pages opened and the changed values. */
  assessmentNew: (facilityId: number, patientId: number) =>
    `/api/Assessment/New/${facilityId}/${patientId}`,
  /** Finish a reopened assessment (409 when it changed since it was loaded). */
  assessmentSave: (facilityId: number, id: number) =>
    `/api/Assessment/Save/${facilityId}/${id}`,
  /** One saved assessment, read-only, as the desktop wizard reopens it (AssessmentSnapshotBuilder). */
  assessmentDocument: (facilityId: number, id: number) =>
    `/api/Assessment/Document/${facilityId}/${id}`,
  /** The desktop wizard's on-screen calculations (BMI, % IBW, weight change, needs) for an assessment. */
  assessmentCalculate: (facilityId: number) =>
    `/api/Assessment/Calculate/${facilityId}`,
  /** The facility's assessment types, groups and details. */
  assessmentTemplate: (facilityId: number) =>
    `/api/Assessment/Template/${facilityId}`,
  /** Facility assessment types as edited in Tables (tblAssessmentSetupType_Facility) */
  assessmentTypeSettings: (facilityId: number) =>
    `/api/Assessment/TypeSettings/${facilityId}`,
  assessmentTypeSettingsSave: (facilityId: number) =>
    `/api/Assessment/TypeSettings/AddMod/${facilityId}`,
  assessmentTypeSettingsOrder: (facilityId: number) =>
    `/api/Assessment/TypeSettings/Order/${facilityId}`,
  /** Facility assessment configuration grid (types x sections x details) */
  assessmentConfiguration: (facilityId: number) =>
    `/api/AssessmentConfiguration/${facilityId}`,
  assessmentConfigurationSave: (facilityId: number) =>
    `/api/AssessmentConfiguration/Save/${facilityId}`,
  assessmentConfigurationResetAll: (facilityId: number) =>
    `/api/AssessmentConfiguration/ResetAll/${facilityId}`,
  assessmentConfigurationCopySources: (facilityId: number) =>
    `/api/AssessmentConfiguration/CopySources/${facilityId}`,
  assessmentConfigurationCopyFrom: (facilityId: number) =>
    `/api/AssessmentConfiguration/CopyFrom/${facilityId}`,
  /** Assessment lookup tables (Tables > CAA/CAT Triggers, Obtained From, Disciplines, Nutrition Care Process) */
  caaCatTriggers: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggers/${facilityId}`,
  caaCatTriggersAddMod: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggers/AddMod/${facilityId}`,
  caaCatTriggersDelete: (facilityId: number, id: number) => `/api/AssessmentLookups/CaaCatTriggers/${facilityId}/${id}`,
  caaCatTriggersOrder: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggers/Order/${facilityId}`,
  caaCatTriggerFrom: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggerFrom/${facilityId}`,
  caaCatTriggerFromAddMod: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggerFrom/AddMod/${facilityId}`,
  caaCatTriggerFromDelete: (facilityId: number, id: number) => `/api/AssessmentLookups/CaaCatTriggerFrom/${facilityId}/${id}`,
  caaCatTriggerFromOrder: (facilityId: number) => `/api/AssessmentLookups/CaaCatTriggerFrom/Order/${facilityId}`,
  assessmentDisciplines: (facilityId: number) => `/api/AssessmentLookups/Disciplines/${facilityId}`,
  assessmentDisciplinesAddMod: (facilityId: number) => `/api/AssessmentLookups/Disciplines/AddMod/${facilityId}`,
  assessmentDisciplinesDelete: (facilityId: number, id: number) => `/api/AssessmentLookups/Disciplines/${facilityId}/${id}`,
  nutritionCareProcesses: (facilityId: number) => `/api/AssessmentLookups/NutritionCareProcesses/${facilityId}`,
  nutritionCareProcessesAddMod: (facilityId: number) => `/api/AssessmentLookups/NutritionCareProcesses/AddMod/${facilityId}`,
  nutritionCareProcessesDelete: (facilityId: number, id: number) => `/api/AssessmentLookups/NutritionCareProcesses/${facilityId}/${id}`,

  // Plan of Care
  planOfCare: (facilityId: number) => `/api/planofcare/${facilityId}`,
  patientPlanOfCare: (facilityId: number, patientId: number) =>
    `/api/planofcare/${facilityId}/${patientId}`,
  planOfCareDetail: (facilityId: number, id: number) =>
    `/api/planofcare/Detail/${facilityId}/${id}`,
  planOfCareAddMod: (facilityId: number) =>
    `/api/planofcare/AddMod/${facilityId}`,
  planOfCareDelete: (facilityId: number, id: number) =>
    `/api/planofcare/${facilityId}/${id}`,
  planOfCareDomains: (facilityId: number, headerId: number) =>
    `/api/planofcare/Domains/${facilityId}/${headerId}`,
  planOfCareDomainsAddMod: (facilityId: number) =>
    `/api/planofcare/Domains/AddMod/${facilityId}`,
  planOfCareDomainsDelete: (facilityId: number, id: number) =>
    `/api/planofcare/Domains/${facilityId}/${id}`,
  // The evaluation wizard and the resident's plan list (PlanOfCareEvaluationController).
  planOfCareEvaluation: (facilityId: number, patientId: number, id: number) =>
    `/api/PlanOfCareEvaluation/${facilityId}/${patientId}/${id}`,
  planOfCareEvaluationSave: (facilityId: number, patientId: number) =>
    `/api/PlanOfCareEvaluation/Save/${facilityId}/${patientId}`,
  planOfCareEvaluationSaveBlank: (facilityId: number, patientId: number) =>
    `/api/PlanOfCareEvaluation/SaveBlank/${facilityId}/${patientId}`,
  planOfCareEvaluationCopy: (facilityId: number, patientId: number, id: number) =>
    `/api/PlanOfCareEvaluation/CopyFromExisting/${facilityId}/${patientId}/${id}`,
  planOfCareEvaluationCompleted: (facilityId: number, patientId: number, id: number, completed: boolean) =>
    `/api/PlanOfCareEvaluation/Completed/${facilityId}/${patientId}/${id}?completed=${completed}`,
  planOfCareEvaluationDelete: (facilityId: number, patientId: number, id: number) =>
    `/api/PlanOfCareEvaluation/${facilityId}/${patientId}/${id}`,

  // Help / About (local API)
  helpContactPage: () => `/api/pages/contact`,
  helpPrivacyPolicy: () => `/api/pages/privacypolicy`,
  helpWhatsNewBefore: () => `/api/github/whatsnewbefore`,
  helpWhatsNewAfter: () => `/api/github/whatsnewafter`,

  // Census
  // Census history (PM_Server tblPrd_Census + tblPrd_Census_{facility}).
  censusHistory: (facilityId: number, start: string, end: string) =>
    `/api/Census/${facilityId}/history?start=${start}&end=${end}`,
  censusDeleteAll: (facilityId: number) => `/api/Census/${facilityId}`,

  // Locations
  locations: (facilityId: number) => `/api/Location/${facilityId}`,
  locationSubAll: (facilityId: number) => `/api/Location/${facilityId}/allsub`,
  /** GET / PUT the Division/Region editor: the two names and every location and sub location. */
  locationEditor: (facilityId: number) => `/api/Location/${facilityId}/editor`,

  // Physical Properties
  /** The facility's physical properties with all their default values (Facility Settings > Physical Properties). */
  physicalProperties: (facilityId: number) => `/api/PhysicalProperty/${facilityId}`,
  // Display-true physical properties for the facility with
  // their associated default values nested under each. Used to
  // configure lookup-edit dropdowns.
  physicalPropertiesDisplay: (facilityId: number) => `/api/PhysicalProperty/Display/${facilityId}`,
  /** PUT: one property's display and Other settings and its default values. */
  physicalPropertySave: (facilityId: number, id: number) => `/api/PhysicalProperty/${facilityId}/${id}`,
  /** PUT: every property key of the facility, in the new display order. */
  physicalPropertyOrder: (facilityId: number) => `/api/PhysicalProperty/${facilityId}/order`,

  // Food Storage
  foodStorageList: (facilityId: number) => `/api/FoodStorage/${facilityId}`,
  /** Add or update one location — this page writes immediately. */
  foodStorageAddMod: (facilityId: number) => `/api/FoodStorage/AddMod/${facilityId}`,
  foodStorageDelete: (facilityId: number, id: number) => `/api/FoodStorage/${facilityId}/${id}`,

  // Item Linking
  itemLinking: (facilityId: number) => `/api/ItemLinking/${facilityId}`,
  itemLinkingSave: (facilityId: number) => `/api/ItemLinking/Save/${facilityId}`,

  // Menu Category Grouping
  menuCategoryGroupings: (facilityId: number) => `/api/MenuCategoryGrouping/${facilityId}`,
  /** The Menu Category picker — a master table, not facility-filtered. */
  menuCategoryGroupingCategories: (facilityId: number) =>
    `/api/MenuCategoryGrouping/Categories/${facilityId}`,
  menuCategoryGroupingAddMod: (facilityId: number) =>
    `/api/MenuCategoryGrouping/AddMod/${facilityId}`,
  /** Report / POS layouts still using a group — the delete guard. */
  menuCategoryGroupingReferences: (facilityId: number, id: number) =>
    `/api/MenuCategoryGrouping/References/${facilityId}/${id}`,
  menuCategoryGroupingDelete: (facilityId: number, id: number) =>
    `/api/MenuCategoryGrouping/${facilityId}/${id}`,

  // Forecasting Prep/Pull
  /** The whole grid for one menu cycle, plus the corporate flags. */
  prepPullLoad: (facilityId: number, menuHeaderId: number, viewFacility?: number) =>
    `/api/PrepPull/${facilityId}/${menuHeaderId}`
    + (viewFacility ? `?viewFacility=${viewFacility}` : ''),
  /** The menu list plus the cycle in use today for the viewed facility. */
  prepPullMenus: (facilityId: number, viewFacility?: number) =>
    `/api/PrepPull/Menus/${facilityId}`
    + (viewFacility ? `?viewFacility=${viewFacility}` : ''),
  prepPullAreas: (facilityId: number) => `/api/PrepPull/Areas/${facilityId}`,
  prepPullSave: (facilityId: number) => `/api/PrepPull/Save/${facilityId}`,

  // Facility Sets
  facilitySetList: (facilityId: number) => `/api/facilityset/${facilityId}`,
  /** The deferred Save — the whole three-grid layout in one request. */
  facilitySetSaveAll: (facilityId: number) => `/api/facilityset/SaveAll/${facilityId}`,
  /** Residents + tray line overrides still sitting on one set. */
  facilitySetInUse: (facilityId: number, mealtime: number, number: number) =>
    `/api/facilityset/InUse/${facilityId}/${mealtime}/${number}`,
  /** Remove one set and shift the tray line down behind it. */
  facilitySetRemove: (facilityId: number, id: number, mealtime: number, number: number) =>
    `/api/facilityset/Remove/${facilityId}/${id}/${mealtime}/${number}`,
  /** Auto Tray Unit picker options, in Floor/Wing order. */
  facilitySetUnits: (facilityId: number) => `/api/facilityset/Units/${facilityId}`,
  /** Per-mealtime resident set ceiling, for the Copy guard. */
  facilitySetMax: (facilityId: number) => `/api/facilityset/SetMax/${facilityId}`,

  // Business Hours / Tech Support Status
  businessHoursStatus: () => `/api/BusinessHours/get`,

  // Credit Billing Codes (CRUD)
  creditBillingCodeAddMod: (facilityId: number) =>
    `/api/creditbillingcode/AddMod/${facilityId}`,

  // Adjustment (Patient-level)
  adjustmentByPatient: (facilityId: number, patientId: number) =>
    `/api/Adjustment/${facilityId}/${patientId}`,
  adjustmentChildrenByPatient: (facilityId: number, patientId: number) =>
    `/api/Adjustment/Children/${facilityId}/${patientId}`,
  adjustmentExceptionsByPatient: (facilityId: number, patientId: number) =>
    `/api/Adjustment/Exceptions/${facilityId}/${patientId}`,
  adjustmentOnMenuByPatient: (facilityId: number, patientId: number) =>
    `/api/Adjustment/OnMenu/${facilityId}/${patientId}`,
  adjustmentDietOrderByPatient: (facilityId: number, patientId: number) =>
    `/api/Adjustment/DietOrder/${facilityId}/${patientId}`,
  adjustmentAddMod: (facilityId: number) =>
    `/api/Adjustment/AddMod/${facilityId}`,
  adjustmentDelete: (facilityId: number, patientId: number, pKey: number) =>
    `/api/Adjustment/${facilityId}/${patientId}/${pKey}`,
  adjustmentSwapPriority: (facilityId: number, patientId: number) =>
    `/api/Adjustment/SwapPriority/${facilityId}/${patientId}`,
  adjustmentBulkActivate: (facilityId: number, patientId: number, activate: boolean) =>
    `/api/Adjustment/BulkActivate/${facilityId}/${patientId}/${activate}`,
  adjustmentToggleInactive: (facilityId: number, patientId: number) =>
    `/api/Adjustment/ToggleInactive/${facilityId}/${patientId}`,
  adjLogAdd: (facilityId: number, patientId: number, parentPKey: number) =>
    `/api/AdjustmentLog/Add/${facilityId}/${patientId}/${parentPKey}`,
  adjLogModify: (facilityId: number, patientId: number, parentPKey: number) =>
    `/api/AdjustmentLog/Modify/${facilityId}/${patientId}/${parentPKey}`,
  adjLogDelete: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/Delete/${facilityId}/${patientId}`,
  adjLogDeleteAll: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/DeleteAll/${facilityId}/${patientId}`,
  adjLogInactiveToggle: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/InactiveToggle/${facilityId}/${patientId}`,
  adjLogAddGlobal: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/AddGlobal/${facilityId}/${patientId}`,
  adjLogResetToGlobal: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/ResetToGlobal/${facilityId}/${patientId}`,
  adjLogMakeIntoGlobal: (facilityId: number, patientId: number) =>
    `/api/AdjustmentLog/MakeIntoGlobal/${facilityId}/${patientId}`,
  adjustmentResetToGlobal: (facilityId: number, pKey: number) =>
    `/api/Adjustment/ResetToGlobal/${facilityId}/${pKey}`,
  adjustmentDeleteAll: (facilityId: number, patientId: number) =>
    `/api/Adjustment/DeleteAll/${facilityId}/${patientId}`,
  adjustmentCopyFromResident: (facilityId: number, toPatientId: number, fromPatientId: number) =>
    `/api/Adjustment/CopyFromResident/${facilityId}/${toPatientId}/${fromPatientId}`,
  adjustmentInsertPatientFromTemplate: (facilityId: number, patientId: number, templatePKey: number) =>
    `/api/Adjustment/InsertPatientFromTemplate/${facilityId}/${patientId}/${templatePKey}`,
  adjustmentBreakGlobalLink: (facilityId: number, patientId: number, pKeyGlobal: number) =>
    `/api/Adjustment/BreakGlobalLink/${facilityId}/${patientId}/${pKeyGlobal}`,
  adjustmentMakeIntoGlobal: (facilityId: number, patientId: number, legacyPKey: number, templatePKey: number) =>
    `/api/Adjustment/MakeIntoGlobal/${facilityId}/${patientId}/${legacyPKey}/${templatePKey}`,
  adjustmentExceptionAdd: (facilityId: number) =>
    `/api/Adjustment/Exception/Add/${facilityId}`,
  adjustmentExceptionDelete: (facilityId: number, fKeyAdj: number, fKeyItem: number, type: number) =>
    `/api/Adjustment/Exception/${facilityId}/${fKeyAdj}/${fKeyItem}/${type}`,
  adjustmentOnMenuAdd: (facilityId: number) =>
    `/api/Adjustment/OnMenu/Add/${facilityId}`,
  adjustmentChildAdd: (facilityId: number) =>
    `/api/Adjustment/Child/Add/${facilityId}`,
  adjustmentChildDelete: (facilityId: number, pKey: number) =>
    `/api/Adjustment/Child/${facilityId}/${pKey}`,
  adjustmentOnMenuDelete: (facilityId: number, pKey: number) =>
    `/api/Adjustment/OnMenu/${facilityId}/${pKey}`,

  // AIM
  aimSettings: (facilityId: number) =>
    `/api/AIM/Settings/${facilityId}`,
  aimExecute: (facilityId: number) =>
    `/api/AIM/Execute/${facilityId}`,
  aimReverseMealPattern: (facilityId: number) =>
    `/api/AIM/ReverseMealPattern/${facilityId}`,
  aimReverseAdjustment: (facilityId: number) =>
    `/api/AIM/ReverseAdjustment/${facilityId}`,
  aimCheckItem: (facilityId: number, patientId: number, fKeyItem: number) =>
    `/api/AIM/CheckItem/${facilityId}/${patientId}/${fKeyItem}`,

  // Adjustment Diet Filter
  adjDietFilters: (facilityId: number, adjPKey: number) =>
    `/api/AdjustmentDietFilter/${facilityId}/${adjPKey}`,
  adjDietFilterSaveAll: (facilityId: number, adjPKey: number) =>
    `/api/AdjustmentDietFilter/SaveAll/${facilityId}/${adjPKey}`,
  adjDietFilterDelete: (facilityId: number, pKey: number) =>
    `/api/AdjustmentDietFilter/${facilityId}/${pKey}`,

  // Food Allergy Warnings
  foodAllergyWarnings: (facilityId: number, patientId: number) =>
    `/api/FoodAllergy/Warnings/${facilityId}/${patientId}`,

  // Meal Pattern History.
  // `isGlobal=true` switches the controller's WHERE clause from
  // FKey_Prd_Meal_Pattern / FKey_Prd_Adjustment (legacy) to
  // FKey_Prd_Meal_Pattern_Global / FKey_Prd_Adjustment_Global so
  // patient global-instance rows match. The pKey passed alongside
  // MUST be the real positive PKey in the _Global table (not the
  // synthetic-negative grid key).
  mpHistory: (facilityId: number, pKey: number, isGlobal = false) =>
    `/api/MealPatternHistory/MealPattern/${facilityId}/${pKey}?isGlobal=${isGlobal}`,
  adjHistory: (facilityId: number, pKey: number, isGlobal = false) =>
    `/api/MealPatternHistory/Adjustment/${facilityId}/${pKey}?isGlobal=${isGlobal}`,
  mpDeletedHistory: (facilityId: number, patientId: number) =>
    `/api/MealPatternHistory/DeletedMP/${facilityId}/${patientId}`,
  adjDeletedHistory: (facilityId: number, patientId: number) =>
    `/api/MealPatternHistory/DeletedAdj/${facilityId}/${patientId}`,
  // Master-detail snapshots — `logPKey` is the tblLog_Prd_*.PKey
  // (BIGINT) of the parent log row, not the production row's PKey.
  /** Facility Meal Pattern History (every resident's changes between two days). */
  facilityMPHistory: (facilityId: number, start: string, end: string) =>
    `/api/MealPatternHistory/Facility/${facilityId}?start=${start}&end=${end}`,
  mpChildHistory: (facilityId: number, logPKey: number) =>
    `/api/MealPatternHistory/MealPatternChild/${facilityId}/${logPKey}`,
  adjChildHistory: (facilityId: number, logPKey: number) =>
    `/api/MealPatternHistory/AdjustmentChild/${facilityId}/${logPKey}`,
  adjDFHistory: (facilityId: number, logPKey: number) =>
    `/api/MealPatternHistory/AdjustmentDF/${facilityId}/${logPKey}`,
  adjExceptionHistory: (facilityId: number, logPKey: number) =>
    `/api/MealPatternHistory/AdjustmentException/${facilityId}/${logPKey}`,
  adjOnMenuHistory: (facilityId: number, logPKey: number) =>
    `/api/MealPatternHistory/AdjustmentOnMenu/${facilityId}/${logPKey}`,
  mpRestore: (facilityId: number, patientId: number, logPKey: number) =>
    `/api/MealPatternHistory/RestoreMP/${facilityId}/${patientId}/${logPKey}`,
  adjRestore: (facilityId: number, patientId: number, logPKey: number) =>
    `/api/MealPatternHistory/RestoreAdj/${facilityId}/${patientId}/${logPKey}`,
  mpLogAdd: (facilityId: number, patientId: number, parentPKey: number) =>
    `/api/MealPatternLog/Add/${facilityId}/${patientId}/${parentPKey}`,
  mpLogModify: (facilityId: number, patientId: number, parentPKey: number) =>
    `/api/MealPatternLog/Modify/${facilityId}/${patientId}/${parentPKey}`,
  mpLogBulkInactive: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/BulkInactive/${facilityId}/${patientId}`,
  mpLogMakeIntoGlobal: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/MakeIntoGlobal/${facilityId}/${patientId}`,
  mpLogDelete: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/Delete/${facilityId}/${patientId}`,
  mpLogDeleteAll: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/DeleteAll/${facilityId}/${patientId}`,
  mpLogAddGlobalItems: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/AddGlobalItems/${facilityId}/${patientId}`,
  mpLogResetToGlobal: (facilityId: number, patientId: number) =>
    `/api/MealPatternLog/ResetToGlobal/${facilityId}/${patientId}`,

  // Meal Pattern (Patient-level)
  mealPatternByPatient: (facilityId: number, patientId: number) =>
    `/api/MealPattern/${facilityId}/${patientId}`,
  mealPatternChildrenByPatient: (facilityId: number, patientId: number) =>
    `/api/MealPattern/Children/${facilityId}/${patientId}`,
  mealPatternAddMod: (facilityId: number) =>
    `/api/MealPattern/AddMod/${facilityId}`,
  mealPatternDelete: (facilityId: number, pKey: number) =>
    `/api/MealPattern/${facilityId}/${pKey}`,
  mealPatternBulkActivate: (facilityId: number, patientId: number, activate: boolean) =>
    `/api/MealPattern/BulkActivate/${facilityId}/${patientId}/${activate}`,
  mealPatternDeleteAll: (facilityId: number, patientId: number) =>
    `/api/MealPattern/DeleteAll/${facilityId}/${patientId}`,
  mealPatternGlobalItemsByGroup: (facilityId: number, groupPKey: number) =>
    `/api/MealPattern/GlobalItems/${facilityId}/${groupPKey}`,
  mealPatternGlobalItems: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/${facilityId}`,
  // Templates-only variant with all display-name JOINs (menu category,
  // consistency, portion, item type, service item, special devices,
  // size + abbreviation). Returns rows where FKey_Patient IS NULL.
  mealPatternGlobalItemsTemplates: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/Templates/${facilityId}`,
  // Children of every global template item, joined with display names.
  // Drives the master-detail child grid on the global meal pattern page.
  mealPatternGlobalItemTemplateChildren: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/Templates/Children/${facilityId}`,
  // CRUD on tblPrd_Meal_Pattern_Global templates (parent) + group links.
  mealPatternGlobalAddMod: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/AddMod/${facilityId}`,
  // Bulk Activate/Deactivate every template in a group. Body:
  // { groupPKey, newInactive }. Cascades to patient mirror rows via
  // the smart-merge pattern in the DAL. Powers the toolbar
  // Activate/Deactivate → "Entire Group" menu item.
  mealPatternGlobalBulkSetInactive: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/BulkSetInactive/${facilityId}`,
  mealPatternGlobalDelete: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/${facilityId}/${pKey}`,
  // Multi-select Delete: body { pKeys }; per-key results (MealPatternController.DeleteGlobalBatch).
  mealPatternGlobalDeleteBatch: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/DeleteBatch/${facilityId}`,
  // Residents using each of { pKeys }: all of them (the multi-row Delete confirm), and the
  // active ones with their facilities (the impact line).
  mealPatternGlobalItemUsageCounts: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/UsageCounts/${facilityId}`,
  mealPatternGlobalItemGroupIds: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/Groups/${facilityId}/${pKey}`,
  // CRUD on tblPrd_Meal_Pattern_Global_Child.
  mealPatternGlobalChildAddMod: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/Child/AddMod/${facilityId}`,
  mealPatternGlobalChildDelete: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/Child/${facilityId}/${pKey}`,
  mealPatternGlobalChildReorderPriority: (facilityId: number) =>
    `/api/MealPattern/GlobalItems/Child/ReorderPriority/${facilityId}`,
  mealPatternGlobalItemUsage: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/Usage/${facilityId}/${pKey}`,
  mealPatternGlobalReplaceCandidates: (facilityId: number, focusedPKey: number) =>
    `/api/MealPattern/GlobalItems/ReplaceCandidates/${facilityId}/${focusedPKey}`,
  mealPatternGlobalReplace: (facilityId: number, focusedPKey: number, replacementPKey: number) =>
    `/api/MealPattern/GlobalItems/Replace/${facilityId}/${focusedPKey}/${replacementPKey}`,
  mealPatternGlobalHide: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/Hide/${facilityId}/${pKey}`,
  mealPatternGlobalUnhide: (facilityId: number, pKey: number) =>
    `/api/MealPattern/GlobalItems/Unhide/${facilityId}/${pKey}`,
  mealPatternInsertFromGlobal: (facilityId: number) =>
    `/api/MealPattern/InsertFromGlobal/${facilityId}`,
  mealPatternAddModChild: (facilityId: number) =>
    `/api/MealPattern/AddModChild/${facilityId}`,
  mealPatternDeleteChild: (facilityId: number, pKey: number) =>
    `/api/MealPattern/Child/${facilityId}/${pKey}`,
  mealPatternReorderPriority: (facilityId: number) =>
    `/api/MealPattern/ReorderPriority/${facilityId}`,
  mealPatternBreakGlobalLink: (facilityId: number, pKey: number) =>
    `/api/MealPattern/BreakGlobalLink/${facilityId}/${pKey}`,
  mealPatternResetToGlobal: (facilityId: number, pKey: number) =>
    `/api/MealPattern/ResetToGlobal/${facilityId}/${pKey}`,
  mealPatternMakeIntoGlobal: (facilityId: number) =>
    `/api/MealPattern/MakeIntoGlobal/${facilityId}`,
  mealPatternInsertPatientGlobalFromTemplate: (
    facilityId: number,
    patientId: number,
    templatePKey: number,
  ) =>
    `/api/MealPattern/InsertPatientGlobalFromTemplate/${facilityId}/${patientId}/${templatePKey}`,
  mealPatternGlobalTemplatesCombined: (facilityId: number) =>
    `/api/MealPattern/GlobalTemplatesCombined/${facilityId}`,
  mealPatternInsertPatientGlobalBatch: (facilityId: number, patientId: number) =>
    `/api/MealPattern/InsertPatientGlobalBatch/${facilityId}/${patientId}`,
  menuHeaderTreeForFacility: (facilityId: number) => `/api/MenuHeader/Tree/${facilityId}`,
  mealPatternMenuDaysByMealPattern: (facilityId: number, mealPatternId: number) =>
    `/api/MealPatternMenuDays/${facilityId}/MealPattern/${mealPatternId}`,
  mealPatternMenuDaysAddMod: (facilityId: number) =>
    `/api/MealPatternMenuDays/AddMod/${facilityId}`,
  mealPatternMenuDaysDelete: (facilityId: number, pKey: number) =>
    `/api/MealPatternMenuDays/${facilityId}/${pKey}`,
  mealPatternCopyFromResident: (facilityId: number, destPatientId: number, sourcePatientId: number) =>
    `/api/MealPattern/CopyFromResident/${facilityId}/${destPatientId}/${sourcePatientId}`,

  // Meal Pattern Lock
  mealPatternLockAcquire: () =>
    `/api/MealPatternLock/Acquire`,
  mealPatternLockHeartbeat: (pKey: number) =>
    `/api/MealPatternLock/Heartbeat/${pKey}`,
  mealPatternLockRelease: (pKey: number) =>
    `/api/MealPatternLock/Release/${pKey}`,
  mealPatternLockForce: (patientId: number) =>
    `/api/MealPatternLock/Force/${patientId}`,

  // Predefined resident lists (Posting → Specific Resident dropdown)
  residentSelectLists: (facilityId: number, type: number) =>
    `/api/ResidentSelectList/${facilityId}?type=${type}`,
  residentSelectListAddMod: (facilityId: number) =>
    `/api/ResidentSelectList/AddMod/${facilityId}`,
  residentSelectListDelete: (facilityId: number, pKey: number) =>
    `/api/ResidentSelectList/${facilityId}/${pKey}`,

  // Predefined Production Tally filters (multi-select "Filter" combo)
  tallyFilters: (facilityId: number) =>
    `/api/TallyFilter/${facilityId}`,
  tallyFilterById: (facilityId: number, id: number) =>
    `/api/TallyFilter/${facilityId}/${id}`,
  tallyFilterAddMod: (facilityId: number) =>
    `/api/TallyFilter/AddMod/${facilityId}`,
  tallyFilterAddModDetails: (facilityId: number, headerPKey: number) =>
    `/api/TallyFilter/AddModDetails/${facilityId}/${headerPKey}`,
  tallyFilterDetails: (facilityId: number, headerPKey: number) =>
    `/api/TallyFilter/Details/${facilityId}/${headerPKey}`,
  tallyFilterDelete: (facilityId: number, id: number) =>
    `/api/TallyFilter/${facilityId}/${id}`,

  // Problems / Goals
  problems: (facilityId: number, patientId: number) =>
    `/api/Problems/${facilityId}/${patientId}`,
  problemsAddMod: (facilityId: number) =>
    `/api/Problems/AddMod/${facilityId}`,
  problemsDelete: (facilityId: number, pKeyProblems: number) =>
    `/api/Problems/${facilityId}/${pKeyProblems}`,
  problemsTemplates: (facilityId: number) =>
    `/api/Problems/Templates/${facilityId}`,

  // Usage Tracking
  usageTracking: (facilityId: number, entityType: string, entityId: number) =>
    `/api/UsageTracking/${facilityId}/${entityType}/${entityId}`,

  // Plan of Care Config Tables
  pocConfigDomains: (facilityId: number) =>
    `/api/PlanOfCareConfig/Domains/${facilityId}`,
  pocConfigDomainsAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/Domains/AddMod/${facilityId}`,
  pocConfigDomainsDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/Domains/${facilityId}/${id}`,
  pocConfigDomainProblems: (facilityId: number, domainId: number) =>
    `/api/PlanOfCareConfig/DomainProblems/${facilityId}/${domainId}`,
  pocConfigDomainProblemsAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/DomainProblems/AddMod/${facilityId}`,
  pocConfigDomainProblemsDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/DomainProblems/${facilityId}/${id}`,
  pocConfigDomainProblemInterventions: (facilityId: number, problemId: number) =>
    `/api/PlanOfCareConfig/DomainProblemInterventions/${facilityId}/${problemId}`,
  pocConfigDomainProblemInterventionsAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/DomainProblemInterventions/AddMod/${facilityId}`,
  pocConfigDomainProblemInterventionsDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/DomainProblemInterventions/${facilityId}/${id}`,
  pocConfigDomainServiceNeeds: (facilityId: number, domainId: number) =>
    `/api/PlanOfCareConfig/DomainServiceNeeds/${facilityId}/${domainId}`,
  pocConfigDomainServiceNeedsAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/DomainServiceNeeds/AddMod/${facilityId}`,
  pocConfigDomainServiceNeedsDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/DomainServiceNeeds/${facilityId}/${id}`,
  pocConfigResponsibleParties: (facilityId: number) =>
    `/api/PlanOfCareConfig/ResponsibleParties/${facilityId}`,
  pocConfigResponsiblePartiesAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/ResponsibleParties/AddMod/${facilityId}`,
  pocConfigResponsiblePartiesDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/ResponsibleParties/${facilityId}/${id}`,
  pocConfigScoreTypes: (facilityId: number) =>
    `/api/PlanOfCareConfig/ScoreTypes/${facilityId}`,
  pocConfigScoreTypesAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/ScoreTypes/AddMod/${facilityId}`,
  pocConfigScoreTypesDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/ScoreTypes/${facilityId}/${id}`,
  pocConfigScoreTypeRanges: (facilityId: number) =>
    `/api/PlanOfCareConfig/ScoreTypeRanges/${facilityId}`,
  pocConfigScoreTypeRangesAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/ScoreTypeRanges/AddMod/${facilityId}`,
  pocConfigScoreTypeRangesDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/ScoreTypeRanges/${facilityId}/${id}`,
  pocConfigReportTypes: (facilityId: number) =>
    `/api/PlanOfCareConfig/ReportTypes/${facilityId}`,
  pocConfigSignatureReportTypes: (facilityId: number) =>
    `/api/PlanOfCareConfig/SignatureReportTypes/${facilityId}`,
  pocConfigSignatureReportTypesAddMod: (facilityId: number) =>
    `/api/PlanOfCareConfig/SignatureReportTypes/AddMod/${facilityId}`,
  pocConfigSignatureReportTypesDelete: (facilityId: number, id: number) =>
    `/api/PlanOfCareConfig/SignatureReportTypes/${facilityId}/${id}`,
  // tblPlanOfCareConfiguration (frmPlanOfCare_Configuration): GET and POST.
  planOfCareConfiguration: (facilityId: number) => `/api/PlanOfCareConfig/Configuration/${facilityId}`,

  // Satisfaction Surveys
  surveyTemplates: (facilityId: number) => `/api/Survey/Templates/${facilityId}`,
  surveyTemplateById: (facilityId: number, id: number) => `/api/Survey/Templates/${facilityId}/${id}`,
  surveyTemplateAddMod: (facilityId: number) => `/api/Survey/Templates/AddMod/${facilityId}`,
  surveyTemplateDelete: (facilityId: number, id: number) => `/api/Survey/Templates/${facilityId}/${id}`,
  surveyQuestions: (facilityId: number, templateId: number) => `/api/Survey/Questions/${facilityId}/${templateId}`,
  surveyQuestionAddMod: (facilityId: number) => `/api/Survey/Questions/AddMod/${facilityId}`,
  surveyQuestionDelete: (facilityId: number, id: number) => `/api/Survey/Questions/${facilityId}/${id}`,
  surveySessions: (facilityId: number) => `/api/Survey/Sessions/${facilityId}`,
  surveySessionById: (facilityId: number, id: number) => `/api/Survey/Sessions/${facilityId}/${id}`,
  surveySessionAddMod: (facilityId: number) => `/api/Survey/Sessions/AddMod/${facilityId}`,
  surveySessionDelete: (facilityId: number, id: number) => `/api/Survey/Sessions/${facilityId}/${id}`,
  surveyResponses: (facilityId: number, sessionId: number) => `/api/Survey/Responses/${facilityId}/${sessionId}`,
  surveyAnswers: (facilityId: number, responseId: number) => `/api/Survey/Answers/${facilityId}/${responseId}`,
  surveyResponseSave: (facilityId: number) => `/api/Survey/Responses/Save/${facilityId}`,
  surveyResponseDelete: (facilityId: number, id: number) => `/api/Survey/Responses/${facilityId}/${id}`,
  surveyTrending: (facilityId: number) => `/api/Survey/Analysis/Trending/${facilityId}`,
  surveySummary: (facilityId: number) => `/api/Survey/Analysis/Summary/${facilityId}`,
  surveyUnitComparison: (facilityId: number) => `/api/Survey/Analysis/UnitComparison/${facilityId}`,


  // Bedside TV / Pillow Speaker
  bedsideActivate: (deviceToken: string) => `/api/Bedside/Activate/${deviceToken}`,
  bedsideHelp: (deviceToken: string) => `/api/Bedside/Help/${deviceToken}`,
  bedsideHeartbeat: (deviceToken: string) => `/api/Bedside/Heartbeat/${deviceToken}`,
  bedsideMenu: (facilityId: number, patientId: number) => `/api/Bedside/Menu/${facilityId}/${patientId}`,
  bedsideSnackMenu: (facilityId: number, patientId: number) => `/api/Bedside/SnackMenu/${facilityId}/${patientId}`,
  bedsideGuestMenu: (facilityId: number) => `/api/Bedside/GuestMenu/${facilityId}`,
  bedsideSelection: (facilityId: number) => `/api/Bedside/Selection/${facilityId}`,
  bedsideDevices: (facilityId: number) => `/api/Bedside/Devices/${facilityId}`,
  bedsideDeviceRegister: (facilityId: number) => `/api/Bedside/Devices/Register/${facilityId}`,
  bedsideDeviceUpdate: (deviceId: number) => `/api/Bedside/Devices/${deviceId}`,
  bedsideDeviceDeactivate: (deviceId: number) => `/api/Bedside/Devices/${deviceId}/Deactivate`,
  bedsideDeviceRenew: (deviceId: number) => `/api/Bedside/Devices/${deviceId}/Renew`,
  bedsideDeviceDelete: (deviceId: number) => `/api/Bedside/Devices/${deviceId}/Permanent`,
  bedsidePurgeExpired: (facilityId: number) => `/api/Bedside/Devices/PurgeExpired/${facilityId}`,
  bedsideHealth: (facilityId: number) => `/api/Bedside/Health/${facilityId}`,
  bedsideNotOrdered: (facilityId: number, mealtime: number) => `/api/Bedside/NotOrdered/${facilityId}/${mealtime}`,
  // Item Photos
  itemPhoto: (facilityId: number, itemId: number) => `/api/Item/Photo/${facilityId}/${itemId}`,
  // Item Education Card (aggregated nutrition + allergens + ingredients + photo)
  itemEducationCard: (facilityId: number, itemId: number) => `/api/Item/EducationCard/${facilityId}/${itemId}`,

  // Kitchen Display System (KDS) - reads from census, status tracked in bridge tables
  kdsOrders: (facilityId: number) => `/api/KitchenDisplay/${facilityId}`,
  kdsHistory: (facilityId: number) => `/api/KitchenDisplay/${facilityId}/History`,
  kdsBumpOrder: (facilityId: number, orderId: number) => `/api/KitchenDisplay/BumpOrder/${facilityId}/${orderId}`,
  kdsCancelOrder: (facilityId: number, orderId: number) => `/api/KitchenDisplay/CancelOrder/${facilityId}/${orderId}`,
  kdsCompleteItem: (facilityId: number, itemId: number) => `/api/KitchenDisplay/CompleteItem/${facilityId}/${itemId}`,
  kdsStations: (facilityId: number) => `/api/KitchenDisplay/Stations/${facilityId}`,
  kdsStationAddMod: (facilityId: number) => `/api/KitchenDisplay/Stations/AddMod/${facilityId}`,
  kdsStationDelete: (facilityId: number, id: number) => `/api/KitchenDisplay/Stations/${facilityId}/${id}`,
  kdsStats: (facilityId: number) => `/api/KitchenDisplay/Stats/${facilityId}`,
  kdsDeliveries: (facilityId: number) => `/api/KitchenDisplay/Delivery/${facilityId}`,
  kdsMyDeliveries: (facilityId: number, userId: number) => `/api/KitchenDisplay/Delivery/MyDeliveries/${facilityId}/${userId}`,
  kdsDeliveryAssign: (facilityId: number) => `/api/KitchenDisplay/Delivery/Assign/${facilityId}`,
  kdsDeliveryConfirm: (facilityId: number, deliveryId: number) => `/api/KitchenDisplay/Delivery/Confirm/${facilityId}/${deliveryId}`,
  kdsServeEarly: (facilityId: number, id: number) => `/api/KitchenDisplay/ServeEarly/${facilityId}/${id}`,
  kdsUndoServeEarly: (facilityId: number, id: number) => `/api/KitchenDisplay/UndoServeEarly/${facilityId}/${id}`,
  kdsDeliveryStats: (facilityId: number) => `/api/KitchenDisplay/Delivery/Stats/${facilityId}`,
  kdsServiceWindows: (facilityId: number) => `/api/KitchenDisplay/ServiceWindows/${facilityId}`,
  kdsServiceWindowAddMod: (facilityId: number) => `/api/KitchenDisplay/ServiceWindows/AddMod/${facilityId}`,
  kdsServiceWindowDelete: (facilityId: number, id: number) => `/api/KitchenDisplay/ServiceWindows/${facilityId}/${id}`,
  kdsSettings: (facilityId: number) => `/api/KitchenDisplay/Settings/${facilityId}`,
  kdsServiceWindowStatus: (facilityId: number) => `/api/KitchenDisplay/ServiceWindowStatus/${facilityId}`,
  kdsProcessEscalations: (facilityId: number) => `/api/KitchenDisplay/ProcessEscalations/${facilityId}`,
  kdsRefireOrder: (facilityId: number, id: number) => `/api/KitchenDisplay/RefireOrder/${facilityId}/${id}`,
  kdsHoldOrder: (facilityId: number, id: number) => `/api/KitchenDisplay/HoldOrder/${facilityId}/${id}`,
  kdsResumeOrder: (facilityId: number, id: number) => `/api/KitchenDisplay/ResumeOrder/${facilityId}/${id}`,

  // Room Service / On-Demand Dining
  roomServiceWindows: (facilityId: number) => `/api/RoomServiceConfig/${facilityId}`,
  roomServiceWindowById: (facilityId: number, id: number) => `/api/RoomServiceConfig/${facilityId}/${id}`,
  roomServiceAddMod: (facilityId: number) => `/api/RoomServiceConfig/AddMod/${facilityId}`,
  roomServiceDelete: (facilityId: number, id: number) => `/api/RoomServiceConfig/${facilityId}/${id}`,
  roomServiceAvailableNow: (facilityId: number, unitMappingId?: number) =>
    unitMappingId
      ? `/api/RoomServiceConfig/AvailableNow/${facilityId}/${unitMappingId}`
      : `/api/RoomServiceConfig/AvailableNow/${facilityId}`,

  // FHIR Integration
  fhirPatientSyncStatus: (facilityId: number, mrn: string) =>
    `/api/FhirIntegration/PatientSyncStatus/${facilityId}/${encodeURIComponent(mrn)}`,

  // Migration (PSI only). only= names scripts (repeated), force=false runs only what the journal says is pending.
  migrationRun: (opts?: { only?: string[]; force?: boolean }) => `/api/Migration/Run` + migrationQuery(opts),
  migrationPreview: (opts?: { only?: string[]; force?: boolean }) => `/api/Migration/Preview` + migrationQuery(opts),
  migrationStatus: () => `/api/Migration/Status`,

  // Login check: is this connection's database at the API build's schema? (DatabaseUpdateController)
  databaseUpdateEnsure: () => `/api/DatabaseUpdate/Ensure`,
  databaseUpdateStatus: (runId?: number) =>
    runId ? `/api/DatabaseUpdate/Status?runId=${runId}` : `/api/DatabaseUpdate/Status`,

  // USDA FoodData Central
  usdaSearch: () => `/api/Usda/Search`,
  usdaFood: (fdcId: number) => `/api/Usda/Food/${fdcId}`,
  usdaLinkItem: (facilityId: number) => `/api/Usda/LinkItem/${facilityId}`,
  usdaItemLink: (facilityId: number, itemId: number) =>
    `/api/Usda/ItemLink/${facilityId}/${itemId}`,
  usdaAllLinks: (facilityId: number) => `/api/Usda/AllLinks/${facilityId}`,
  usdaUnlinkItem: (facilityId: number, itemId: number) =>
    `/api/Usda/UnlinkItem/${facilityId}/${itemId}`,

  // Print Agent
  printAgents: (facilityId: number) => `/api/PrintAgent/Agents/${facilityId}`,
  // Selective Touch tablets (device sign-in + server PIN)
  touchDevices: (facilityId: number) => `/api/TouchDevice/${facilityId}`,
  touchDeviceCode: (facilityId: number, deviceId: number) => `/api/TouchDevice/${facilityId}/${deviceId}/code`,
  touchDeviceRevoke: (facilityId: number, deviceId: number) => `/api/TouchDevice/${facilityId}/${deviceId}/revoke`,
  touchDeviceEnroll: () => '/api/TouchDevice/enroll',
  touchDeviceUnlock: () => '/api/TouchDevice/unlock',
  printAgentRegister: (facilityId: number, userId: number) =>
    `/api/PrintAgent/Agents/Register/${facilityId}/${userId}`,
  printAgentUpdate: (facilityId: number) => `/api/PrintAgent/Agents/Update/${facilityId}`,
  printAgentDeactivate: (facilityId: number, agentId: number) =>
    `/api/PrintAgent/Agents/Deactivate/${facilityId}/${agentId}`,
  printAgentDelete: (facilityId: number, agentId: number) =>
    `/api/PrintAgent/Agents/${facilityId}/${agentId}`,
  printFacilityPrinters: (facilityId: number) => `/api/PrintAgent/FacilityPrinters/${facilityId}`,
  printRoutingRules: (facilityId: number) => `/api/PrintAgent/Routing/${facilityId}`,
  printRoutingAddMod: (facilityId: number) => `/api/PrintAgent/Routing/AddMod/${facilityId}`,
  printRoutingDelete: (facilityId: number, ruleId: number) =>
    `/api/PrintAgent/Routing/${facilityId}/${ruleId}`,
  printJobs: (facilityId: number) => `/api/PrintAgent/Jobs/${facilityId}`,
  printJobCreate: (facilityId: number) => `/api/PrintAgent/Jobs/Create/${facilityId}`,

  // Dietech data update (PSI master data pulled from Techlog; scoped by X-Connection-Id)
  dietechDataUpdateStatus: () => `/api/DietechDataUpdate/status`,
  dietechDataUpdateHistory: (limit = 20) => `/api/DietechDataUpdate/history?limit=${limit}`,
  dietechDataUpdateRequest: () => `/api/DietechDataUpdate/request`,
  dietechDataUpdateJob: (jobId: number) => `/api/DietechDataUpdate/job/${jobId}`,
  dietechDataUpdateMatching: () => `/api/DietechDataUpdate/matching`,

  // System Status (cross-facility)
  systemStatusPublic: () => `/api/systemstatus/public`,
  systemStatusIncidents: (days = 30) => `/api/systemstatus/admin/incidents?days=${days}`,
  systemStatusIncidentCreate: () => `/api/systemstatus/admin/incidents`,
  systemStatusIncidentUpdate: (id: number) => `/api/systemstatus/admin/incidents/${id}`,
  systemStatusIncidentResolve: (id: number) => `/api/systemstatus/admin/incidents/${id}/resolve`,
  systemStatusIncidentDelete: (id: number) => `/api/systemstatus/admin/incidents/${id}`,
} as const;
